import React, { useCallback, useEffect, useMemo, useState } from 'react';
import type { Filters } from '../types/biApp';
import { api, downloadFile, mutate } from '../shared/api';
import { formatMoney, formatNumber } from '../shared/formatting';
import { BrandSpinner } from '../shared/BrandSpinner';
import { NiceLineChart, Treemap, RankedBar } from '../shared/charts';
import { PersonName } from '../shared/PersonName';
import { ORDER_STATUS, ORDER_STATUS_SEQUENCE, carrierLabel, currentOrderStatus, orderStatusLabel, orderStatusTooltip } from '../shared/orderStatus';
import { DateRangePicker } from '../shared/DateRangePicker';
import { AdoEkonomskiPnl } from './adoEkonomskiPnl';
import { AdoClientDrilldownDrawer } from './adoClientDrilldown';

/**
 * Modul 1 (ADO) — kompletan rebuild s time-axis selectorom.
 *
 * 4 razine:
 *   1. PulseWall — exec hero KPI kartice (revenue, conversion, open value, lead time, top kanal)
 *   2. Performance — breakdown po kanalu/poslovnici/partneru/mjesecu/kvartalu/godini/doc_prefix
 *      + trend line chart + funnel + buyback prikaz (otkup zasebno per user feedback)
 *   3. Operations — open offers queue (sortable, filterable) za CRM-style workflow
 *   4. Customers — RFM tabela + bot auto-flag (conversion=0% + N>=3 + recency<30d)
 *
 * Time-axis selector pri vrhu (Operativno / Knjigovodstveno / Cashflow) — server-side
 * switch koji partition po offer_date ili first_invoice_date.
 *
 * Backend: GET /api/ado-m1/* (vidi AdoModule1Controller).
 */

type Axis = 'operational' | 'accounting' | 'cashflow';

type PulsePayload = {
    pulse: {
        axis: Axis;
        axis_label: string;
        offers_count: number;
        offered_total: number;
        realized_total: number;
        invoiced_total: number;
        open_count: number;
        open_value: number;
        realized_count: number;
        conversion_by_count_pct: number;
        conversion_by_value_pct: number;
        avg_lead_days: number;
        buyback_count: number;
        buyback_amount: number;
        ruc_total_eur?: number | null;
        ruc_total_pct?: number | null;
        ruc_estimated?: boolean;
        ruc_actual_share_pct?: number;
    };
};

type BreakdownRow = {
    bucket: string;
    bucket_is_person?: boolean;
    bucket_hint?: string | null;
    offers_count: number;
    offered: number;
    realized: number;
    open_value: number;
    realized_count: number;
    conversion_by_count_pct: number;
    conversion_by_value_pct: number;
    ruc_issue_eur?: number | null;
    ruc_issue_pct?: number | null;
    ruc_accept_eur?: number | null;
    ruc_accept_pct?: number | null;
    ruc_total_eur?: number | null;
    ruc_total_pct?: number | null;
    ruc_estimated?: boolean;
    ruc_actual_share_pct?: number;
};

type TrendRow = {
    period: string;
    offers_count: number;
    offered: number;
    realized: number;
    open_value: number;
    spot_eur_per_g?: number | null;
};

type FunnelStage = { stage: string; count: number; value: number };

type OpenOfferRow = {
    offer_id: number;
    offer_number: string;
    partner_code: string;
    partner_name: string;
    partner_name_is_person?: boolean;
    offer_date: string;
    age_days: number | null;
    amount_offered: number;
    kasa_id_str: string;
};

type CustomerRow = {
    partner_code: string;
    partner_name: string;
    partner_name_is_person?: boolean;
    offers_count: number;
    realized_count: number;
    offered_total: number;
    realized_total: number;
    conversion_by_count_pct: number;
    conversion_by_value_pct: number;
    first_offer_date: string;
    last_offer_date: string;
    recency_days: number | null;
    frequency: number;
    monetary: number;
    tier: 'micro' | 'small' | 'mid' | 'large' | 'jumbo';
    rfm_tier: 'vip' | 'high' | 'regular' | 'at_risk' | 'lost' | 'inactive' | 'never_active';
    is_at_risk: boolean;
    is_lost: boolean;
    is_bot_signal: boolean;
};

type Dimension = 'kanal' | 'poslovnica' | 'partner' | 'mjesec' | 'kvartal' | 'godina' | 'doc_prefix';
type Granularity = 'dan' | 'tjedan' | 'mjesec' | 'kvartal' | 'godina';
type TabId = 'live' | 'pnl' | 'performance' | 'customers' | 'crm' | 'naplata' | 'pregled';

const AXIS_OPTIONS: Array<{ value: Axis; labelHr: string; labelEn: string; hintHr: string }> = [
    { value: 'operational', labelHr: 'Operativno', labelEn: 'Operational', hintHr: 'Po datumu ponude (kad se prodaja dogodila)' },
    { value: 'accounting', labelHr: 'Knjigovodstveno', labelEn: 'Accounting', hintHr: 'Po datumu računa (kad COGS i prihod nastaju)' },
    { value: 'cashflow', labelHr: 'Cashflow', labelEn: 'Cashflow', hintHr: 'Po datumu naplate (proxy: datum računa)' },
];

const TABS: Array<{ id: TabId; hr: string; en: string }> = [
    { id: 'live', hr: '📡 Live feed', en: '📡 Live feed' },
    { id: 'performance', hr: 'Promet', en: 'Channels' },
    { id: 'pregled', hr: 'Pregled', en: 'Overview' },
    { id: 'customers', hr: 'Klijenti', en: 'Customers' },
    { id: 'crm', hr: '🎯 CRM', en: '🎯 CRM' },
    { id: 'naplata', hr: '💰 Naplata', en: '💰 Collections' },
    { id: 'pnl', hr: 'Ekonomski P&L', en: 'Economic P&L' },
];

const DIM_OPTIONS: Array<{ value: Dimension; labelHr: string; labelEn: string }> = [
    { value: 'kanal', labelHr: 'Kanal', labelEn: 'Channel' },
    { value: 'poslovnica', labelHr: 'Poslovnica', labelEn: 'Branch' },
    { value: 'partner', labelHr: 'Partner', labelEn: 'Partner' },
    { value: 'mjesec', labelHr: 'Mjesec', labelEn: 'Month' },
    { value: 'kvartal', labelHr: 'Kvartal', labelEn: 'Quarter' },
    { value: 'godina', labelHr: 'Godina', labelEn: 'Year' },
    { value: 'doc_prefix', labelHr: 'Vrsta', labelEn: 'Doc type' },
];

type Country = 'HR' | 'SI' | 'BG';
const COUNTRY_OPTIONS: Array<{ value: Country; labelHr: string; labelEn: string }> = [
    { value: 'HR', labelHr: 'HRVATSKA', labelEn: 'CROATIA' },
    { value: 'SI', labelHr: 'SLOVENIJA', labelEn: 'SLOVENIA' },
    { value: 'BG', labelHr: 'BUGARSKA', labelEn: 'BULGARIA' },
];
const COUNTRIES_STORAGE_KEY = 'bis.ado-m1.countries';
const DEFAULT_COUNTRIES: Country[] = ['HR'];

/** Odabir drzava iz localStorage; novi korisnik ili neispravan zapis daje samo HR. */
function loadCountries(): Country[] {
    try {
        const raw = localStorage.getItem(COUNTRIES_STORAGE_KEY);
        const arr: unknown = raw ? JSON.parse(raw) : null;
        if (Array.isArray(arr)) {
            const picked = COUNTRY_OPTIONS.map((o) => o.value).filter((c) => arr.includes(c));
            if (picked.length > 0) return picked;
        }
    } catch {
        // pokvaren zapis, ostaje zadano
    }
    return DEFAULT_COUNTRIES;
}

/** Veliki gumbi drzava kupca za sve tabove m.1. Zadnja ukljucena drzava se ne da iskljuciti. */
function CountryToggles({ countries, setCountries, locale }: { countries: Country[]; setCountries: (c: Country[]) => void; locale: 'hr' | 'en' }) {
    const toggle = (c: Country) => {
        const on = countries.includes(c);
        if (on && countries.length === 1) return;
        const next = COUNTRY_OPTIONS.map((o) => o.value).filter((v) => (v === c ? !on : countries.includes(v)));
        setCountries(next);
    };
    return (
        <div className="ado-m1__countries" role="group" aria-label={locale === 'en' ? 'Customer country' : 'Država kupca'}
            style={{ display: 'flex', flexWrap: 'wrap', gap: 12, margin: '10px 0 6px' }}>
            {COUNTRY_OPTIONS.map((o) => {
                const on = countries.includes(o.value);
                const locked = on && countries.length === 1;
                return (
                    <label key={o.value}
                        title={locked ? (locale === 'en' ? 'At least one country must stay selected' : 'Barem jedna država mora ostati uključena') : undefined}
                        style={{
                            display: 'inline-flex', alignItems: 'center', gap: 10, padding: '10px 22px', minWidth: 170,
                            fontSize: '1.05rem', fontWeight: 700, letterSpacing: '0.04em',
                            border: '2px solid '+(on ? '#2f3337' : '#c9ccd1'), borderRadius: 6,
                            background: on ? '#2f3337' : '#fff', color: on ? '#fff' : '#2f3337',
                            cursor: locked ? 'not-allowed' : 'pointer', userSelect: 'none',
                        }}>
                        <input type="checkbox" checked={on} disabled={locked} onChange={() => toggle(o.value)}
                            style={{ width: 20, height: 20, margin: 0, cursor: 'inherit' }} />
                        {locale === 'en' ? o.labelEn : o.labelHr}
                    </label>
                );
            })}
        </div>
    );
}

export function AdoModule({
    token,
    locale = 'hr',
    filters,
}: {
    payload: unknown;
    token: string;
    locale?: 'hr' | 'en';
    filters: Filters;
    datasetMode: unknown;
    setDatasetMode: unknown;
    canToggleDataset: unknown;
}) {
    const t = (hr: string, en: string): string => (locale === 'en' ? en : hr);

    const [tab, setTab] = useState<TabId>('live');
    const [axis, setAxis] = useState<Axis>('operational');
    const [dim, setDim] = useState<Dimension>('kanal');
    const [granularity, setGranularity] = useState<Granularity>('mjesec');
    // Drzave kupca za sve tabove m.1, zadano samo HR (Vedran 30.10.2026).
    const [countries, setCountriesState] = useState<Country[]>(loadCountries);
    const setCountries = useCallback((c: Country[]) => {
        setCountriesState(c);
        try { localStorage.setItem(COUNTRIES_STORAGE_KEY, JSON.stringify(c)); } catch { /* bez pamcenja */ }
    }, []);
    const countriesParam = countries.join(',');

    const [pulse, setPulse] = useState<PulsePayload | null>(null);
    const [breakdown, setBreakdown] = useState<BreakdownRow[]>([]);
    const [trend, setTrend] = useState<TrendRow[]>([]);
    const [funnel, setFunnel] = useState<FunnelStage[]>([]);
    const [openOffers, setOpenOffers] = useState<OpenOfferRow[]>([]);
    const [customers, setCustomers] = useState<CustomerRow[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    // Drilldown drawer state — pohrani partner_code kojeg user želi pogledati.
    const [drilldownPartner, setDrilldownPartner] = useState<string | null>(null);

    const params = useCallback((): URLSearchParams => {
        const p = new URLSearchParams();
        p.set('axis', axis);
        if (filters.dateFrom) p.set('date_from', filters.dateFrom);
        if (filters.dateTo) p.set('date_to', filters.dateTo);
        p.set('countries', countriesParam);
        return p;
    }, [axis, filters.dateFrom, filters.dateTo, countriesParam]);

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        setError('');
        const load = async (): Promise<void> => {
            try {
                // Pulse strip se uvijek prikazuje iznad tabova → uvijek dohvati pulse
                // (re-fetch kad se drzave mijenjaju, jer KPI-ovi se onda razlikuju)
                const pulsePromise = api<PulsePayload>(`/ado-m1/pulse?${params().toString()}`, token);

                if (tab === 'live') {
                    const p = await pulsePromise;
                    if (cancelled) return;
                    setPulse(p);
                } else if (tab === 'performance') {
                    const p = new URLSearchParams(params());
                    p.set('dim', dim);
                    const pt = new URLSearchParams(params());
                    pt.set('granularity', granularity);
                    const [b, tr, pu] = await Promise.all([
                        api<{ rows: BreakdownRow[] }>(`/ado-m1/breakdown?${p.toString()}`, token),
                        api<{ rows: TrendRow[] }>(`/ado-m1/trend?${pt.toString()}`, token),
                        pulsePromise,
                    ]);
                    if (cancelled) return;
                    setBreakdown(b.rows);
                    setTrend(tr.rows);
                    setPulse(pu);
                } else if (tab === 'customers') {
                    const [r, pu] = await Promise.all([
                        api<{ rows: CustomerRow[] }>(`/ado-m1/customers?${params().toString()}&limit=300`, token),
                        pulsePromise,
                    ]);
                    if (cancelled) return;
                    setCustomers(r.rows);
                    setPulse(pu);
                } else if (tab === 'crm') {
                    const p = await pulsePromise;
                    if (cancelled) return;
                    setPulse(p);
                } else if (tab === 'naplata') {
                    const p = await pulsePromise;
                    if (cancelled) return;
                    setPulse(p);
                } else if (tab === 'pregled') {
                    const p = await pulsePromise;
                    if (cancelled) return;
                    setPulse(p);
                } else if (tab === 'pnl') {
                    const p = await pulsePromise;
                    if (cancelled) return;
                    setPulse(p);
                }
            } catch (err) {
                if (cancelled) return;
                setError(err instanceof Error ? err.message : String(err));
            } finally {
                if (!cancelled) setLoading(false);
            }
        };
        void load();

        return () => {
            cancelled = true;
        };
    }, [tab, axis, dim, granularity, params, token]);

    const axisHint = useMemo(() => AXIS_OPTIONS.find((a) => a.value === axis)?.hintHr ?? '', [axis]);

    return (
        <section className="ado-m1">
            <header className="ado-m1__header">
                <div className="ado-m1__title">
                    <h1>{t('1 · Prodaja i ponude', '1 · Sales & Offers')}</h1>
                    <p className="ado-m1__subtitle">{t(
                        'Lifecycle pogled: ponude, realizacija, otvoreni rizik, kanali, klijenti.',
                        'Lifecycle view: offers, conversion, open risk, channels, customers.'
                    )}</p>
                </div>
                {(tab === 'performance' || tab === 'pnl') ? (
                    <div className="ado-m1__axis-selector" role="radiogroup" aria-label={t('Vremenska os', 'Time axis')}>
                        {AXIS_OPTIONS.map((opt) => (
                            <button
                                key={opt.value}
                                type="button"
                                className={`ado-m1__axis-btn${axis === opt.value ? ' is-active' : ''}`}
                                onClick={() => setAxis(opt.value)}
                                aria-pressed={axis === opt.value}
                                title={opt.hintHr}
                            >
                                {locale === 'en' ? opt.labelEn : opt.labelHr}
                            </button>
                        ))}
                    </div>
                ) : null}
            </header>
            {(tab === 'performance' || tab === 'pnl') ? <div className="ado-m1__axis-hint">{axisHint}</div> : null}

            <CountryToggles countries={countries} setCountries={setCountries} locale={locale} />

            <nav className="ado-m1__tabs">
                {TABS.map((tDef) => (
                    <button
                        key={tDef.id}
                        type="button"
                        className={`ado-m1__tab${tab === tDef.id ? ' is-active' : ''}`}
                        onClick={() => setTab(tDef.id)}
                        aria-pressed={tab === tDef.id}
                    >
                        {locale === 'en' ? tDef.en : tDef.hr}
                    </button>
                ))}
            </nav>

            {loading && <div className="ado-m1__loading"><BrandSpinner /></div>}
            {error && <div className="ado-m1__error" role="alert">{error}</div>}

            {!loading && !error && pulse && tab !== 'pregled' && <ExecPulseStrip data={pulse.pulse} locale={locale} onJump={(t) => setTab(t)} token={token} />}

            {!loading && !error && tab === 'live' && (
                <LiveOffersFeed token={token} locale={locale} countries={countriesParam} />
            )}
            {!loading && !error && tab === 'performance' && (
                <PerformancePanel
                    rows={breakdown}
                    trend={trend}
                    dim={dim}
                    setDim={setDim}
                    granularity={granularity}
                    setGranularity={setGranularity}
                    countries={countriesParam}
                    locale={locale}
                    token={token}
                    axis={axis}
                />
            )}
            {!loading && !error && tab === 'pregled' && (
                <GoldSalesPregledPanel token={token} locale={locale} />
            )}
            {!loading && !error && tab === 'customers' && (
                <CustomersPanel rows={customers} locale={locale} onPartnerClick={setDrilldownPartner} token={token} />
            )}
            {!loading && !error && tab === 'crm' && (
                <CrmPanel token={token} locale={locale} />
            )}
            {!loading && !error && tab === 'naplata' && (
                <ArAgingPanel token={token} locale={locale} />
            )}
            {!loading && !error && tab === 'pnl' && (
                <AdoEkonomskiPnl token={token} locale={locale} />
            )}
            {drilldownPartner !== null ? (
                <AdoClientDrilldownDrawer
                    token={token}
                    partnerCode={drilldownPartner}
                    filters={filters}
                    locale={locale}
                    onClose={() => setDrilldownPartner(null)}
                />
            ) : null}
        </section>
    );
}

function GoldStatsMirror({ locale }: { locale: 'hr' | 'en' }) {
    const t = (hr: string, en: string) => (locale === 'en' ? en : hr);
    const [data, setData] = useState<any>(null);
    const [loading, setLoading] = useState(true);
    useEffect(() => {
        (async () => {
            try {
                const r = await fetch('/api/ado-m1/gold-stats', { credentials: 'include', headers: { Authorization: `Bearer ${localStorage.getItem('bi.token') ?? ''}` } });
                if (r.ok) setData(await r.json());
            } finally {
                setLoading(false);
            }
        })();
    }, []);
    if (loading) return null;
    if (!data) return null;
    const td = data.today || {};
    const w7 = data.last_7d || {};
    const fmt = (n: number) => Number(n).toLocaleString(locale === 'en' ? 'en-GB' : 'hr-HR', { maximumFractionDigits: 0 });
    return (
        <section style={{ background: 'linear-gradient(135deg, rgba(201,155,49,0.06), rgba(107,79,28,0.04))', border: '1px solid rgba(201,155,49,0.3)', borderRadius: 12, padding: '14px 18px', marginTop: 16, marginBottom: 16 }}>
            <h3 style={{ margin: '0 0 4px', color: '#6b4f1c', fontSize: '1rem' }}>🥇 {t('Gold supply chain (mirror m.40)', 'Gold supply chain (mirror m.40)')}</h3>
            <p className="muted" style={{ margin: '0 0 12px', fontSize: '0.78rem' }}>
                {t('Real-time iz Live Offers + Delivery Allocations. Skok u m.40.1 za detalje / hedge / plan.', 'Real-time from Live Offers + Delivery Allocations. Jump to m.40.1 for details / hedge / plan.')}
            </p>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 10, marginBottom: 14 }}>
                <div style={{ background: '#fff', borderRadius: 8, padding: '10px 12px', border: '1px solid rgba(184,156,91,0.3)' }}>
                    <div style={{ fontSize: '0.68rem', textTransform: 'uppercase', letterSpacing: '0.08em', color: 'rgba(0,0,0,0.5)' }}>{t('Danas — ponude', 'Today — offers')}</div>
                    <strong style={{ fontSize: '1.2rem' }}>{td.offers_count ?? 0}</strong>
                    <div className="muted" style={{ fontSize: '0.74rem', marginTop: 2 }}>{fmt(td.offers_value_eur)} €</div>
                </div>
                <div style={{ background: '#fff', borderRadius: 8, padding: '10px 12px', border: '1px solid rgba(31,122,54,0.3)' }}>
                    <div style={{ fontSize: '0.68rem', textTransform: 'uppercase', letterSpacing: '0.08em', color: 'rgba(0,0,0,0.5)' }}>{t('Danas — prihvaćeno', 'Today — accepted')}</div>
                    <strong style={{ fontSize: '1.2rem', color: '#1f7a36' }}>{td.accepted_count ?? 0}</strong>
                    <div className="muted" style={{ fontSize: '0.74rem', marginTop: 2 }}>{fmt(td.accepted_value_eur)} €</div>
                </div>
                <div style={{ background: '#fff', borderRadius: 8, padding: '10px 12px', border: '1px solid rgba(177,64,64,0.3)' }}>
                    <div style={{ fontSize: '0.68rem', textTransform: 'uppercase', letterSpacing: '0.08em', color: 'rgba(0,0,0,0.5)' }}>⚠ {t('Open (rizik spot)', 'Open (spot risk)')}</div>
                    <strong style={{ fontSize: '1.2rem', color: '#b14040' }}>{td.open_count ?? 0}</strong>
                    <div className="muted" style={{ fontSize: '0.74rem', marginTop: 2 }}>{Number(td.open_fine_g ?? 0).toFixed(1)} g · {fmt(td.open_value_eur)} €</div>
                </div>
                <div style={{ background: '#fff', borderRadius: 8, padding: '10px 12px', border: '1px solid rgba(30,64,175,0.3)' }}>
                    <div style={{ fontSize: '0.68rem', textTransform: 'uppercase', letterSpacing: '0.08em', color: 'rgba(0,0,0,0.5)' }}>🛡 {t('Hedge today', 'Hedge today')}</div>
                    <strong style={{ fontSize: '1.2rem', color: '#1e40af' }}>{td.hedged_count ?? 0}</strong>
                </div>
                <div style={{ background: '#fff', borderRadius: 8, padding: '10px 12px', border: '1px solid rgba(184,156,91,0.3)' }}>
                    <div style={{ fontSize: '0.68rem', textTransform: 'uppercase', letterSpacing: '0.08em', color: 'rgba(0,0,0,0.5)' }}>{t('7d konverzija', '7d conversion')}</div>
                    <strong style={{ fontSize: '1.2rem' }}>{w7.conversion_pct ?? 0}%</strong>
                    <div className="muted" style={{ fontSize: '0.74rem', marginTop: 2 }}>{w7.accepted_count}/{w7.offers_count}</div>
                </div>
            </div>
            {data.ruc_by_channel_30d?.length > 0 ? (
                <>
                    <h4 style={{ fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.12em', margin: '8px 0 4px', color: '#8a6817' }}>{t('RUC po kanalu (30d)', 'GM by channel (30d)')}</h4>
                    <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 8 }}>
                        {data.ruc_by_channel_30d.map((c: any) => (
                            <div key={c.kanal} style={{ background: '#fff', padding: '6px 12px', borderRadius: 6, fontSize: '0.78rem' }}>
                                <strong>{c.kanal}</strong>: {fmt(c.expected_ruc_eur)} € ({Number(c.avg_ruc_pct ?? 0).toFixed(1)}%) · {c.cnt}
                            </div>
                        ))}
                    </div>
                </>
            ) : null}
            {data.ruc_by_supplier_30d?.length > 0 ? (
                <>
                    <h4 style={{ fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.12em', margin: '8px 0 4px', color: '#8a6817' }}>{t('RUC po dobavljaču realized (30d)', 'GM by supplier realized (30d)')}</h4>
                    <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                        {data.ruc_by_supplier_30d.map((s: any) => (
                            <div key={s.source_supplier_name} style={{ background: '#fff', padding: '6px 12px', borderRadius: 6, fontSize: '0.78rem' }}>
                                <strong>{s.source_supplier_name}</strong>: {fmt(s.realized_ruc_eur)} € · drift {Number(s.drift_eur) >= 0 ? '+' : ''}{fmt(s.drift_eur)}
                            </div>
                        ))}
                    </div>
                </>
            ) : null}
        </section>
    );
}

function PulseWall({ data, funnel, locale }: { data: PulsePayload['pulse']; funnel: FunnelStage[]; locale: 'hr' | 'en' }) {
    const t = (hr: string, en: string): string => (locale === 'en' ? en : hr);
    const cards = [
        { label: t('Promet (po ponudi)', 'Revenue (offer)'), value: formatMoney(data.realized_total, locale), hint: t(`${formatNumber(data.realized_count, locale)} realiziranih ponuda`, `${formatNumber(data.realized_count, locale)} realized offers`) },
        { label: t('Konverzija', 'Conversion'), value: `${data.conversion_by_count_pct.toFixed(1)}% / ${data.conversion_by_value_pct.toFixed(1)}%`, hint: t('po broju / po vrijednosti', 'by count / by value') },
        {
            label: t('RUC (marža)', 'RUC (margin)'),
            value: data.ruc_total_eur != null ? (data.ruc_estimated ? '⁓' : '') + formatMoney(data.ruc_total_eur, locale) : '—',
            hint: data.ruc_total_pct != null
                ? `${(data.ruc_estimated ? '⁓' : '') + data.ruc_total_pct.toFixed(1)}% · ${data.ruc_estimated ? t(`${data.ruc_actual_share_pct ?? 0}% stvaran, ostalo proc. (P&L)`, `${data.ruc_actual_share_pct ?? 0}% actual, rest est. (P&L)`) : t('stvaran', 'actual')}`
                : '—',
        },
        { label: t('Otvoreni rizik', 'Open risk'), value: formatMoney(data.open_value, locale), hint: t(`${formatNumber(data.open_count, locale)} ponuda bez računa`, `${formatNumber(data.open_count, locale)} offers without invoice`) },
        { label: t('Lead time', 'Lead time'), value: data.avg_lead_days > 0 ? `${data.avg_lead_days.toFixed(1)} ${t('dana', 'days')}` : '—', hint: t('prosjek od ponude do računa', 'avg from offer to invoice') },
        { label: t('Otkup (zasebno)', 'Buyback (separate)'), value: data.buyback_count > 0 ? formatMoney(data.buyback_amount, locale) : '—', hint: t(`${formatNumber(data.buyback_count, locale)} otkupa`, `${formatNumber(data.buyback_count, locale)} buybacks`) },
    ];
    const maxStageCount = funnel.length > 0 ? Math.max(...funnel.map((s) => s.count)) : 1;

    return (
        <div className="ado-m1__pulse">
            <div className="ado-m1__cards">
                {cards.map((c) => (
                    <article className="ado-m1__card" key={c.label}>
                        <h2>{c.value}</h2>
                        <h3>{c.label}</h3>
                        <p>{c.hint}</p>
                    </article>
                ))}
            </div>

            {/* Gold supply chain mirror (Priority D) — čita bi_gold_daily_offers + bi_delivery_allocations */}
            <GoldStatsMirror locale={locale} />

            <section className="ado-m1__funnel">
                <h3>{t('Funnel — pipeline ponuda', 'Funnel — offer pipeline')}</h3>
                <div className="ado-m1__funnel-bars">
                    {funnel.map((s) => {
                        const pct = maxStageCount > 0 ? (s.count / maxStageCount) * 100 : 0;
                        return (
                            <div className="ado-m1__funnel-row" key={s.stage}>
                                <div className="ado-m1__funnel-label">{s.stage}</div>
                                <div className="ado-m1__funnel-bar" style={{ width: `${pct}%` }}>
                                    <span className="ado-m1__funnel-count">{formatNumber(s.count, locale)}</span>
                                </div>
                                {s.value > 0 && (
                                    <div className="ado-m1__funnel-value">{formatMoney(s.value, locale)}</div>
                                )}
                            </div>
                        );
                    })}
                </div>
            </section>
            <section className="ado-m1__totals">
                <h3>{t('Ukupna pozicija', 'Total position')}</h3>
                <dl>
                    <div><dt>{t('Ukupno ponuda', 'Total offers')}</dt><dd>{formatNumber(data.offers_count, locale)}</dd></div>
                    <div><dt>{t('Ukupan iznos ponuda', 'Total offered')}</dt><dd>{formatMoney(data.offered_total, locale)}</dd></div>
                    <div><dt>{t('Iznos s računom', 'Realized (offer-amount)')}</dt><dd>{formatMoney(data.realized_total, locale)}</dd></div>
                    <div><dt>{t('Stvarno fakturirano', 'Actually invoiced')}</dt><dd>{formatMoney(data.invoiced_total, locale)}</dd></div>
                    <div><dt>{t('Otvoreni iznos', 'Open value')}</dt><dd>{formatMoney(data.open_value, locale)}</dd></div>
                </dl>
            </section>
        </div>
    );
}

type YoyRow = {
    bucket: string;
    bucket_is_person?: boolean;
    years: Record<string, { offered: number; realized: number; offers_count: number }>;
    total_offered: number;
    yoy_pct: number | null;
};

function PerformancePanel({
    rows, trend, dim, setDim, granularity, setGranularity, countries, locale, token, axis,
}: {
    rows: BreakdownRow[]; trend: TrendRow[]; dim: Dimension; setDim: (d: Dimension) => void;
    granularity: Granularity; setGranularity: (g: Granularity) => void;
    countries: string;
    locale: 'hr' | 'en'; token: string; axis: Axis;
}) {
    const t = (hr: string, en: string): string => (locale === 'en' ? en : hr);
    const maxOffered = rows.length > 0 ? Math.max(...rows.map((r) => r.offered)) : 1;
    const maxTrend = trend.length > 0 ? Math.max(...trend.map((tr) => Math.max(tr.offered, tr.realized))) : 1;

    return (
        <PerformancePanelInner
            rows={rows} trend={trend} dim={dim} setDim={setDim}
            granularity={granularity} setGranularity={setGranularity}
            countries={countries}
            locale={locale} token={token} axis={axis} maxOffered={maxOffered} maxTrend={maxTrend}
        />
    );
}

function PerformancePanelInner({
    rows, trend, dim, setDim, granularity, setGranularity, countries, locale, token, axis, maxOffered, maxTrend,
}: {
    rows: BreakdownRow[]; trend: TrendRow[]; dim: Dimension; setDim: (d: Dimension) => void;
    granularity: Granularity; setGranularity: (g: Granularity) => void;
    countries: string;
    locale: 'hr' | 'en'; token: string; axis: Axis; maxOffered: number; maxTrend: number;
}) {
    const t = (hr: string, en: string): string => (locale === 'en' ? en : hr);
    const [showYoy, setShowYoy] = useState(false);
    const [yoyRows, setYoyRows] = useState<YoyRow[]>([]);
    const [yoyLoading, setYoyLoading] = useState(false);
    const [yoyYears, setYoyYears] = useState<number[]>([2024, 2025, 2026]);
    const [yoyDrill, setYoyDrill] = useState<{ bucket: string; year: number } | null>(null);
    const [yoyDrillRows, setYoyDrillRows] = useState<any[]>([]);
    const [yoyDrillLoading, setYoyDrillLoading] = useState(false);
    // Sortiranje razrade (klik na zaglavlje kolone, kao u live feedu).
    type BKey = 'bucket' | 'offers_count' | 'offered' | 'realized' | 'open_value' | 'conversion_by_count_pct' | 'conversion_by_value_pct' | 'ruc_total_eur' | 'ruc_total_pct';
    const [bSortKey, setBSortKey] = useState<BKey>('offered');
    const [bSortDir, setBSortDir] = useState<'asc' | 'desc'>('desc');
    const sortedRows = React.useMemo(() => {
        const arr = [...rows];
        arr.sort((a, b) => {
            const av: any = (a as any)[bSortKey] ?? (bSortKey === 'bucket' ? '' : 0);
            const bv: any = (b as any)[bSortKey] ?? (bSortKey === 'bucket' ? '' : 0);
            const c = bSortKey === 'bucket' ? String(av).localeCompare(String(bv)) : (Number(av) - Number(bv));
            return bSortDir === 'asc' ? c : -c;
        });
        return arr;
    }, [rows, bSortKey, bSortDir]);
    const bToggle = (k: BKey) => { if (bSortKey === k) setBSortDir((d) => d === 'asc' ? 'desc' : 'asc'); else { setBSortKey(k); setBSortDir(k === 'bucket' ? 'asc' : 'desc'); } };
    const bArrow = (k: BKey) => bSortKey === k ? (bSortDir === 'asc' ? ' ▲' : ' ▼') : '';
    const thSort: React.CSSProperties = { cursor: 'pointer', userSelect: 'none' };

    useEffect(() => {
        if (!yoyDrill) { setYoyDrillRows([]); return; }
        setYoyDrillLoading(true);
        const p = new URLSearchParams({ dim, bucket: yoyDrill.bucket, year: String(yoyDrill.year), axis, countries });
        api<{ rows: any[] }>('/ado-m1/yoy-cell?'+p.toString(), token)
            .then((r) => setYoyDrillRows(r.rows))
            .catch(() => undefined)
            .finally(() => setYoyDrillLoading(false));
    }, [yoyDrill, dim, axis, countries, token]);

    useEffect(() => {
        if (!showYoy) return;
        setYoyLoading(true);
        const p = new URLSearchParams({ dim, axis, countries });
        api<{ rows: YoyRow[]; years: number[] }>('/ado-m1/breakdown-yoy?'+p.toString(), token)
            .then((r) => { setYoyRows(r.rows); setYoyYears(r.years); })
            .catch(() => undefined)
            .finally(() => setYoyLoading(false));
    }, [showYoy, dim, axis, countries, token]);

    return (
        <div className="ado-m1__performance">
            <div className="ado-m1__perf-controls" style={{ display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'center' }}>
                <label>
                    {t('Dimenzija', 'Dimension')}:
                    <select value={dim} onChange={(e) => setDim(e.target.value as Dimension)}>
                        {DIM_OPTIONS.map((d) => (
                            <option key={d.value} value={d.value}>{locale === 'en' ? d.labelEn : d.labelHr}</option>
                        ))}
                    </select>
                </label>
                <label>
                    {t('Granularnost trenda', 'Trend granularity')}:
                    <select value={granularity} onChange={(e) => setGranularity(e.target.value as Granularity)}>
                        <option value="dan">{t('Dan', 'Day')}</option>
                        <option value="tjedan">{t('Tjedan', 'Week')}</option>
                        <option value="mjesec">{t('Mjesec', 'Month')}</option>
                        <option value="kvartal">{t('Kvartal', 'Quarter')}</option>
                        <option value="godina">{t('Godina', 'Year')}</option>
                    </select>
                </label>
                <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '4px 10px', background: showYoy ? 'rgba(31,122,54,0.10)' : 'rgba(0,0,0,0.04)', border: '1px solid '+(showYoy ? '#1f7a36' : 'rgba(0,0,0,0.15)'), borderRadius: 4, cursor: 'pointer', fontSize: '0.82rem' }}>
                    <input type="checkbox" checked={showYoy} onChange={(e) => setShowYoy(e.target.checked)} />
                    {t('YoY usporedba 2024/2025/2026', 'YoY 2024/2025/2026')}
                </label>
            </div>

            {showYoy ? (
                <section>
                    <h3>{t(`YoY po: ${dim}`, `YoY by: ${dim}`)}</h3>
                    {yoyLoading ? <p className="muted">{t('Učitavam YoY…', 'Loading YoY…')}</p> : (
                        <table className="admin-user-table">
                            <thead>
                                <tr>
                                    <th>{t(dim, dim)}</th>
                                    {yoyYears.map((y) => (
                                        <th key={y} style={{ textAlign: 'right' }}>{y}</th>
                                    ))}
                                    <th style={{ textAlign: 'right' }}>{t('YoY %', 'YoY %')}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {yoyRows.map((r) => (
                                    <tr key={r.bucket}>
                                        <td><strong><PersonName value={r.bucket} isPerson={r.bucket_is_person} /></strong></td>
                                        {yoyYears.map((y) => {
                                            const cell = r.years[String(y)];
                                            const clickable = cell && cell.offered > 0;
                                            return (
                                                <td
                                                    key={y}
                                                    style={{ textAlign: 'right', whiteSpace: 'nowrap', cursor: clickable ? 'pointer' : 'default' }}
                                                    onClick={() => clickable && setYoyDrill({ bucket: r.bucket, year: y })}
                                                    title={clickable ? t('Klik za detalje', 'Click for details') : ''}
                                                >
                                                    {clickable
                                                        ? <><strong style={{ textDecoration: 'underline dotted', textUnderlineOffset: 2 }}>{formatMoney(cell.offered, locale)}</strong><br /><span className="muted" style={{ fontSize: '0.7rem' }}>{cell.offers_count} {t('pon', 'off')}</span></>
                                                        : <span className="muted">—</span>}
                                                </td>
                                            );
                                        })}
                                        <td style={{ textAlign: 'right', fontWeight: 700, color: r.yoy_pct == null ? '#999' : r.yoy_pct >= 0 ? '#1f7a36' : '#b14040' }}>
                                            {r.yoy_pct == null ? '—' : (r.yoy_pct >= 0 ? '+' : '')+r.yoy_pct+'%'}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </section>
            ) : null}

            {yoyDrill ? (
                <div role="dialog" aria-modal="true" onClick={() => setYoyDrill(null)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 1000, display: 'flex', justifyContent: 'flex-end' }}>
                    <div onClick={(e) => e.stopPropagation()} style={{ width: 'min(720px, 96vw)', background: '#fff', height: '100vh', overflowY: 'auto', padding: 0 }}>
                        <div style={{ padding: '14px 20px', background: 'linear-gradient(135deg, #6b4f1c, #c99b31)', color: '#fff', position: 'sticky', top: 0, zIndex: 5, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <h3 style={{ margin: 0, fontSize: '1rem' }}>📋 {yoyDrill.bucket} · {yoyDrill.year} · top {yoyDrillRows.length} {t('ponuda', 'offers')}</h3>
                            <button type="button" onClick={() => setYoyDrill(null)} style={{ background: 'rgba(255,255,255,0.2)', border: 0, color: '#fff', padding: '4px 10px', borderRadius: 4, cursor: 'pointer' }}>✕</button>
                        </div>
                        <div style={{ padding: 14 }}>
                            {yoyDrillLoading ? <p className="muted">{t('Učitavam…', 'Loading…')}</p> : (
                                <table className="admin-user-table" style={{ fontSize: '0.78rem' }}>
                                    <thead><tr><th>{t('Broj', 'Offer #')}</th><th>{t('Datum', 'Date')}</th><th>{t('Partner', 'Partner')}</th><th style={{ textAlign: 'right' }}>{t('Iznos', 'Amount')}</th><th>{t('Računi', 'Inv.')}</th></tr></thead>
                                    <tbody>
                                        {yoyDrillRows.map((r, i) => (
                                            <tr key={i}>
                                                <td><code>{r.offer_number}</code></td>
                                                <td>{r.offer_date}</td>
                                                <td><PersonName value={r.partner_name} /> <span className="muted" style={{ fontSize: '0.72rem' }}>({r.partner_code})</span></td>
                                                <td style={{ textAlign: 'right', fontWeight: 700 }}>{formatMoney(r.amount_offered, locale)}</td>
                                                <td>{r.invoice_count > 0 ? `✓ ${r.invoice_count}` : '—'}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            )}
                        </div>
                    </div>
                </div>
            ) : null}

            {/* Treemap visual ako je dimenzija kategorička i podaci postoje */}
            {(dim === 'kanal' || dim === 'poslovnica' || dim === 'partner' || dim === 'doc_prefix') && rows.length > 0 ? (
                <section style={{ marginBottom: 16 }}>
                    <h3 style={{ marginBottom: 6 }}>🟩 {t(`Mix po: ${dim}`, `Mix by: ${dim}`)}</h3>
                    <Treemap
                        items={rows.slice(0, 10).map((r) => ({ label: r.bucket, value: r.offered }))}
                        height={200}
                        locale={locale}
                        moneyFormatter={(n) => formatMoney(n, locale)}
                    />
                </section>
            ) : null}

            {/* Trend graf — odmah ispod "Mix po …" (premješteno s dna na zahtjev). */}
            <TrendSection trend={trend} granularity={granularity} locale={locale} />

            {/* Ranked bars — top 10 po realiziranom (zeleno) + open (žuto) za isti dim */}
            {rows.length > 0 ? (
                <section style={{ marginBottom: 16 }}>
                    <h3 style={{ marginBottom: 6 }}>📊 {t(`Top 10 po realizaciji: ${dim}`, `Top 10 by realized: ${dim}`)}</h3>
                    <div style={{ background: '#fff', border: '1px solid rgba(184,156,91,0.25)', borderRadius: 10, padding: '12px 14px' }}>
                        <RankedBar
                            locale={locale}
                            maxBars={10}
                            barColor="#1f7a36"
                            items={rows.map((r) => ({
                                label: r.bucket,
                                value: r.realized,
                                sub: `${r.offers_count} ${t('pon', 'off')} · ${r.conversion_by_value_pct.toFixed(0)}% ${t('konv.', 'conv.')}`,
                            }))}
                            moneyFormatter={(n) => formatMoney(n, locale)}
                        />
                    </div>
                </section>
            ) : null}

            <section className="ado-m1__breakdown">
                <h3>{t(`Razrada po: ${dim}`, `Breakdown by: ${dim}`)}</h3>
                <table>
                    <thead>
                        <tr>
                            <th style={thSort} onClick={() => bToggle('bucket')}>{t(dim, dim)}{bArrow('bucket')}</th>
                            <th style={thSort} onClick={() => bToggle('offers_count')}>{t('Ponude', 'Offers')}{bArrow('offers_count')}</th>
                            <th style={thSort} onClick={() => bToggle('offered')}>{t('Iznos ponuda', 'Offered')}{bArrow('offered')}</th>
                            <th style={thSort} onClick={() => bToggle('realized')}>{t('Realizirano', 'Realized')}{bArrow('realized')}</th>
                            <th style={thSort} onClick={() => bToggle('open_value')}>{t('Otvoreno', 'Open')}{bArrow('open_value')}</th>
                            <th style={thSort} onClick={() => bToggle('conversion_by_count_pct')}>{t('% kol.', '% cnt')}{bArrow('conversion_by_count_pct')}</th>
                            <th style={thSort} onClick={() => bToggle('conversion_by_value_pct')}>{t('% vrij.', '% val')}{bArrow('conversion_by_value_pct')}</th>
                            <th style={thSort} onClick={() => bToggle('ruc_total_eur')} title={t('RUC = stvarni (JSON cost) + procijenjeni (⁓ realizirano × godišnja bruto marža iz P&L)', 'RUC = actual (JSON cost) + estimated (⁓ realized × annual gross margin from P&L)')}>{t('RUC €', 'RUC €')}{bArrow('ruc_total_eur')}</th>
                            <th style={thSort} onClick={() => bToggle('ruc_total_pct')} title={t('RUC % nad realiziranim. ⁓ = procjenom dopunjeno (nema JSON cost)', 'RUC % over realized. ⁓ = estimate-augmented (no JSON cost)')}>{t('RUC %', 'RUC %')}{bArrow('ruc_total_pct')}</th>
                            <th />
                        </tr>
                    </thead>
                    <tbody>
                        {sortedRows.map((r) => {
                            const rucEur = r.ruc_total_eur ?? r.ruc_accept_eur ?? null;
                            const rucPct = r.ruc_total_pct ?? r.ruc_accept_pct ?? null;
                            const rucColor = (rucEur ?? 0) >= 0 ? '#1f7a36' : '#b14040';
                            const est = r.ruc_estimated === true;
                            const estTitle = est ? t(`Procijenjeno: ${r.ruc_actual_share_pct ?? 0}% RUC-a iz stvarnog JSON cost-a, ostalo iz godišnje marže (P&L)`, `Estimated: ${r.ruc_actual_share_pct ?? 0}% of RUC from actual JSON cost, rest from annual margin (P&L)`) : '';
                            return (
                                <tr key={r.bucket}>
                                    <td><PersonName value={r.bucket} isPerson={r.bucket_is_person} hint={r.bucket_hint} /></td>
                                    <td>{formatNumber(r.offers_count, locale)}</td>
                                    <td>{formatMoney(r.offered, locale)}</td>
                                    <td>{formatMoney(r.realized, locale)}</td>
                                    <td>{formatMoney(r.open_value, locale)}</td>
                                    <td>{r.conversion_by_count_pct.toFixed(1)}%</td>
                                    <td>{r.conversion_by_value_pct.toFixed(1)}%</td>
                                    <td style={{ color: rucColor, fontWeight: 600 }} title={estTitle}>{rucEur != null ? (est ? '⁓' : '') + formatMoney(rucEur, locale) : '—'}</td>
                                    <td style={{ color: rucColor }} title={estTitle}>{rucPct != null ? (est ? '⁓' : '') + rucPct.toFixed(1) + '%' : '—'}</td>
                                    <td className="ado-m1__bar-cell">
                                        <div className="ado-m1__bar" style={{ width: `${(r.offered / maxOffered) * 100}%` }} />
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </section>
        </div>
    );
}

function TrendSection({ trend, granularity, locale }: { trend: TrendRow[]; granularity: Granularity; locale: 'hr' | 'en' }) {
    const t = (hr: string, en: string) => (locale === 'en' ? en : hr);
    const [showForecast, setShowForecast] = useState(true);
    const [showSpot, setShowSpot] = useState(true);

    // Linear regression forecast 3 perioda unaprijed (na osnovu zadnjih 6)
    const forecast = useMemo(() => {
        if (trend.length < 3) return [] as TrendRow[];
        const recent = trend.slice(-6);
        const n = recent.length;
        const xMean = (n - 1) / 2;
        const fcastFor = (key: 'offered' | 'realized'): number[] => {
            const yMean = recent.reduce((s, r) => s + r[key], 0) / n;
            const num = recent.reduce((s, r, i) => s + (i - xMean) * (r[key] - yMean), 0);
            const den = recent.reduce((s, _r, i) => s + (i - xMean) ** 2, 0);
            const slope = den === 0 ? 0 : num / den;
            const intercept = yMean - slope * xMean;
            return [0, 1, 2].map((step) => Math.max(0, intercept + slope * (n + step)));
        };
        const offFc = fcastFor('offered');
        const reaFc = fcastFor('realized');
        return offFc.map((v, i) => ({
            period: '+'+(i + 1)+(granularity === 'mjesec' ? 'm' : granularity === 'tjedan' ? 'w' : granularity === 'kvartal' ? 'q' : 'd'),
            offers_count: 0, offered: v, realized: reaFc[i], open_value: 0,
        }));
    }, [trend, granularity]);

    if (trend.length < 2) return <section className="ado-m1__trend"><h3>{t('Trend', 'Trend')}</h3><p className="muted">{t('Nema dovoljno podataka.', 'Not enough data.')}</p></section>;

    // Build series: historija + forecast (s null padding-om za poravnanje)
    const histLen = trend.length;
    const fcLen = forecast.length;
    const totalLen = histLen + fcLen;
    const fcOffsetPad = Array(histLen - 1).fill(null);
    const lastHistOff = trend[histLen - 1].offered;
    const lastHistRea = trend[histLen - 1].realized;

    const seriesData: Array<{ name: string; color: string; values: Array<number | null>; style?: 'solid' | 'dashed' }> = [
        { name: t('Ponuđeno', 'Offered'), color: '#6b4f1c', values: [...trend.map((p) => p.offered), ...Array(fcLen).fill(null)] },
        { name: t('Realizirano', 'Realized'), color: '#1f7a36', values: [...trend.map((p) => p.realized), ...Array(fcLen).fill(null)] },
    ];
    if (showForecast && fcLen > 0) {
        seriesData.push(
            { name: t('Forecast ponuđeno', 'Forecast offered'), color: '#c99b31', style: 'dashed', values: [...fcOffsetPad, lastHistOff, ...forecast.map((f) => f.offered)] },
            { name: t('Forecast realizirano', 'Forecast realized'), color: '#5ca36f', style: 'dashed', values: [...fcOffsetPad, lastHistRea, ...forecast.map((f) => f.realized)] },
        );
    }
    if (showSpot) {
        const spotVals = trend.map((p) => p.spot_eur_per_g ?? null);
        if (spotVals.some((v) => v != null)) {
            // Skaliraj spot da bude vidljiv na istoj Y skali — multiply by 1000 (€/g je male brojeve)
            const maxBudget = Math.max(...trend.map((p) => Math.max(p.offered, p.realized)));
            const maxSpot = Math.max(...spotVals.filter((v): v is number => v != null));
            const scale = maxSpot > 0 ? maxBudget / maxSpot : 1;
            seriesData.push({ name: t('Spot zlato (€/g, skalirano)', 'Gold spot (€/g, scaled)'), color: 'rgba(177,64,64,0.7)', values: [...spotVals.map((v) => v == null ? null : v * scale), ...Array(fcLen).fill(null)] });
        }
    }

    const xLabels = [
        ...trend.map((p) => p.period.slice(0, 7)),
        ...forecast.map((f) => f.period),
    ];

    return (
        <section className="ado-m1__trend">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                <h3 style={{ margin: 0 }}>📈 {t(`Trend (${granularity})`, `Trend (${granularity})`)}</h3>
                <div style={{ display: 'flex', gap: 6 }}>
                    <label style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: '0.78rem', cursor: 'pointer' }}>
                        <input type="checkbox" checked={showForecast} onChange={(e) => setShowForecast(e.target.checked)} />
                        {t('Predviđanje +3', 'Forecast +3')}
                    </label>
                    <label style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: '0.78rem', cursor: 'pointer' }}>
                        <input type="checkbox" checked={showSpot} onChange={(e) => setShowSpot(e.target.checked)} />
                        {t('Spot zlato', 'Gold spot')}
                    </label>
                </div>
            </div>

            <div style={{ background: '#fff', border: '1px solid rgba(184,156,91,0.3)', borderRadius: 6, padding: 12 }}>
                <NiceLineChart
                    series={seriesData}
                    height={260}
                    locale={locale}
                    xLabels={xLabels}
                    yFormatter={(n) => n >= 1e6 ? (n/1e6).toFixed(1)+'M €' : n >= 1e3 ? (n/1e3).toFixed(0)+'k €' : Math.round(n)+' €'}
                />
                <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', justifyContent: 'center', marginTop: 6, fontSize: '0.74rem' }}>
                    {seriesData.map((s) => (
                        <span key={s.name} style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                            <span style={{ width: 14, borderTop: s.style === 'dashed' ? `2px dashed ${s.color}` : `2px solid ${s.color}`, display: 'inline-block' }} />
                            {s.name}
                        </span>
                    ))}
                </div>
            </div>
        </section>
    );
}

type LiveOfferRow = {
    id: number | string; // ERP-only ima 'erp_NNN'; JSON ima number
    source?: 'erp_only' | 'live';
    broj_ponude: string; datum_ponude: string; datum_prihvacanja: string | null;
    id_poslovnice: number | null; poslovnica: string | null;
    sifra_artikla: string | null; naziv_artikla: string | null; vrsta_kovine: string | null;
    kolicina: number; fine_ukupno_g: number;
    iznos_bez_pdv: number; iznos_sa_pdv: number;
    spot_na_izdavanju: number | null; vrijednost_na_izdavanju: number | null;
    spot_na_prihvacanju: number | null; vrijednost_na_prihvacanju: number | null;
    hedge_status: string | null; hedge_deal_id: number | null; hedge_qty_oz: number | null;
    hedge_deal_label: string | null; hedge_price: number | null; hedge_date: string | null;
    ruc_planiran_eur: number; ruc_planiran_pct: number;
    ruc_realiziran_eur: number | null; ruc_realiziran_pct: number | null;
    nabavni_premium_eur?: number;
    ruc_planiran_neto_eur?: number; ruc_planiran_neto_pct?: number;
    ruc_realiziran_neto_eur?: number | null; ruc_realiziran_neto_pct?: number | null;
    hedge_price_per_oz: number | null; fixed_spot_per_g: number | null;
};
type LiveOffersSummary = {
    count_offers: number; count_lines: number;
    total_prodajno: number; total_nabavna_izdavanje: number; total_nabavna_prihvacanje: number;
    total_prodajno_prihvaceno?: number;
    total_purchase_premium_eur?: number; total_purchase_premium_accepted_eur?: number;
    total_fine_g: number; accepted_count: number;
    hedged_full_count: number; hedged_partial_count: number;
};
type LiveOffersResponse = {
    rows: LiveOfferRow[]; summary: LiveOffersSummary;
    buyback_rows?: Array<{ id: number; type: 'otkup_investicijsko' | 'otkup_lom'; txn_at: string; kasa_name: string | null; doc_ref: string; amount_eur: number; gold_fine_g: number | null; silver_fine_g: number | null; ruc_eur: number | null }>;
    filters_available: { poslovnice: Array<{ id_poslovnice: number; poslovnica: string }> };
    pagination: { total: number; page: number; per_page: number; total_pages: number; from_idx: number; to_idx: number };
    breakdown: {
        by_metal: Array<{ vrsta_kovine: string | null; count_lines: number; count_offers: number; total_eur: number; total_fine_g: number }>;
        by_branch: Array<{ id_poslovnice: number | null; poslovnica: string | null; count_lines: number; count_offers: number; total_eur: number; total_fine_g: number }>;
        by_size: Array<{ fine_per_unit: number; count_lines: number; count_offers: number; total_eur: number; total_fine_g: number; total_kom: number }>;
        by_mint: Array<{ mint: string; count_lines: number; count_offers: number; total_eur: number; total_fine_g: number }>;
        by_type?: Array<{ type: 'prodaja_investicijsko' | 'otkup_investicijsko' | 'otkup_lom'; count_lines: number; count_offers: number; total_eur: number; total_fine_g: number; ruc_eur?: number; ruc_neto_eur?: number; nabavni_premium_eur?: number; cash_dir?: number }>;
        buyback_lom?: { count: number; gold_fine_g: number; silver_fine_g: number; nabavna_eur: number; prihod_eur: number; ruc_eur: number } | null;
    };
    applied: Record<string, any>;
};

function LiveOfferCard({
    r, idx, onOpen, locale, fmtMoney, fmtNum, fmtPct, token, onAnnotationsChanged,
}: {
    r: any; idx: number; onOpen: () => void; locale: 'hr' | 'en';
    fmtMoney: (n: number | null | undefined) => string;
    fmtNum: (n: number | null | undefined, d?: number) => string;
    fmtPct: (n: number | null | undefined) => string;
    token: string;
    onAnnotationsChanged?: () => void;
}) {
    const t = (hr: string, en: string) => (locale === 'en' ? en : hr);
    const isSilver = r.vrsta_kovine === 'Srebro';
    const isAccepted = !!r.datum_prihvacanja;
    const isHedged = r.hedge_status === 'full' || r.hedge_status === 'partial';

    return (
        <div onClick={onOpen} className="bi-offer-card" data-accepted={isAccepted ? 'y' : 'n'} data-source={r.source ?? 'live'} title={t('Klik za detalje', 'Click for details')}>
            <div className="bi-offer-row1">
                <SourceBadge source={r.source} locale={locale} />
                <span className="bi-offer-idx">#{idx}</span>
                <span className="bi-offer-datum" title={t('Izdana', 'Issued') + ' ' + (r.datum_ponude ?? '').slice(0, 16).replace('T', ' ')}>
                    {(r.datum_prihvacanja ?? r.datum_ponude ?? '').slice(0, 16).replace('T', ' ')}
                    {r.datum_prihvacanja && r.datum_ponude && r.datum_prihvacanja.slice(0, 10) !== r.datum_ponude.slice(0, 10)
                        ? <span style={{ color: '#8a6817', marginLeft: 6, fontWeight: 400 }}>{t('izd.', 'iss.')} {r.datum_ponude.slice(8, 10)}.{r.datum_ponude.slice(5, 7)}.</span>
                        : null}
                </span>
                <span className="bi-offer-broj">
                    <strong>{r.broj_ponude}</strong>
                    {isAccepted ? <span style={{ color: '#1f7a36', marginLeft: 4 }}>✓</span> : null}
                    {isHedged ? <span style={{ color: r.hedge_status === 'full' ? '#1f7a36' : '#8a6817', marginLeft: 4 }}>{r.hedge_status === 'full' ? 'H✓' : 'H⚠'}</span> : null}
                </span>
                {r.erp_partner_name ? (
                    <span className="bi-offer-partner" title={r.erp_partner_name + (r.erp_offer_number ? ' • ERP ' + r.erp_offer_number : '')}>
                        👤 {r.erp_partner_name}
                    </span>
                ) : null}
                <span className="bi-offer-posl" title={r.poslovnica ?? ''}>🏪 {r.poslovnica ?? '—'}</span>
                <span className="bi-offer-artikl" title={r.naziv_artikla ?? ''}>
                    {r.source === 'erp_only' ? null : <><span style={{ color: isSilver ? '#7a7a7a' : '#c99b31', fontWeight: 700 }}>{isSilver ? 'Ag' : 'Au'}</span>{' '}</>}
                    {r.naziv_artikla ?? '—'}
                </span>
                <span className="bi-offer-iznos">{fmtMoney(r.iznos_sa_pdv)}</span>
            </div>
            <div className="bi-offer-row2">
                <Kpi label={t('Spot izd.', 'Spot iss.')} value={fmtNum(r.spot_na_izdavanju, 2)} />
                <Kpi label={t('Spot prihv.', 'Spot acc.')} value={fmtNum(r.spot_na_prihvacanju, 2)} />
                <Kpi label={t('Valjanost', 'Validity')} value={fmtMinutes(r.valjanost_min, locale)} muted={r.valjanost_min == null} />
                <Kpi label="Hedge" value={r.hedge_price ? fmtNum(r.hedge_price, 2) : '—'} muted={!r.hedge_price} />
                <Kpi label="Fix" value="—" muted />
                <Kpi label={t('RUC pon. €', 'RUC off. €')} value={rucDual(r.ruc_planiran_neto_eur ?? r.ruc_planiran_eur, r.ruc_planiran_eur, fmtMoney)} color={(r.ruc_planiran_neto_eur ?? r.ruc_planiran_eur ?? 0) >= 0 ? 'pos' : 'neg'} strong />
                <Kpi label={t('RUC pon. %', 'RUC off. %')} value={rucDual(r.ruc_planiran_neto_pct ?? r.ruc_planiran_pct, r.ruc_planiran_pct, fmtPct)} color={(r.ruc_planiran_neto_eur ?? r.ruc_planiran_eur ?? 0) >= 0 ? 'pos' : 'neg'} />
                <Kpi label={t('RUC pot. €', 'RUC cnf. €')} value={rucDual(r.ruc_realiziran_neto_eur ?? r.ruc_realiziran_eur, r.ruc_realiziran_eur, fmtMoney)} color={(r.ruc_realiziran_neto_eur ?? r.ruc_realiziran_eur ?? 0) >= 0 ? 'pos' : 'neg'} strong muted={r.ruc_realiziran_eur == null} />
                <Kpi label={t('RUC pot. %', 'RUC cnf. %')} value={rucDual(r.ruc_realiziran_neto_pct ?? r.ruc_realiziran_pct, r.ruc_realiziran_pct, fmtPct)} color={(r.ruc_realiziran_neto_eur ?? r.ruc_realiziran_eur ?? 0) >= 0 ? 'pos' : 'neg'} muted={r.ruc_realiziran_pct == null} />
                <Kpi label={t('RUC fix. €', 'RUC fix. €')} value="—" muted />
                <Kpi label={t('RUC fix. %', 'RUC fix. %')} value="—" muted />
            </div>
            <AnnotationStrip r={r} locale={locale} token={token} onChanged={onAnnotationsChanged} />
        </div>
    );
}

/** V2 UnifiedOfferCard — 1 ponuda = 1 row, header iz bi_offer_documents + aggregirani line items. */
function UnifiedOfferCard({
    r, idx, onOpen, locale, fmtMoney, fmtNum, token, onAnnotationsChanged,
}: {
    r: any; idx: number; onOpen: () => void; locale: 'hr' | 'en';
    fmtMoney: (n: number | null | undefined) => string;
    fmtNum: (n: number | null | undefined, d?: number) => string;
    token: string;
    onAnnotationsChanged?: () => void;
}) {
    const t = (hr: string, en: string) => (locale === 'en' ? en : hr);
    const origin = r.json_origin as string;
    const originMeta: Record<string, { icon: string; label: string; color: string; bg: string }> = {
        erp: { icon: '📋', label: t('ERP', 'ERP'), color: '#6b4f1c', bg: 'rgba(184,156,91,0.18)' },
        json_placeholder: { icon: '⏳', label: t('Samo JSON', 'JSON only'), color: '#8a6817', bg: 'rgba(201,155,49,0.15)' },
        enriched: { icon: '📡✓', label: t('JSON+ERP', 'JSON+ERP'), color: '#1f7a36', bg: 'rgba(31,122,54,0.15)' },
    };
    const om = originMeta[origin] ?? originMeta.erp;
    const acceptedAll = r.is_accepted === true;
    const acceptedSome = r.accepted_lines > 0 && !acceptedAll;
    const partnerLabel = r.partner_name ?? '—';
    const posloLabel = (r.poslovnice ?? []).length > 0 ? (r.poslovnice ?? []).join(', ') : '—';

    return (
        <div onClick={onOpen} className="bi-offer-card" data-source={origin} title={t('Klik za detalje ponude', 'Click for offer details')} style={{ borderLeft: `4px solid ${om.color}` }}>
            <div className="bi-offer-row1">
                <span style={{ background: om.bg, color: om.color, padding: '2px 8px', borderRadius: 6, fontSize: '0.72rem', fontWeight: 700 }}>{om.icon} {om.label}</span>
                <span className="bi-offer-idx">#{idx}</span>
                <span className="bi-offer-datum" title={t('Izdana', 'Issued') + ' ' + (r.offer_date ?? '').slice(0, 10)}>{(r.accepted_date ?? r.offer_date ?? '').slice(0, 10)}</span>
                <span className="bi-offer-broj"><strong>{r.offer_number}</strong>
                    {acceptedAll ? <span style={{ color: '#1f7a36', marginLeft: 4 }}>✓</span> : null}
                    {acceptedSome ? <span style={{ color: '#8a6817', marginLeft: 4 }}>◐</span> : null}
                </span>
                <span className="bi-offer-partner" title={r.partner_code ?? ''}>👤 {partnerLabel}</span>
                <span className="bi-offer-posl" title={posloLabel}>🏪 {posloLabel}</span>
                <span className="bi-offer-artikl">
                    {(r.metals ?? []).map((m: string) => (
                        <span key={m} style={{ color: m === 'Srebro' ? '#7a7a7a' : '#c99b31', fontWeight: 700, marginRight: 4 }}>
                            {m === 'Srebro' ? '🥈 Ag' : '🥇 Au'}
                        </span>
                    ))}
                    <span className="muted" style={{ fontSize: '0.78rem' }}>· {r.line_count ?? 0} {t('stavki', 'lines')}</span>
                </span>
                <span className="bi-offer-iznos">{fmtMoney(r.amount_total)}</span>
            </div>
            <div className="bi-offer-row2">
                <Kpi label={t('Fine g', 'Fine g')} value={fmtNum(r.total_fine_g, 2)} />
                <Kpi label={t('Komada', 'Qty')} value={String(Math.round(Number(r.total_qty ?? 0)))} />
                <Kpi label={t('Računi', 'Invoices')} value={String(r.invoice_count ?? 0)} muted={(r.invoice_count ?? 0) === 0} />
                <Kpi label={t('RUC pon.', 'RUC offer')} value={rucDual(r.ruc_planiran_neto_eur ?? r.ruc_planiran_eur, r.ruc_planiran_eur, fmtMoney)} color={(r.ruc_planiran_neto_eur ?? r.ruc_planiran_eur ?? 0) >= 0 ? 'pos' : 'neg'} strong />
                <Kpi label="%" value={rucDual(r.ruc_planiran_neto_pct ?? r.ruc_planiran_pct, r.ruc_planiran_pct, (n) => (n != null ? `${Number(n).toFixed(1)}%` : '—'))} color={(r.ruc_planiran_neto_eur ?? r.ruc_planiran_eur ?? 0) >= 0 ? 'pos' : 'neg'} />
                <Kpi label={t('RUC pot.', 'RUC cnf.')} value={rucDual(r.ruc_realiziran_neto_eur ?? r.ruc_realiziran_eur, r.ruc_realiziran_eur, fmtMoney)} color={(r.ruc_realiziran_neto_eur ?? r.ruc_realiziran_eur ?? 0) >= 0 ? 'pos' : 'neg'} muted={r.ruc_realiziran_eur == null} strong />
                <Kpi label="%" value={rucDual(r.ruc_realiziran_neto_pct ?? r.ruc_realiziran_pct, r.ruc_realiziran_pct, (n) => (n != null ? `${Number(n).toFixed(1)}%` : '—'))} color={(r.ruc_realiziran_neto_eur ?? r.ruc_realiziran_eur ?? 0) >= 0 ? 'pos' : 'neg'} muted={r.ruc_realiziran_pct == null} />
            </div>
            <AnnotationStrip r={{ offer_document_id: r.offer_document_id, annotations: r.annotations ?? [] }} locale={locale} token={token} onChanged={onAnnotationsChanged} />
        </div>
    );
}

/** Inline note editor s lokalnim state-om i debounce save (800ms idle ili blur). */
function NoteInlineEditor({ value, onSave, placeholder, title }: { value: string; onSave: (v: string) => void; placeholder?: string; title?: string }) {
    const [draft, setDraft] = useState<string>(value);
    // Resync s parent prop-om SAMO kad se ovaj editor ne edituje (parent reload nakon blur-a)
    const editingRef = React.useRef(false);
    React.useEffect(() => { if (! editingRef.current) setDraft(value); }, [value]);
    const flush = React.useCallback((next: string) => { onSave(next); }, [onSave]);
    const timerRef = React.useRef<number | null>(null);
    return (
        <input
            type="text"
            value={draft}
            placeholder={placeholder}
            title={title}
            onFocus={() => { editingRef.current = true; }}
            onBlur={() => {
                editingRef.current = false;
                if (timerRef.current) { window.clearTimeout(timerRef.current); timerRef.current = null; }
                if (draft !== value) flush(draft);
            }}
            onClick={(e) => e.stopPropagation()}
            onChange={(e) => {
                const v = e.target.value;
                setDraft(v);
                if (timerRef.current) window.clearTimeout(timerRef.current);
                timerRef.current = window.setTimeout(() => { flush(v); timerRef.current = null; }, 800);
            }}
            style={{ background: '#fff', color: '#444', border: '1px solid rgba(0,0,0,0.15)', borderRadius: 4, padding: '1px 4px', fontSize: '0.74rem', minWidth: 120, marginLeft: 4, fontWeight: 500 }}
        />
    );
}

/** Inline date editor — UI je dd.mm.yyyy, interno ISO yyyy-mm-dd. Debounced save. */
function DateInlineEditor({ isoValue, onSave, placeholder, title }: { isoValue: string | null; onSave: (iso: string) => void; placeholder?: string; title?: string }) {
    const toDisplay = (iso: string | null) => {
        if (! iso) return '';
        const [y, mo, d] = iso.split('-');
        return y && mo && d ? `${d}.${mo}.${y}` : '';
    };
    const [draft, setDraft] = useState<string>(toDisplay(isoValue));
    const editingRef = React.useRef(false);
    React.useEffect(() => { if (! editingRef.current) setDraft(toDisplay(isoValue)); }, [isoValue]);
    const tryFlush = (v: string) => {
        const m = v.trim().match(/^(\d{1,2})\.(\d{1,2})\.(\d{4})$/);
        if (m) {
            const iso = `${m[3]}-${m[2].padStart(2, '0')}-${m[1].padStart(2, '0')}`;
            if (iso !== isoValue) onSave(iso);
        }
    };
    const timerRef = React.useRef<number | null>(null);
    return (
        <input
            type="text"
            value={draft}
            placeholder={placeholder}
            title={title}
            onFocus={() => { editingRef.current = true; }}
            onBlur={() => {
                editingRef.current = false;
                if (timerRef.current) { window.clearTimeout(timerRef.current); timerRef.current = null; }
                tryFlush(draft);
            }}
            onClick={(e) => e.stopPropagation()}
            onChange={(e) => {
                const v = e.target.value;
                setDraft(v);
                if (timerRef.current) window.clearTimeout(timerRef.current);
                timerRef.current = window.setTimeout(() => { tryFlush(v); timerRef.current = null; }, 800);
            }}
            style={{ background: '#fff', color: '#444', border: '1px solid rgba(0,0,0,0.15)', borderRadius: 4, padding: '1px 4px', fontSize: '0.74rem', width: 88, marginLeft: 4, fontWeight: 500 }}
        />
    );
}

/** Manager skladišta: potvrdi ili odgodi planirani datum isporuke (šalje email poslovnici). */
function ManagerDateDecision({ annotationId, token, locale, onDone }: { annotationId: number; token: string; locale: 'hr' | 'en'; onDone?: () => void }) {
    const t = (hr: string, en: string) => (locale === 'en' ? en : hr);
    const [postponing, setPostponing] = useState(false);
    const [busy, setBusy] = useState(false);
    const decide = async (decision: 'confirmed' | 'postponed', newDate: string | null = null) => {
        setBusy(true);
        try { await mutate(`/offer-annotations/${annotationId}/manager-decision`, token, 'POST', { decision, new_date: newDate }); onDone?.(); }
        catch { /* ignore */ } finally { setBusy(false); setPostponing(false); }
    };
    const btn = { background: 'rgba(255,255,255,0.6)', border: '1px solid rgba(0,0,0,0.15)', borderRadius: 4, padding: '0 5px', cursor: 'pointer', fontSize: '0.7rem', fontWeight: 700 };
    return (
        <span style={{ display: 'inline-flex', gap: 3, alignItems: 'center', marginLeft: 6, paddingLeft: 6, borderLeft: '1px solid rgba(0,0,0,0.15)' }} onClick={(e) => e.stopPropagation()}>
            {!postponing ? (
                <>
                    <button type="button" disabled={busy} title={t('Manager: potvrdi datum', 'Manager: confirm')} onClick={() => void decide('confirmed')} style={{ ...btn, color: '#1f7a36' }}>✓ {t('Potvrdi', 'Confirm')}</button>
                    <button type="button" disabled={busy} title={t('Manager: odgodi', 'Manager: postpone')} onClick={() => setPostponing(true)} style={{ ...btn, color: '#b14040' }}>⏳ {t('Odgodi', 'Delay')}</button>
                </>
            ) : (
                <>
                    <span style={{ fontSize: '0.68rem' }}>{t('Novi:', 'New:')}</span>
                    <DateInlineEditor isoValue={null} onSave={(iso) => void decide('postponed', iso)} placeholder="dd.mm.gggg" title={t('Novi datum isporuke', 'New delivery date')} />
                    <button type="button" onClick={() => setPostponing(false)} style={{ ...btn }}>✕</button>
                </>
            )}
        </span>
    );
}

/**
 * Annotation strip — prikaz aktivnih oznaka + opcionalno dropdown za dodati/ukloniti.
 *
 * Dropdown za PROMJENU anotacija je vidljiv SAMO u m.40.3 Ručna alokacija (gdje
 * poslovnice rade), ne i u m.1 Live Feed gdje je samo display.
 * Default = readonly=true (samo prikaz badge-ova).
 */
const NACIN_LABELS: Record<string, string> = { u_poslovnici: 'U poslovnici', vozac: 'Vozač', gls_posta: 'GLS pošta', centralno: 'Centralno' };
let _deliveryOptsCache: { methods: Array<{ value: string; label: string }>; places: string[]; carriers?: Array<{ value: string; label: string }>; suggested_delivery_date?: string; lead_working_days?: number; min_calendar_days?: number } | null = null;
let _deliveryOptsPromise: Promise<any> | null = null;
function useDeliveryOptions(token: string) {
    const [opts, setOpts] = useState(_deliveryOptsCache);
    useEffect(() => {
        if (_deliveryOptsCache) { setOpts(_deliveryOptsCache); return; }
        if (!_deliveryOptsPromise) {
            _deliveryOptsPromise = api<any>('/offer-annotations/delivery-options', token).then((r) => { _deliveryOptsCache = r; return r; }).catch(() => ({ methods: [], places: [] }));
        }
        let alive = true;
        _deliveryOptsPromise.then((r) => { if (alive) setOpts(r); });
        return () => { alive = false; };
    }, [token]);
    return opts;
}

export function AnnotationStrip({ r, locale, token, onChanged, readonly = true, isManager = false, imamJuLabel }: { r: any; locale: 'hr' | 'en'; token: string; onChanged?: () => void; readonly?: boolean; isManager?: boolean; imamJuLabel?: { hr: string; en: string } }) {
    const t = (hr: string, en: string) => (locale === 'en' ? en : hr);
    // Prikazni naziv statusa imam_ju: Smješteno (Tonći, mail 01.10.2026; shared/orderStatus). Vrijednost 'imam_ju' se ne mijenja.
    const imamJuText = imamJuLabel ? t(imamJuLabel.hr, imamJuLabel.en) : orderStatusLabel('imam_ju', locale);
    const deliveryOpts = useDeliveryOptions(token);
    const [open, setOpen] = useState(false);
    const [busy, setBusy] = useState(false);
    // v347 — click-outside za +Oznaka dropdown
    const dropdownRef = React.useRef<HTMLDivElement>(null);
    React.useEffect(() => {
        if (!open) return;
        const handler = (e: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) setOpen(false);
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, [open]);
    const offerDocId = r.offer_document_id ?? null;
    const orderNumber = r.order_number ?? null;
    const annotations: any[] = r.annotations ?? [];

    // Anchor: ili JSON header (offer_document_id) ili broj narudžbe (order_number) — dovoljno bilo koje.
    if (!offerDocId && !orderNumber) {
        return null;
    }

    const meta: Record<string, { icon: string; label: string; color: string; bg: string }> = {
        imam_ju: { icon: '✋', label: imamJuText, color: '#1f7a36', bg: 'rgba(31,122,54,0.12)' },
        hitno: { icon: '🚨', label: t('Hitno', 'Urgent'), color: '#b14040', bg: 'rgba(177,64,64,0.12)' },
        express: { icon: '🚀', label: t('Express', 'Express'), color: '#fff', bg: '#9333ea' },
        dogovoren_datum: { icon: '📅', label: t('Dogovoren datum', 'Agreed date'), color: '#8a6817', bg: 'rgba(138,104,23,0.14)' },
        alarm: { icon: '⚠', label: t('ALARM', 'ALARM'), color: '#fff', bg: '#b14040' },
        nacin_isporuke: { icon: '🚚', label: t('Način isporuke', 'Delivery method'), color: '#1e40af', bg: 'rgba(30,64,175,0.12)' },
        mjesto_isporuke: { icon: '📍', label: t('Mjesto isporuke', 'Delivery place'), color: '#6b4f1c', bg: 'rgba(184,156,91,0.16)' },
        napomena: { icon: '📝', label: t('Napomena', 'Note'), color: 'var(--ad-ink)', bg: 'rgba(120,113,90,0.16)' },
        // #9 Ružica/T87 (mail UID 663, 18.09.2026): narudžba čeka na klijenta (odluku, uplatu,
        // dogovor) — bilo gdje u tijeku, ne mijenja lifecycle status, ide uz napomenu (razlog).
        na_cekanju_klijent: { icon: '⏸', label: t('Na čekanju', 'On hold'), color: '#b45309', bg: 'rgba(180,83,9,0.14)' },
    };

    // "+ Oznaka" dropdown — POSLOVNICA vidi hitno + EXPRESS + dogovoren datum + mjesto isporuke
    // (može označiti da klijent želi preuzeti u POHRANI / drugoj poslovnici); LOGISTIKA (manager+)
    // dodatno način isporuke (HP/GLS/vozač/centralno — odluka manager-a, ne poslovnice).
    // EXPRESS = za narudžbe > 50.000 EUR, auto-rok 15 radnih dana (server: WorkingDays::add).
    // imam_ju/na_putu/poslano/pakirano/u_dolasku su STATUS (ne flag) — niže.
    const flagTypes: string[] = isManager
        ? ['hitno', 'express', 'dogovoren_datum', 'nacin_isporuke', 'mjesto_isporuke', 'napomena', 'na_cekanju_klijent']
        : ['hitno', 'express', 'dogovoren_datum', 'mjesto_isporuke', 'napomena', 'na_cekanju_klijent'];
    const presentTypes = new Set(annotations.map((a) => a.annotation_type));

    // ── Status narudžbe, jedan niz (Tonći, mail 01.10.2026; shared/orderStatus): bez statusa → U obradi (u_dolasku)
    //    → Pakirano → Poslano (na_putu, prema poslovnici) | Poslano klijentu (poslano) → Smješteno (imam_ju).
    //    Isporučeno je ukinut: postojeće oznake se prikazuju, nove se ne postavljaju. Završni račun zatvara narudžbu.
    const STATUS_STEPS = ORDER_STATUS_SEQUENCE.map((key) => ({
        key, icon: ORDER_STATUS[key].icon, label: key === 'imam_ju' ? imamJuText : orderStatusLabel(key, locale),
    }));
    const naPutuAnn = annotations.find((a) => a.annotation_type === 'na_putu');
    const poslanoAnn = annotations.find((a) => a.annotation_type === 'poslano');
    const currentStatus = currentOrderStatus(annotations);
    const statusIdx = STATUS_STEPS.findIndex((s) => s.key === currentStatus);
    const stepIdx = (key: string) => STATUS_STEPS.findIndex((s) => s.key === key);
    const statusTitle = orderStatusTooltip(currentStatus, locale);
    const carrierAnn = currentStatus === 'poslano' ? poslanoAnn : currentStatus === 'na_putu' ? naPutuAnn : undefined;
    const carrier = carrierAnn?.value ? carrierLabel(carrierAnn.value, locale) : null;
    const showCarrier = (currentStatus === 'na_putu' || currentStatus === 'poslano') && !!carrier;

    // Toast: inline obavijest (zamjenjuje alert popupe)
    const [toast, setToast] = useState<{ msg: string; tone: 'ok' | 'warn' | 'err' } | null>(null);
    React.useEffect(() => { if (!toast) return; const id = setTimeout(() => setToast(null), 6000); return () => clearTimeout(id); }, [toast]);

    // Default values bez popupa:
    //   imam_ju → bez napomene, bez datuma (samo trigger rezervacije)
    //   hitno → bez vrijednosti, inline note input se prikazuje uz chip nakon kreiranja
    //   dogovoren_datum → today default, inline date input dd.mm.yyyy uz chip
    const add = async (type: string, valueArg?: string | null) => {
        // dogovoren_datum → predloži NAJRANIJI IZVEDIV datum (danas + N radnih dana po uputama logistike).
        const date: string | null = type === 'dogovoren_datum'
            ? (deliveryOpts?.suggested_delivery_date ?? new Date().toISOString().slice(0, 10))
            : null;
        const note: string | null = null;
        const value: string | null = valueArg !== undefined ? valueArg : (type === 'nacin_isporuke' ? 'u_poslovnici' : (type === 'mjesto_isporuke' ? (deliveryOpts?.places?.[0] ?? '') : null));
        setBusy(true);
        try {
            const resp = await mutate<{ ok: boolean; id: number; stock_reservation_ids?: number[]; reservation_warnings?: string[] }>('/offer-annotations', token, 'POST', {
                offer_document_id: offerDocId,
                order_number: orderNumber,
                annotation_type: type,
                agreed_delivery_date: date,
                note,
                value,
            });
            if (type === 'imam_ju') {
                const okCount = resp.stock_reservation_ids?.length ?? 0;
                const warnings = resp.reservation_warnings ?? [];
                if (okCount > 0 && warnings.length === 0) {
                    setToast({ msg: t(`✓ Rezervirano ${okCount} stavki.`, `✓ Reserved ${okCount} items.`), tone: 'ok' });
                } else if (okCount > 0 && warnings.length > 0) {
                    // Rezervacija JE napravljena (na ime narudžbe), ali BIS zaliha trenutno kasni za Farosom.
                    // Mjesto je "zadržano" → kad Faros uveze robu, raspoloživo se točno smanji. Oznaka ostaje.
                    setToast({ msg: t(`✓ Rezervirano ${okCount} — mjesto zadržano. (BIS zaliha kasni za Farosom, uskladit će se pri sljedećem uvozu.)`, `✓ Reserved ${okCount} — slot held. (BIS stock lags Faros; reconciles on next import.)`), tone: 'warn' });
                } else if (okCount === 0 && warnings.length > 0) {
                    // Nije ni rezervirano (npr. artikl nije matchan s katalogom), ali oznaku ZADRŽAVAMO —
                    // poslovnica autoritativno tvrdi da ima (Faros = izvor istine).
                    setToast({ msg: t(`✓ Oznaka "${imamJuText}" spremljena. (Rezervacija nije napravljena — provjeri katalog/artikl; Faros je mjerodavan.)`, `✓ "${imamJuText}" flag saved. (No reservation made — check catalog/item; Faros is authoritative.)`), tone: 'warn' });
                }
            }
            setOpen(false);
            onChanged?.();
        } catch (e) {
            // v347 — bolji error context: tip akcije + pravi razlog (formatApiError) + HTTP status hint
            const reason = e instanceof Error ? e.message : 'mreža';
            const action = type === 'imam_ju' ? imamJuText
                : type in ORDER_STATUS ? orderStatusLabel(type, locale)
                : type;
            const userMsg = reason === 'API' || reason === 'mreža'
                ? t(`Nije moguće postaviti "${action}" — javi se IT-u.`, `Could not set "${action}" — contact IT.`)
                : t(`Nije moguće postaviti "${action}": ${reason}`, `Could not set "${action}": ${reason}`);
            setToast({ msg: userMsg, tone: 'err' });
        } finally { setBusy(false); }
    };

    // Update postojeću anotaciju (note ili date) bez popupa — POST je dedupe-aware.
    // NE pozivamo onChanged() ovdje (refresh bi resetirao input cursor) — sync UI samo na blur.
    const updateAnnotation = async (type: string, patch: { note?: string | null; agreed_delivery_date?: string | null; value?: string | null }, opts: { skipReload?: boolean } = {}) => {
        try {
            await mutate('/offer-annotations', token, 'POST', {
                offer_document_id: offerDocId,
                order_number: orderNumber,
                annotation_type: type,
                ...patch,
            });
            if (! opts.skipReload) onChanged?.();
        } catch (e) {
            setToast({ msg: e instanceof Error ? e.message : 'API', tone: 'err' });
        }
    };

    const clear = async (annotationId: number, e: React.MouseEvent) => {
        e.stopPropagation();
        setBusy(true);
        try {
            await mutate(`/offer-annotations/${annotationId}`, token, 'DELETE');
            onChanged?.();
        } catch (err) {
            setToast({ msg: err instanceof Error ? err.message : 'API', tone: 'err' });
        } finally { setBusy(false); }
    };

    // Korak nazad u statusu — makni anotaciju trenutnog statusa (bez statusa nema anotaciju).
    const stepBack = async () => {
        const a = annotations.find((x) => x.annotation_type === currentStatus);
        if (!a) return;
        setBusy(true);
        try { await mutate(`/offer-annotations/${a.id}`, token, 'DELETE'); onChanged?.(); }
        catch (e) { setToast({ msg: e instanceof Error ? e.message : 'API', tone: 'err' }); }
        finally { setBusy(false); }
    };

    // Check 48h alarm na imam_ju (UI hint)
    const imamJu = annotations.find((a) => a.annotation_type === 'imam_ju');
    const imamJuAge = imamJu?.set_at ? (Date.now() - new Date(imamJu.set_at).getTime()) / (1000 * 60 * 60) : 0;
    const imamJuOverdue = imamJu && imamJuAge > 48;

    return (
        <div className="bi-offer-annotations" style={{ display: 'flex', flexWrap: 'wrap', gap: 6, padding: '4px 8px 6px', borderTop: '1px dashed rgba(184,156,91,0.18)', fontSize: '0.74rem', alignItems: 'center' }} onClick={(e) => {
            // Stopiraj propagaciju SAMO ako je klik na interaktivnu kontrolu (button/input/select).
            // Klik na prazan prostor traka dopušta da row-onClick otvori drawer.
            const tgt = e.target as HTMLElement;
            if (tgt.closest('button, input, select, textarea, a, [role="button"]')) e.stopPropagation();
        }}>
            {/* Status: bez statusa → U obradi → Pakirano → Poslano (+kako dolazi) | Poslano klijentu → Smješteno.
                Readonly (npr. m.40.1) → samo kompaktni bedž ako je status napredovao; editable (m.40.3) → puni stepper + akcije. */}
            {readonly ? (
                statusIdx > 0 ? (
                    <span title={statusTitle} style={{ display: 'inline-flex', alignItems: 'center', gap: 3, padding: '2px 8px', borderRadius: 10, fontWeight: 700, fontSize: '0.72rem', cursor: 'help', background: `${ORDER_STATUS[currentStatus].color}1a`, color: ORDER_STATUS[currentStatus].color }}>
                        {STATUS_STEPS[statusIdx].icon} {STATUS_STEPS[statusIdx].label}{showCarrier ? ` · ${carrier}` : ''}
                    </span>
                ) : null
            ) : (
            <div style={{ flexBasis: '100%', display: 'flex', flexWrap: 'wrap', gap: 4, alignItems: 'center', marginBottom: 2 }}>
                {/* Samo AKTIVNI status (ne posivjeli koraci). */}
                {(() => {
                    const cur = STATUS_STEPS[statusIdx];
                    const bg = ORDER_STATUS[currentStatus].color;
                    return (
                        <span title={statusTitle} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '3px 10px', borderRadius: 10, fontWeight: 800, fontSize: '0.76rem', background: bg, color: '#fff', cursor: 'help' }}>
                            {cur.icon} {cur.label}{showCarrier ? ` · ${carrier}` : ''}
                        </span>
                    );
                })()}
                {!readonly ? (
                    <span style={{ display: 'inline-flex', gap: 4, alignItems: 'center', marginLeft: 8, flexWrap: 'wrap' }}>
                        {isManager && currentStatus === 'none' ? (
                            // U obradi = naručeno kod dobavljača; inače ga postavlja potvrda plana (GoldCmDeliveryPlanController).
                            <button type="button" disabled={busy} onClick={() => void add('u_dolasku')}
                                title={orderStatusTooltip('u_dolasku', locale)}
                                style={{ fontSize: '0.7rem', border: '1px solid #8a6817', color: '#8a6817', background: '#fff', borderRadius: 5, padding: '1px 7px', cursor: 'pointer' }}>🛠 {orderStatusLabel('u_dolasku', locale)}</button>
                        ) : null}
                        {isManager && statusIdx < stepIdx('na_putu') ? (
                            <>
                                <span style={{ fontSize: '0.7rem', color: '#666' }} title={orderStatusTooltip('na_putu', locale)}>{t('Poslano po:', 'Sent via:')}</span>
                                {(['gls', 'vozac', 'hp', 'pohranjeno'] as string[]).map((v) => (
                                    <button key={v} type="button" disabled={busy} onClick={() => void add('na_putu', v)}
                                        title={orderStatusTooltip('na_putu', locale)}
                                        style={{ fontSize: '0.7rem', border: '1px solid '+(v === 'pohranjeno' ? 'var(--ad-ink)' : '#1e40af'), color: v === 'pohranjeno' ? 'var(--ad-ink)' : '#1e40af', background: '#fff', borderRadius: 5, padding: '1px 7px', cursor: 'pointer' }}>{v === 'pohranjeno' ? '🏦' : '📦'} {carrierLabel(v, locale)}</button>
                                ))}
                            </>
                        ) : null}
                        {isManager && statusIdx < stepIdx('poslano') ? (
                            // Poslano klijentu = s centralnog izravno KLIJENTU (ne u poslovnicu); postavlja logistika.
                            // Prijevoznik obavezan: HP (Pošta) ili GLS (Vedran, mail UID 782, 30.09.2026).
                            <>
                                <span style={{ fontSize: '0.7rem', color: '#666' }} title={orderStatusTooltip('poslano', locale)}>{t('Poslano klijentu po:', 'Sent to customer via:')}</span>
                                {(['hp', 'gls'] as string[]).map((v) => (
                                    <button key={v} type="button" disabled={busy} onClick={() => void add('poslano', v)}
                                        title={orderStatusTooltip('poslano', locale)}
                                        style={{ fontSize: '0.72rem', border: '1px solid #6d28d9', color: '#6d28d9', background: '#fff', borderRadius: 5, padding: '2px 9px', cursor: 'pointer', fontWeight: 700 }}>📤 {carrierLabel(v, locale)}</button>
                                ))}
                            </>
                        ) : null}
                        {statusIdx < stepIdx('poslano') ? (
                            <button type="button" disabled={busy} onClick={() => void add('imam_ju')}
                                title={orderStatusTooltip('imam_ju', locale)}
                                style={{ fontSize: '0.72rem', border: '1px solid #1f7a36', color: '#fff', background: '#1f7a36', borderRadius: 5, padding: '2px 9px', cursor: 'pointer', fontWeight: 700 }}>✋ {imamJuText}</button>
                        ) : null}
                        {statusIdx > 0 && (
                            // Poslovnica ne smije undo na_putu/poslano/nacin/mjesto isporuke (logistika set-ovi).
                            // imam_ju i u_dolasku mogu, jer ih sami postavljaju.
                            (currentStatus === 'imam_ju' || currentStatus === 'u_dolasku') || isManager
                        ) ? (
                            <button type="button" disabled={busy} onClick={() => void stepBack()} title={t('Korak nazad', 'Step back')}
                                style={{ fontSize: '0.74rem', border: '1px solid #ccc', color: '#888', background: '#fff', borderRadius: 5, padding: '1px 7px', cursor: 'pointer' }}>↩</button>
                        ) : null}
                    </span>
                ) : null}
                {deliveryOpts?.suggested_delivery_date ? (
                    <span style={{ fontSize: '0.68rem', color: '#8a6817', marginLeft: 'auto' }} title={t('Najraniji dopušteni datum isporuke: min 45 kalendarskih dana od narudžbe (osim EXPRESS)', 'Earliest allowed delivery: min 45 calendar days from order (except EXPRESS)')}>
                        📦 {t('najraniji isporuka', 'earliest delivery')}: {(() => { const [yy, mm, dd] = String(deliveryOpts.suggested_delivery_date).split('-'); return `${dd}.${mm}.${yy}`; })()}{deliveryOpts.min_calendar_days ? ` (min ${deliveryOpts.min_calendar_days} dana)` : ''}
                    </span>
                ) : null}
            </div>
            )}
            {annotations.map((a) => {
                // Status-tipovi prikazani su u status-stepperu, ne kao chip.
                if (a.annotation_type in ORDER_STATUS) return null;
                const m = meta[a.annotation_type];
                if (!m) return null;
                const editable = !readonly && a.annotation_type !== 'alarm' && a.annotation_type !== 'imam_ju';
                return (
                    <span key={a.id} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, background: m.bg, color: m.color, padding: '2px 8px', borderRadius: 10, fontWeight: 700 }}>
                        {m.icon} {m.label}
                        {(a.annotation_type === 'dogovoren_datum' || a.annotation_type === 'express') && editable ? (
                            <DateInlineEditor
                                isoValue={a.agreed_delivery_date ?? null}
                                onSave={(iso) => void updateAnnotation(a.annotation_type, { agreed_delivery_date: iso }, { skipReload: true })}
                                placeholder="dd.mm.gggg"
                                title={a.annotation_type === 'express'
                                    ? t('EXPRESS rok isporuke (auto +15 r.d.)', 'EXPRESS delivery deadline (auto +15 wd)')
                                    : t('Datum isporuke (dd.mm.gggg)', 'Delivery date (dd.mm.yyyy)')}
                            />
                        ) : a.agreed_delivery_date ? (
                            <span style={{ marginLeft: 4, fontWeight: 500 }}>· {(() => { const [y,mo,d] = String(a.agreed_delivery_date).split('-'); return `${d}.${mo}.${y}`; })()}</span>
                        ) : null}
                        {(a.annotation_type === 'hitno' || a.annotation_type === 'napomena' || a.annotation_type === 'na_cekanju_klijent') && editable ? (
                            <NoteInlineEditor
                                value={a.note ?? ''}
                                onSave={(v) => void updateAnnotation(a.annotation_type, { note: v }, { skipReload: true })}
                                placeholder={a.annotation_type === 'na_cekanju_klijent' ? t('razlog čekanja…', 'reason for hold…') : t('napomena…', 'note…')}
                                title={a.annotation_type === 'napomena' ? t('Napomena (poslovnica/logistika)', 'Note (branch/logistics)') : a.annotation_type === 'na_cekanju_klijent' ? t('Razlog čekanja na klijenta', 'Reason order is on hold') : t('Razlog hitnosti', 'Urgency note')}
                            />
                        ) : a.note ? (
                            <span title={a.note} style={{ marginLeft: 4, fontWeight: 500 }}>· 📝</span>
                        ) : null}
                        {a.annotation_type === 'nacin_isporuke' ? (
                            editable ? (
                                <select value={a.value ?? 'u_poslovnici'} onChange={(e) => void updateAnnotation('nacin_isporuke', { value: e.target.value })}
                                    style={{ marginLeft: 4, fontSize: '0.72rem', border: 0, background: 'transparent', color: m.color, fontWeight: 700, cursor: 'pointer' }}>
                                    {(deliveryOpts?.methods ?? [{ value: 'u_poslovnici', label: 'U poslovnici' }, { value: 'vozac', label: 'Vozač' }, { value: 'gls_posta', label: 'GLS pošta' }, { value: 'centralno', label: 'Centralno' }]).map((o) => (
                                        <option key={o.value} value={o.value}>{o.label}</option>
                                    ))}
                                </select>
                            ) : <span style={{ marginLeft: 4, fontWeight: 500 }}>· {NACIN_LABELS[a.value ?? ''] ?? a.value ?? '—'}</span>
                        ) : null}
                        {a.annotation_type === 'mjesto_isporuke' ? (
                            editable ? (
                                <select value={a.value ?? ''} onChange={(e) => void updateAnnotation('mjesto_isporuke', { value: e.target.value })}
                                    style={{ marginLeft: 4, fontSize: '0.72rem', border: 0, background: 'transparent', color: m.color, fontWeight: 700, cursor: 'pointer', maxWidth: 160 }}>
                                    {(deliveryOpts?.places ?? []).map((p) => <option key={p} value={p}>{p}</option>)}
                                </select>
                            ) : <span style={{ marginLeft: 4, fontWeight: 500 }}>· {a.value ?? '—'}</span>
                        ) : null}
                        {a.annotation_type === 'dogovoren_datum' && a.manager_status ? (
                            <span style={{ marginLeft: 4, fontWeight: 700, color: a.manager_status === 'confirmed' ? '#1f7a36' : '#b14040' }}>
                                {a.manager_status === 'confirmed' ? t('· ✓ potvrđeno', '· ✓ confirmed') : t('· ⏳ odgođeno', '· ⏳ postponed')}
                            </span>
                        ) : null}
                        {a.annotation_type === 'dogovoren_datum' && isManager && !readonly ? (
                            <ManagerDateDecision annotationId={a.id} token={token} locale={locale} onDone={onChanged} />
                        ) : null}
                        {!readonly && a.annotation_type !== 'alarm' && (
                            // Logistika oznake (postavlja logistika): na_putu + poslano + nacin_isporuke + mjesto_isporuke.
                            // Samo manager+ smije ih poništiti, poslovnica ne (Ružica feedback 2026-06-02).
                            !['nacin_isporuke', 'mjesto_isporuke', 'na_putu', 'poslano'].includes(a.annotation_type) || isManager
                        ) ? (
                            <button type="button" onClick={(e) => clear(a.id, e)} disabled={busy} style={{ background: 'transparent', border: 0, cursor: 'pointer', color: m.color, fontSize: '0.84rem', marginLeft: 2, padding: 0 }} title={t('Makni oznaku', 'Remove')}>✕</button>
                        ) : null}
                    </span>
                );
            })}
            {imamJuOverdue ? (
                <span style={{ background: '#b14040', color: '#fff', padding: '3px 10px', borderRadius: 10, fontWeight: 800, fontSize: '0.78rem', animation: 'bi-pulse 2s infinite' }}>
                    ❗ {t('Ova narudžba treba se isporučiti u roku 24h ili roba prodati!', 'Order must ship in 24h or stock be sold!')}
                </span>
            ) : null}
            {!readonly ? (
                <div ref={dropdownRef} style={{ position: 'relative', marginLeft: 'auto' }}>
                    <button type="button" onClick={(e) => { e.stopPropagation(); setOpen((s) => !s); }} disabled={busy}
                        style={{ background: 'transparent', border: '1px solid rgba(184,156,91,0.4)', color: '#6b4f1c', padding: '3px 10px', borderRadius: 6, cursor: 'pointer', fontSize: '0.74rem', fontWeight: 600 }}>
                        + {t('Oznaka', 'Flag')} ▾
                    </button>
                    {open ? (
                        <div style={{ position: 'absolute', top: '100%', right: 0, marginTop: 4, background: '#fff', border: '1px solid rgba(184,156,91,0.4)', borderRadius: 8, padding: 6, boxShadow: '0 4px 12px rgba(107,79,28,0.18)', zIndex: 20, minWidth: 170 }}>
                            {flagTypes.map((type) => {
                                const m = meta[type];
                                const already = presentTypes.has(type);
                                return (
                                    <button key={type} type="button" onClick={() => void add(type)} disabled={already || busy}
                                        style={{ display: 'flex', width: '100%', alignItems: 'center', gap: 6, padding: '5px 10px', background: 'transparent', border: 0, cursor: already ? 'default' : 'pointer', opacity: already ? 0.4 : 1, textAlign: 'left', fontSize: '0.82rem' }}>
                                        <span style={{ color: m.color }}>{m.icon}</span>
                                        {m.label} {already ? '✓' : ''}
                                    </button>
                                );
                            })}
                        </div>
                    ) : null}
                </div>
            ) : null}
            {toast ? (
                <div style={{
                    flexBasis: '100%',
                    marginTop: 4,
                    padding: '4px 8px',
                    background: toast.tone === 'ok' ? 'rgba(31,122,54,0.10)' : toast.tone === 'warn' ? 'rgba(184,156,91,0.18)' : 'rgba(177,64,64,0.12)',
                    border: '1px solid ' + (toast.tone === 'ok' ? 'rgba(31,122,54,0.4)' : toast.tone === 'warn' ? 'rgba(184,156,91,0.5)' : 'rgba(177,64,64,0.5)'),
                    color: toast.tone === 'ok' ? '#1f7a36' : toast.tone === 'warn' ? '#6b4f1c' : '#b14040',
                    borderRadius: 6,
                    fontSize: '0.72rem',
                    fontWeight: 600,
                }}>
                    {toast.msg}
                    <button type="button" onClick={() => setToast(null)} style={{ float: 'right', background: 'transparent', border: 0, cursor: 'pointer', color: 'inherit', padding: 0, marginLeft: 8 }}>✕</button>
                </div>
            ) : null}
        </div>
    );
}

type CrmFollowupRow = {
    id: number; status: string; assigned_to_user_id: number | null; due_date: string | null; notes: string | null;
    last_contact_at: string | null; contact_attempts: number;
    offer_id: number; offer_number: string; partner_code: string; partner_name: string; partner_name_is_person?: boolean;
    offer_date: string; offer_amount_eur: number;
    assigned_to_name: string | null; assigned_to_email: string | null;
};
type CrmNoContactRow = {
    offer_id: number; offer_number: string; partner_code: string; partner_name: string; partner_name_is_person?: boolean;
    offer_date: string; offer_amount_eur: number;
};
type CrmConvUser = {
    assigned_to_user_id: number; assigned_to_name: string | null; total: number; won: number; lost: number; open_count: number;
};
type CrmPayload = {
    my_todays: CrmFollowupRow[]; overdue: CrmFollowupRow[]; no_contact: CrmNoContactRow[]; conversion_per_user: CrmConvUser[];
    meta: { stale_days: number; user_id: number; today: string };
};

type ArBucket = { key: string; label: string; min: number; max: number; count: number; amount: number };
type ArDebtor = { customer_code: string; customer_name: string; outstanding: number; invoices_count: number; oldest_due: string | null };
type ArInvoice = {
    invoice_number: string; document_date: string; due_date: string | null;
    customer_code: string; customer_name: string;
    amount_eur: number; paid_eur: number; outstanding: number; days_overdue: number; status_paid: string | null;
};
type ArPayload = {
    buckets: ArBucket[]; top_debtors: ArDebtor[]; detailed: ArInvoice[];
    meta: { total_outstanding: number; total_invoices: number; as_of: string; show_all: boolean; mode: string };
};

function ArAgingPanel({ token, locale }: { token: string; locale: 'hr' | 'en' }) {
    const t = (hr: string, en: string) => (locale === 'en' ? en : hr);
    const [showAll, setShowAll] = useState(false);
    const [data, setData] = useState<ArPayload | null>(null);
    const [error, setError] = useState('');

    useEffect(() => {
        const qs = showAll ? '?show_all=1' : '';
        api<ArPayload>('/ado/ar-aging'+qs, token).then(setData).catch((e) => setError(e instanceof Error ? e.message : 'API'));
    }, [token, showAll]);

    if (error) return <p className="error">{error}</p>;
    if (!data) return <p className="muted">{t('Učitavam…', 'Loading…')}</p>;

    const bucketColors = ['#1f7a36', '#5ca36f', '#c99b31', '#d97757', '#b14040'];

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {/* Header summary */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
                <div>
                    <h3 style={{ margin: 0, fontSize: '1rem' }}>💰 {showAll ? t('AR Aging — svi kupci', 'AR Aging — all customers') : t('AR Aging — B2B foreign (SI/BG/EAM)', 'AR Aging — B2B foreign (SI/BG/EAM)')}</h3>
                    <p className="muted" style={{ margin: '4px 0 0', fontSize: '0.78rem' }}>
                        {t(`Na dan ${data.meta.as_of} · ${data.meta.total_invoices} računa · ukupno ${formatMoney(data.meta.total_outstanding, locale)}`, `As of ${data.meta.as_of} · ${data.meta.total_invoices} invoices · total ${formatMoney(data.meta.total_outstanding, locale)}`)}
                    </p>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <ExportXlsxBtn token={token} type="ar_aging" label={t('Export xlsx', 'Export xlsx')} locale={locale} />
                    <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '4px 10px', background: showAll ? 'rgba(184,156,91,0.18)' : 'rgba(31,122,54,0.10)', border: '1px solid '+(showAll ? '#c99b31' : 'rgba(31,122,54,0.3)'), borderRadius: 4, cursor: 'pointer', fontSize: '0.82rem' }}>
                        <input type="checkbox" checked={showAll} onChange={(e) => setShowAll(e.target.checked)} />
                        {showAll ? t('Svi kupci (uklj. građani)', 'All customers (incl. retail)') : t('Samo B2B foreign (default)', 'B2B foreign only (default)')}
                    </label>
                </div>
            </div>

            {!showAll ? (
                <div style={{ padding: '8px 12px', background: 'rgba(184,156,91,0.06)', borderLeft: '3px solid #c99b31', fontSize: '0.78rem', color: '#6b4f1c', borderRadius: 4 }}>
                    💡 {t('Default mode: samo kreditni B2B partneri (Slovenija, Bugarska, EAM, Goldman Graff, GOLD TRUST). Građani plaćaju unaprijed pa AR za njih ne vrijedi.', 'Default mode: credit B2B partners only (Slovenia, Bulgaria, EAM, Goldman Graff, GOLD TRUST). Retail customers prepay so AR does not apply.')}
                </div>
            ) : null}

            {/* Bucket strip */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 6 }}>
                {data.buckets.map((b, i) => (
                    <div key={b.key} style={{ padding: '10px 14px', background: '#fff', borderLeft: '4px solid '+bucketColors[i], borderRadius: 4, border: '1px solid rgba(184,156,91,0.25)' }}>
                        <div style={{ fontSize: '0.7rem', color: '#666', textTransform: 'uppercase', fontWeight: 600 }}>{b.label}</div>
                        <div style={{ fontSize: '1.1rem', fontWeight: 700, color: bucketColors[i], fontVariantNumeric: 'tabular-nums' }}>{formatMoney(b.amount, locale)}</div>
                        <div style={{ fontSize: '0.74rem', color: '#999' }}>{b.count} {t('računa', 'invoices')}</div>
                    </div>
                ))}
            </div>

            {/* Top debtors */}
            <section>
                <h4 style={{ margin: '0 0 8px', fontSize: '0.9rem', color: '#6b4f1c' }}>🥇 {t('Top dužnici', 'Top debtors')}</h4>
                <table className="admin-user-table" style={{ fontSize: '0.84rem' }}>
                    <thead><tr><th>{t('Kupac', 'Customer')}</th><th style={{ textAlign: 'right' }}>{t('Dugovanje', 'Outstanding')}</th><th>{t('Računa', 'Invoices')}</th><th>{t('Najstarije due', 'Oldest due')}</th></tr></thead>
                    <tbody>
                        {data.top_debtors.map((d) => (
                            <tr key={d.customer_code}>
                                <td>
                                    <strong><PersonName value={d.customer_name} /></strong>
                                    <br /><span className="muted" style={{ fontSize: '0.74rem' }}>{d.customer_code}</span>
                                </td>
                                <td style={{ textAlign: 'right', fontWeight: 700, color: '#b14040' }}>{formatMoney(d.outstanding, locale)}</td>
                                <td>{d.invoices_count}</td>
                                <td>{d.oldest_due ?? '—'}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </section>

            {/* Detailed top 200 invoices */}
            <section>
                <h4 style={{ margin: '0 0 8px', fontSize: '0.9rem', color: '#6b4f1c' }}>📄 {t('Top 200 neplaćenih računa', 'Top 200 outstanding invoices')}</h4>
                <table className="admin-user-table" style={{ fontSize: '0.78rem' }}>
                    <thead><tr><th>{t('Račun', 'Invoice')}</th><th>{t('Datum', 'Date')}</th><th>{t('Due', 'Due')}</th><th>{t('Kupac', 'Customer')}</th><th style={{ textAlign: 'right' }}>{t('Iznos', 'Amount')}</th><th style={{ textAlign: 'right' }}>{t('Plaćeno', 'Paid')}</th><th style={{ textAlign: 'right' }}>{t('Dugovanje', 'Outstanding')}</th><th>{t('Kašnjenje', 'Overdue')}</th></tr></thead>
                    <tbody>
                        {data.detailed.map((r) => {
                            const overdueColor = r.days_overdue > 90 ? '#b14040' : r.days_overdue > 60 ? '#d97757' : r.days_overdue > 30 ? '#c99b31' : r.days_overdue > 0 ? '#8a6817' : '#1f7a36';
                            return (
                                <tr key={r.invoice_number}>
                                    <td><code>{r.invoice_number}</code></td>
                                    <td>{r.document_date}</td>
                                    <td>{r.due_date ?? '—'}</td>
                                    <td><PersonName value={r.customer_name} /> <span className="muted" style={{ fontSize: '0.72rem' }}>({r.customer_code})</span></td>
                                    <td style={{ textAlign: 'right' }}>{formatMoney(r.amount_eur, locale)}</td>
                                    <td style={{ textAlign: 'right' }}>{formatMoney(r.paid_eur, locale)}</td>
                                    <td style={{ textAlign: 'right', fontWeight: 700, color: '#b14040' }}>{formatMoney(r.outstanding, locale)}</td>
                                    <td style={{ color: overdueColor, fontWeight: 600 }}>{r.days_overdue > 0 ? r.days_overdue+'d' : 'on time'}</td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </section>
        </div>
    );
}

function CrmPanel({ token, locale }: { token: string; locale: 'hr' | 'en' }) {
    const t = (hr: string, en: string) => (locale === 'en' ? en : hr);
    const [data, setData] = useState<CrmPayload | null>(null);
    const [error, setError] = useState('');
    const [selectedOfferIds, setSelectedOfferIds] = useState<Set<number>>(new Set());
    const [bulkStatus, setBulkStatus] = useState<string>('contacted');
    const [bulkNotes, setBulkNotes] = useState<string>('');
    const [bulkBusy, setBulkBusy] = useState(false);
    const [reloadKey, setReloadKey] = useState(0);

    useEffect(() => {
        api<CrmPayload>('/ado/crm-dashboard', token).then(setData).catch((e) => setError(e instanceof Error ? e.message : 'API'));
    }, [token, reloadKey]);

    if (error) return <p className="error">{error}</p>;
    if (!data) return <p className="muted">{t('Učitavam…', 'Loading…')}</p>;

    const toggleSelect = (id: number) => {
        setSelectedOfferIds((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id); else next.add(id);
            return next;
        });
    };
    const selectAllNoContact = () => {
        if (!data) return;
        setSelectedOfferIds(new Set(data.no_contact.map((r) => r.offer_id)));
    };
    const clearSelection = () => setSelectedOfferIds(new Set());

    async function doBulk() {
        if (selectedOfferIds.size === 0) return;
        setBulkBusy(true);
        try {
            await mutate('/ado/followups/bulk', token, 'POST', {
                offer_ids: Array.from(selectedOfferIds),
                status: bulkStatus,
                notes: bulkNotes || undefined,
            });
            clearSelection();
            setBulkNotes('');
            setReloadKey((k) => k + 1);
        } catch (e) {
            alert(e instanceof Error ? e.message : 'Bulk fail');
        } finally {
            setBulkBusy(false);
        }
    }

    const fmtAge = (d: string) => {
        const days = Math.floor((Date.now() - new Date(d).getTime()) / 86400000);
        return `${days}d`;
    };

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {/* Moje obveze danas */}
            <CrmSection
                title={`📅 ${t('Moje obveze danas', 'My tasks today')} (${data.my_todays.length})`}
                empty={t('Nemaš obveza za danas.', 'No tasks for today.')}
                accent="#1f7a36"
            >
                {data.my_todays.map((r) => (
                    <CrmFollowupCard key={r.id} r={r} locale={locale} />
                ))}
            </CrmSection>

            {/* Overdue */}
            <CrmSection
                title={`⏰ ${t('Zakašnjelo', 'Overdue')} (${data.overdue.length})`}
                empty={t('Nema zakašnjelih follow-up-ova.', 'No overdue follow-ups.')}
                accent="#b14040"
            >
                {data.overdue.map((r) => (
                    <CrmFollowupCard key={r.id} r={r} locale={locale} showDue />
                ))}
            </CrmSection>

            {/* No contact + bulk-edit */}
            <CrmSection
                title={`🔇 ${t(`Otvorene ponude bez followup-a (>${data.meta.stale_days}d)`, `Open offers without follow-up (>${data.meta.stale_days}d)`)} (${data.no_contact.length})`}
                empty={t('Sve otvorene ponude su pokrivene.', 'All open offers are covered.')}
                accent="#8a6817"
                action={<ExportXlsxBtn token={token} type="crm_no_contact" label={t('Export xlsx', 'Export xlsx')} locale={locale} />}
            >
                {data.no_contact.length > 0 ? (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, padding: '8px 10px', background: 'rgba(184,156,91,0.08)', borderRadius: 4, marginBottom: 6, alignItems: 'center', fontSize: '0.78rem' }}>
                        <strong>{selectedOfferIds.size}/{data.no_contact.length} {t('odabrano', 'selected')}</strong>
                        <button type="button" onClick={selectAllNoContact} style={{ padding: '3px 8px', borderRadius: 4, border: '1px solid rgba(184,156,91,0.4)', background: '#fff', cursor: 'pointer' }}>{t('Označi sve', 'Select all')}</button>
                        <button type="button" onClick={clearSelection} disabled={selectedOfferIds.size === 0} style={{ padding: '3px 8px', borderRadius: 4, border: '1px solid rgba(184,156,91,0.4)', background: '#fff', cursor: selectedOfferIds.size === 0 ? 'default' : 'pointer', opacity: selectedOfferIds.size === 0 ? 0.5 : 1 }}>{t('Očisti', 'Clear')}</button>
                        <span style={{ width: 1, height: 18, background: 'rgba(184,156,91,0.4)' }} />
                        <label>
                            {t('Status:', 'Status:')}{' '}
                            <select value={bulkStatus} onChange={(e) => setBulkStatus(e.target.value)} style={{ padding: '2px 6px' }}>
                                <option value="open">open</option>
                                <option value="contacted">contacted</option>
                                <option value="waiting">waiting</option>
                                <option value="won">won</option>
                                <option value="lost">lost</option>
                                <option value="archived">archived</option>
                            </select>
                        </label>
                        <input
                            type="text"
                            placeholder={t('Bilješka (opcija)', 'Note (optional)')}
                            value={bulkNotes}
                            onChange={(e) => setBulkNotes(e.target.value)}
                            style={{ padding: '4px 8px', border: '1px solid rgba(184,156,91,0.4)', borderRadius: 4, flex: '1 1 200px' }}
                        />
                        <button type="button" onClick={() => void doBulk()} disabled={selectedOfferIds.size === 0 || bulkBusy}
                            style={{ padding: '4px 12px', borderRadius: 4, border: 0, background: selectedOfferIds.size === 0 ? '#ccc' : 'linear-gradient(135deg, #1f7a36, #2d8f44)', color: '#fff', fontWeight: 700, cursor: selectedOfferIds.size === 0 ? 'default' : 'pointer' }}>
                            {bulkBusy ? '…' : `${t('Apply na', 'Apply to')} ${selectedOfferIds.size}`}
                        </button>
                    </div>
                ) : null}
                {data.no_contact.map((r) => {
                    const isSel = selectedOfferIds.has(r.offer_id);
                    return (
                        <div key={r.offer_id} onClick={() => toggleSelect(r.offer_id)} style={{ padding: '8px 12px', background: isSel ? 'rgba(31,122,54,0.08)' : '#fff', border: '1px solid '+(isSel ? '#1f7a36' : 'rgba(184,156,91,0.3)'), borderRadius: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 12, cursor: 'pointer' }}>
                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                                <input type="checkbox" checked={isSel} onChange={() => toggleSelect(r.offer_id)} onClick={(e) => e.stopPropagation()} />
                                <code style={{ marginRight: 4 }}>{r.offer_number}</code>
                                <strong><PersonName value={r.partner_name} isPerson={r.partner_name_is_person} /></strong>
                                <span className="muted" style={{ fontSize: '0.78rem' }}>({r.partner_code})</span>
                            </span>
                            <span style={{ fontSize: '0.82rem', display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                                <strong>{formatMoney(r.offer_amount_eur, locale)}</strong>
                                <span className="muted">· {fmtAge(r.offer_date)}</span>
                                <button
                                    type="button"
                                    onClick={(e) => { e.stopPropagation(); openMailDraft({ partnerName: r.partner_name, partnerCode: r.partner_code, offerNumber: r.offer_number, offerDate: r.offer_date, amountEur: r.offer_amount_eur, locale }); }}
                                    title={t('Pripremi email tekst (clipboard)', 'Prepare email text (clipboard)')}
                                    style={{ padding: '2px 6px', border: '1px solid rgba(184,156,91,0.4)', background: '#fff', borderRadius: 3, cursor: 'pointer', fontSize: '0.78rem' }}>📧</button>
                            </span>
                        </div>
                    );
                })}
            </CrmSection>

            {/* Konverzija po useru */}
            <CrmSection
                title={`📊 ${t('Konverzija po useru (zadnjih 90d)', 'Conversion per user (last 90d)')} (${data.conversion_per_user.length})`}
                empty={t('Nema podataka o follow-up aktivnosti.', 'No follow-up activity recorded.')}
                accent="#6b4f1c"
            >
                <table className="admin-user-table" style={{ fontSize: '0.84rem' }}>
                    <thead><tr><th>{t('Korisnik', 'User')}</th><th>{t('Ukupno', 'Total')}</th><th>{t('Won', 'Won')}</th><th>{t('Lost', 'Lost')}</th><th>{t('Aktivno', 'Open')}</th><th>{t('% Win', '% Win')}</th></tr></thead>
                    <tbody>
                        {data.conversion_per_user.map((u) => {
                            const winRate = u.total > 0 ? (100 * u.won / u.total).toFixed(1) : '0';
                            return (
                                <tr key={u.assigned_to_user_id}>
                                    <td><strong>{u.assigned_to_name ?? `#${u.assigned_to_user_id}`}</strong></td>
                                    <td>{u.total}</td>
                                    <td style={{ color: '#1f7a36' }}>{u.won}</td>
                                    <td style={{ color: '#b14040' }}>{u.lost}</td>
                                    <td style={{ color: '#8a6817' }}>{u.open_count}</td>
                                    <td>{winRate}%</td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </CrmSection>
        </div>
    );
}

function CrmSection({ title, empty, accent, action, children }: { title: string; empty: string; accent: string; action?: React.ReactNode; children: React.ReactNode }) {
    const isEmpty = !React.Children.count(children);
    return (
        <section>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                <h3 style={{ margin: 0, fontSize: '0.95rem', color: accent, borderLeft: '4px solid '+accent, paddingLeft: 8 }}>{title}</h3>
                {action}
            </div>
            {isEmpty ? <p className="muted" style={{ padding: 12, fontStyle: 'italic' }}>{empty}</p> : <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>{children}</div>}
        </section>
    );
}

function CrmFollowupCard({ r, locale, showDue }: { r: CrmFollowupRow; locale: 'hr' | 'en'; showDue?: boolean }) {
    const t = (hr: string, en: string) => (locale === 'en' ? en : hr);
    return (
        <div style={{ padding: '8px 12px', background: '#fff', border: '1px solid rgba(184,156,91,0.3)', borderRadius: 4, display: 'grid', gridTemplateColumns: '1fr auto auto', gap: 12, alignItems: 'baseline' }}>
            <span>
                <code style={{ marginRight: 8 }}>{r.offer_number}</code>
                <strong><PersonName value={r.partner_name} isPerson={r.partner_name_is_person} /></strong>
                <span className="muted" style={{ marginLeft: 6, fontSize: '0.76rem' }}>({r.partner_code}) · {r.assigned_to_name ?? '—'}</span>
                {r.notes ? <div className="muted" style={{ fontSize: '0.76rem', marginTop: 2 }}>📝 {r.notes}</div> : null}
            </span>
            <span style={{ fontSize: '0.78rem' }}>
                <span style={{ padding: '2px 6px', borderRadius: 3, background: r.status === 'open' ? 'rgba(31,122,54,0.10)' : 'rgba(0,0,0,0.05)', color: '#444', fontWeight: 600 }}>{r.status}</span>
                {showDue && r.due_date ? <span style={{ marginLeft: 6, color: '#b14040' }}>· {t('due', 'due')} {r.due_date}</span> : null}
                {r.contact_attempts > 0 ? <span style={{ marginLeft: 6 }} className="muted">· {r.contact_attempts}× kontakt</span> : null}
            </span>
            <span style={{ fontWeight: 700 }}>{formatMoney(r.offer_amount_eur, locale)}</span>
        </div>
    );
}

function ExecPulseStrip({ data, locale, onJump, token }: { data: PulsePayload['pulse']; locale: 'hr' | 'en'; onJump: (t: TabId) => void; token: string }) {
    const t = (hr: string, en: string) => (locale === 'en' ? en : hr);
    const fmtM = (n: number) => formatMoney(n, locale);
    const fmtN = (n: number) => formatNumber(n, locale);
    const [mk, setMk] = useState<{ cac_3m: number; current_ltv_cac_ratio: number; max_cac_at_target: number } | null>(null);

    useEffect(() => {
        api<{ metrics: { cac_3m: number; current_ltv_cac_ratio: number; max_cac_at_target: number } }>('/marketing/budget-planer?djelatnost=investicijsko&target_ratio=3', token)
            .then((r) => setMk(r.metrics))
            .catch(() => undefined);
    }, [token]);

    return (
        <div className="ado-puls-strip">
            <PulsBadge label={t('Ponude', 'Offers')} value={fmtN(data.offers_count)} sub={fmtM(data.offered_total)} onClick={() => onJump('live')} />
            <PulsBadge label={t('Konverzija', 'Conversion')} value={`${data.conversion_by_count_pct.toFixed(0)}%`} sub={t('po broju', 'by count')} onClick={() => onJump('performance')} />
            <PulsBadge label={t('Realizirano', 'Realized')} value={fmtM(data.realized_total)} sub={`${data.realized_count}/${data.offers_count}`} onClick={() => onJump('performance')} accent="pos" />
            <PulsBadge label={t('Otvoreno', 'Open')} value={fmtM(data.open_value)} sub={`${data.open_count} ${t('ponuda', 'offers')}`} onClick={() => onJump('live')} accent="warn" />
            <PulsBadge label={t('Avg lead', 'Avg lead')} value={`${data.avg_lead_days.toFixed(1)}d`} sub={t('izdavanje→naplata', 'issue→cash')} onClick={() => onJump('performance')} />
            <PulsBadge label={t('Otkup', 'Buyback')} value={fmtM(data.buyback_amount)} sub={`${data.buyback_count} ${t('kom', 'pcs')}`} onClick={() => onJump('performance')} />
            {mk ? (
                <>
                    <PulsBadge
                        label="CAC 3M"
                        value={fmtM(mk.cac_3m)}
                        sub={`max ${fmtM(mk.max_cac_at_target)}`}
                        accent={mk.cac_3m > mk.max_cac_at_target ? 'neg' : 'pos'}
                    />
                    <PulsBadge
                        label="LTV:CAC"
                        value={`${mk.current_ltv_cac_ratio}:1`}
                        sub={t('cilj 3:1', 'target 3:1')}
                        accent={mk.current_ltv_cac_ratio >= 3 ? 'pos' : 'neg'}
                    />
                </>
            ) : null}
        </div>
    );
}

function PulsBadge({ label, value, sub, onClick, accent }: { label: string; value: string; sub?: string; onClick?: () => void; accent?: 'pos' | 'warn' | 'neg' }) {
    const color = accent === 'pos' ? '#1f7a36' : accent === 'warn' ? '#8a6817' : accent === 'neg' ? '#b14040' : '#1a1a1a';
    return (
        <button type="button" onClick={onClick} className="ado-puls-badge" title={onClick ? 'Klik za detalje' : ''}>
            <span className="ado-puls-label">{label}</span>
            <span className="ado-puls-value" style={{ color }}>{value}</span>
            {sub ? <span className="ado-puls-sub">{sub}</span> : null}
        </button>
    );
}

function SourceBadge({ source, locale }: { source?: string | null; locale: 'hr' | 'en' }) {
    const t = (hr: string, en: string) => (locale === 'en' ? en : hr);
    const cfg = (() => {
        switch (source) {
            case 'live_erp': return { icon: '📡✓', color: '#1f7a36', bg: 'rgba(31,122,54,0.10)', title: t('JSON live + ERP partner match', 'JSON live + ERP partner match') };
            case 'live_erp_multi': return { icon: '📡⚠', color: '#8a6817', bg: 'rgba(184,156,91,0.15)', title: t('JSON live + više ERP kandidata', 'JSON live + multiple ERP candidates') };
            case 'erp_only': return { icon: '📋', color: '#6b4f1c', bg: 'rgba(184,156,91,0.18)', title: t('Samo ERP — JSON par nije pronađen', 'ERP only — no JSON match') };
            default: return { icon: '📡', color: '#999', bg: 'rgba(0,0,0,0.04)', title: t('JSON live (POS)', 'JSON live (POS)') };
        }
    })();
    return (
        <span title={cfg.title} style={{
            display: 'inline-flex', alignItems: 'center',
            fontSize: '0.66rem', padding: '1px 5px', borderRadius: 3,
            background: cfg.bg, color: cfg.color, fontWeight: 700,
            flex: '0 0 auto',
        }}>{cfg.icon}</span>
    );
}

function ExportXlsxBtn({ token, type, label, locale }: { token: string; type: 'ar_aging' | 'crm_no_contact' | 'customers_atrisk'; label: string; locale: 'hr' | 'en' }) {
    const t = (hr: string, en: string) => (locale === 'en' ? en : hr);
    const [busy, setBusy] = useState(false);
    async function go() {
        setBusy(true);
        try {
            const ts = new Date().toISOString().slice(0, 19).replace('T', '_').replace(/:/g, '');
            await downloadFile(`/ado/export-xlsx?type=${type}`, token, `${type}_${ts}.xlsx`);
        } catch (e) {
            alert(e instanceof Error ? e.message : 'Export fail');
        } finally { setBusy(false); }
    }
    return (
        <button type="button" onClick={() => void go()} disabled={busy}
            style={{ padding: '4px 10px', borderRadius: 4, border: '1px solid rgba(184,156,91,0.4)', background: '#fff', color: '#6b4f1c', cursor: 'pointer', fontSize: '0.78rem', fontWeight: 600 }}>
            {busy ? '⏳…' : '📥 '+label}
        </button>
    );
}

function openMailDraft({ partnerName, partnerCode, offerNumber, offerDate, amountEur, locale }: {
    partnerName: string; partnerCode: string; offerNumber?: string; offerDate?: string; amountEur?: number; locale: 'hr' | 'en';
}): void {
    const isHr = locale === 'hr';
    const subject = offerNumber
        ? (isHr ? `Vaša ponuda ${offerNumber}` : `Your offer ${offerNumber}`)
        : (isHr ? `Kontakt — Auro Domus Online` : `Contact — Auro Domus Online`);
    const body = isHr
        ? `Poštovani ${partnerName.split(' ')[0]},\n\n${offerNumber
            ? `kontaktiramo Vas vezano za ponudu ${offerNumber}${offerDate ? ' od '+offerDate : ''}${amountEur ? ' u iznosu '+amountEur.toLocaleString('hr-HR', { maximumFractionDigits: 2 })+' €' : ''}.\n\nMolimo Vas povratnu informaciju o statusu ponude.`
            : `kontaktiramo Vas vezano za našu suradnju.`}\n\nSrdačan pozdrav,\nAuro Domus Online\nvjakomin@gmail.com`
        : `Dear ${partnerName.split(' ')[0]},\n\n${offerNumber
            ? `we are contacting you regarding offer ${offerNumber}${offerDate ? ' from '+offerDate : ''}${amountEur ? ' amounting to €'+amountEur.toLocaleString('en-US', { maximumFractionDigits: 2 }) : ''}.\n\nPlease let us know the status of this offer.`
            : `we are following up on our cooperation.`}\n\nBest regards,\nAuro Domus Online\nvjakomin@gmail.com`;

    // Copy to clipboard + show modal (mailto: doesn't reliably support long bodies)
    const text = `Predmet: ${subject}\n\n${body}\n\n— Partner šifra: ${partnerCode}`;
    if (navigator.clipboard) {
        navigator.clipboard.writeText(text).then(() => {
            alert(isHr
                ? `Tekst kopiran u clipboard.\n\nPredmet: ${subject}\n\nMožeš zaljepiti u email klijent.`
                : `Text copied to clipboard.\n\nSubject: ${subject}\n\nPaste into email client.`);
        }).catch(() => {
            window.open(`mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`, '_blank');
        });
    } else {
        window.open(`mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`, '_blank');
    }
}

function fmtMinutes(min: number | null | undefined, locale: 'hr' | 'en'): string {
    if (min == null) return '—';
    if (min < 60) return `${min}m`;
    const h = Math.floor(min / 60);
    const m = min % 60;
    if (h < 24) return m === 0 ? `${h}h` : `${h}h ${m}m`;
    const d = Math.floor(h / 24);
    const hRem = h % 24;
    return hRem === 0 ? `${d}${locale === 'en' ? 'd' : 'd'}` : `${d}d ${hRem}h`;
}

function Kpi({ label, value, color, strong, muted }: { label: string; value: React.ReactNode; color?: 'pos' | 'neg'; strong?: boolean; muted?: boolean }) {
    const valColor = muted ? '#999' : (color === 'pos' ? '#1f7a36' : color === 'neg' ? '#b14040' : '#333');
    return (
        <div className="bi-kpi">
            <div className="bi-kpi-label">{label}</div>
            <div className="bi-kpi-value" style={{ color: valColor, fontWeight: strong ? 700 : 500 }}>{value}</div>
        </div>
    );
}

/**
 * RUC vrijednost u KPI-ju: NETO (marža nakon dobavljačke premije) kao glavna brojka,
 * a BRUTO (premija nad spotom) ispod, sitno + sivo. Oba uvijek vidljiva.
 */
function rucDual(neto: number | null | undefined, bruto: number | null | undefined, fmt: (n: number | null | undefined) => string): React.ReactNode {
    if (neto == null) return '—';
    return (
        <>
            {fmt(neto)}
            <span style={{ display: 'block', fontSize: '0.58rem', fontWeight: 400, color: '#999', lineHeight: 1.15 }}>{`bruto ${fmt(bruto)}`}</span>
        </>
    );
}

function LiveOfferDrawer({
    data, locale, onClose, fmtMoney, fmtNum, fmtPct,
}: {
    data: any;
    locale: 'hr' | 'en';
    onClose: () => void;
    fmtMoney: (n: number | null | undefined) => string;
    fmtNum: (n: number | null | undefined, d?: number) => string;
    fmtPct: (n: number | null | undefined) => string;
}) {
    const t = (hr: string, en: string) => (locale === 'en' ? en : hr);
    const o = data?.offer ?? null;

    // Izračun RUC-a iste logike kao u kontroleru (za prikaz čitljiv)
    const iznos = o ? Number(o.iznos_sa_pdv ?? 0) : 0;
    const nabIzd = o?.vrijednost_na_izdavanju != null ? Number(o.vrijednost_na_izdavanju) : null;
    const nabPri = o?.vrijednost_na_prihvacanju != null ? Number(o.vrijednost_na_prihvacanju) : null;
    const rucPlan = nabIzd != null ? iznos - nabIzd : null;
    const rucReal = nabPri != null ? iznos - nabPri : null;
    const rucPlanPct = rucPlan != null && iznos > 0 ? (100 * rucPlan / iznos) : null;
    const rucRealPct = rucReal != null && iznos > 0 ? (100 * rucReal / iznos) : null;
    // Neto = bruto − nabavni premium (dobavljačka premija + zavisni iz cjenika).
    const nabPrem = o?.nabavni_premium_eur != null ? Number(o.nabavni_premium_eur) : 0;
    const rucPlanNeto = rucPlan != null ? rucPlan - nabPrem : null;
    const rucRealNeto = rucReal != null ? rucReal - nabPrem : null;
    const rucPlanNetoPct = rucPlanNeto != null && iznos > 0 ? (100 * rucPlanNeto / iznos) : null;
    const rucRealNetoPct = rucRealNeto != null && iznos > 0 ? (100 * rucRealNeto / iznos) : null;
    const isAccepted = !!o?.datum_prihvacanja;
    const isSilver = o?.vrsta_kovine === 'Srebro';

    return (
        <div role="dialog" aria-modal="true" onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 1000, display: 'flex', justifyContent: 'flex-end' }}>
            <div onClick={(e) => e.stopPropagation()} style={{ width: 'min(640px, 96vw)', background: '#fff', height: '100vh', overflowY: 'auto', padding: 0, boxShadow: '-4px 0 24px rgba(0,0,0,0.18)' }}>
                {/* Header */}
                <div style={{ padding: '14px 20px', background: 'linear-gradient(135deg, #6b4f1c, #c99b31)', color: '#fffbeb', position: 'sticky', top: 0, zIndex: 5 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <h3 style={{ margin: 0, fontSize: '1.05rem' }}>📋 {t('Detalji ponude', 'Offer details')}</h3>
                        <button type="button" onClick={onClose} style={{ background: 'rgba(255,255,255,0.2)', color: '#fff', border: 0, padding: '4px 10px', borderRadius: 4, cursor: 'pointer', fontSize: '0.95rem' }}>✕</button>
                    </div>
                    {o ? (
                        <div style={{ marginTop: 6, display: 'flex', alignItems: 'baseline', gap: 12, flexWrap: 'wrap' }}>
                            <span style={{ fontFamily: 'monospace', fontSize: '1rem', fontWeight: 700 }}>{o.broj_ponude}</span>
                            <span style={{ fontSize: '0.78rem', opacity: 0.9 }}>{(o.datum_ponude ?? '').replace('T', ' ').slice(0, 16)}</span>
                            {isAccepted ? <span style={{ background: 'rgba(31,122,54,0.9)', padding: '2px 8px', borderRadius: 10, fontSize: '0.72rem', fontWeight: 700 }}>✓ {t('Prihvaćena', 'Accepted')}</span> : <span style={{ background: 'rgba(0,0,0,0.25)', padding: '2px 8px', borderRadius: 10, fontSize: '0.72rem' }}>{t('Otvorena', 'Open')}</span>}
                            {o.hedge_status === 'full' ? <span style={{ background: '#1f7a36', padding: '2px 8px', borderRadius: 10, fontSize: '0.72rem' }}>H✓ {t('Hedge full', 'Hedge full')}</span> : null}
                            {o.hedge_status === 'partial' ? <span style={{ background: '#8a6817', padding: '2px 8px', borderRadius: 10, fontSize: '0.72rem' }}>H⚠ {t('Hedge partial', 'Hedge partial')}</span> : null}
                        </div>
                    ) : null}
                </div>

                <div style={{ padding: '14px 20px' }}>
                    {!o ? <p className="muted">{t('Učitavam…', 'Loading…')}</p> : (
                        <>
                            {/* Sekcija: Artikl + poslovnica */}
                            <DrawerSection title={t('Artikl i poslovnica', 'Item and branch')}>
                                <DrawerKV label={t('Artikl', 'Item')} value={
                                    <span><span style={{ color: isSilver ? '#7a7a7a' : '#c99b31', fontWeight: 700 }}>{isSilver ? '🥈 Ag' : '🥇 Au'}</span> {o.naziv_artikla ?? '—'}</span>
                                } />
                                <DrawerKV label={t('Šifra', 'SKU')} value={<code>{o.sifra_artikla ?? '—'}</code>} />
                                <DrawerKV label={t('Količina', 'Quantity')} value={<><strong>{Math.round(Number(o.kolicina))} kom</strong> · {fmtNum(o.fine_ukupno_g, 2)}g fine</>} />
                                <DrawerKV label={t('Poslovnica', 'Branch')} value={<>{o.poslovnica ?? '—'} <span className="muted">(id={o.id_poslovnice ?? '—'})</span></>} />
                            </DrawerSection>

                            {/* Sekcija: cijene */}
                            <DrawerSection title={t('Cijene', 'Prices')}>
                                <DrawerKV label={t('Iznos s PDV', 'Amount w/ VAT')} value={<strong style={{ fontSize: '1rem' }}>{fmtMoney(iznos)}</strong>} />
                                <DrawerKV label={t('Iznos bez PDV', 'Amount no VAT')} value={fmtMoney(o.iznos_bez_pdv)} />
                                <DrawerKV label={t('Cijena/kom (bez/s PDV)', 'Unit price (no/w VAT)')} value={<>{fmtMoney(o.cijena_bez_pdv)} / {fmtMoney(o.cijena_sa_pdv)}</>} />
                                <DrawerKV label={t('PDV %', 'VAT %')} value={`${o.pdv_posto ?? '—'}%`} />
                            </DrawerSection>

                            {/* Sekcija: Spot / nabavna vrijednost */}
                            <DrawerSection title={t('Spot i nabavna', 'Spot & cost')}>
                                <DrawerKV label={t('Spot u trenutku izdavanja', 'Spot at issue')} value={<><strong>{fmtNum(o.spot_na_izdavanju, 2)} €/g</strong> · {t('vrijednost', 'value')}: {fmtMoney(o.vrijednost_na_izdavanju)}</>} />
                                <DrawerKV label={t('Spot u trenutku prihvaćanja', 'Spot at accept')} value={isAccepted ? <><strong>{fmtNum(o.spot_na_prihvacanju, 2)} €/g</strong> · {t('vrijednost', 'value')}: {fmtMoney(o.vrijednost_na_prihvacanju)}</> : '—'} />
                                {isAccepted ? <DrawerKV label={t('Datum prihvaćanja', 'Accept date')} value={(o.datum_prihvacanja ?? '').replace('T', ' ').slice(0, 16)} /> : null}
                            </DrawerSection>

                            {/* Sekcija: RUC izračun — NETO glavna brojka, bruto + premija ispod */}
                            <DrawerSection title={t('RUC izračun (neto)', 'RUC calculation (net)')}>
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8, marginBottom: 6 }}>
                                    <RucBox label={t('RUC ponuda', 'RUC offer')} eur={rucPlanNeto} pct={rucPlanNetoPct} fmtMoney={fmtMoney} fmtPct={fmtPct}
                                        sub={rucPlan != null ? <>{t('bruto', 'gross')} {fmtMoney(rucPlan)}{nabPrem ? ` · −${fmtMoney(nabPrem)}` : ''}</> : undefined} />
                                    <RucBox label={t('RUC potvrda', 'RUC confirm')} eur={rucRealNeto} pct={rucRealNetoPct} fmtMoney={fmtMoney} fmtPct={fmtPct}
                                        sub={rucReal != null ? <>{t('bruto', 'gross')} {fmtMoney(rucReal)}{nabPrem ? ` · −${fmtMoney(nabPrem)}` : ''}</> : undefined} />
                                    <RucBox label={t('RUC fixing', 'RUC fixing')} eur={null} pct={null} fmtMoney={fmtMoney} fmtPct={fmtPct} placeholder={t('čeka supplier', 'pending')} />
                                </div>
                                {nabPrem ? <div style={{ fontSize: '0.66rem', color: '#8a6817', textAlign: 'center' }}>{t('Nabavni premium (dobavljač + zavisni) iz cjenika', 'Supplier premium (+ ancillary) from price list')}: −{fmtMoney(nabPrem)}</div> : null}
                            </DrawerSection>

                            {/* Sekcija: Hedge */}
                            <DrawerSection title={t('Hedge', 'Hedge')}>
                                <DrawerKV label={t('Status', 'Status')} value={o.hedge_status === 'full' ? '✓ Full' : o.hedge_status === 'partial' ? '⚠ Partial' : t('— nije hedgano', '— not hedged')} />
                                {o.hedge_deal_label ? <DrawerKV label={t('Deal', 'Deal')} value={<>{o.hedge_deal_label} · {o.hedge_date} · {fmtNum(o.hedge_price, 2)}/oz ({o.hedge_pair})</>} /> : null}
                                {o.hedge_qty_oz != null ? <DrawerKV label={t('Količina hedge', 'Hedge qty')} value={`${fmtNum(o.hedge_qty_oz, 4)} oz`} /> : null}
                                {data.hedge_links?.length > 0 ? (
                                    <div style={{ marginTop: 6, padding: '8px 10px', background: 'rgba(31,122,54,0.05)', borderRadius: 4 }}>
                                        <div style={{ fontSize: '0.74rem', color: '#6b4f1c', fontWeight: 700, marginBottom: 4 }}>🔗 {t('Hedge linkovi', 'Hedge links')} ({data.hedge_links.length})</div>
                                        {data.hedge_links.map((h: any, i: number) => (
                                            <div key={i} style={{ fontSize: '0.78rem', padding: '2px 0' }}>{h.deal_label} · {h.trade_date} · {fmtNum(h.allocated_oz, 4)} oz @ {fmtNum(h.price, 2)} ({h.currency_pair})</div>
                                        ))}
                                    </div>
                                ) : null}
                            </DrawerSection>

                            {/* Sekcija: ERP partner + račune (objedinjeno iz bi_offer_documents + bi_outgoing_invoices) */}
                            {data.erp_matches && data.erp_matches.length > 0 ? (
                                <DrawerSection title={t('ERP partner / računi', 'ERP partner / invoices')}>
                                    {data.erp_matches.map((e: any, i: number) => (
                                        <div key={i} style={{ padding: '6px 0', borderBottom: i < data.erp_matches.length - 1 ? '1px solid #f1ece0' : 0 }}>
                                            <DrawerKV label={t('Partner', 'Partner')} value={
                                                <span style={{ fontWeight: 700 }}>
                                                    {e.partner_name ?? '—'} <span className="muted" style={{ fontWeight: 400 }}>({e.partner_code})</span>
                                                </span>
                                            } />
                                            <DrawerKV label={t('ERP broj ponude', 'ERP offer #')} value={<code>{e.offer_number}</code>} />
                                            <DrawerKV label={t('Datum', 'Date')} value={e.offer_date} />
                                            <DrawerKV label={t('Iznos', 'Amount')} value={<strong>{fmtMoney(e.amount_total)}</strong>} />
                                            <DrawerKV label={t('Računi', 'Invoices')} value={
                                                e.invoice_count > 0
                                                    ? <span>{e.invoice_count} {t('vezana', 'linked')}</span>
                                                    : <span className="muted">{t('— bez računa', '— no invoices')}</span>
                                            } />
                                        </div>
                                    ))}
                                    {data.erp_invoices && data.erp_invoices.length > 0 ? (
                                        <div style={{ marginTop: 8, padding: '8px 10px', background: 'rgba(184,156,91,0.06)', borderRadius: 4 }}>
                                            <div style={{ fontSize: '0.74rem', color: '#6b4f1c', fontWeight: 700, marginBottom: 4 }}>📄 {t('Računi', 'Invoices')} ({data.erp_invoices.length})</div>
                                            {data.erp_invoices.map((inv: any, i: number) => (
                                                <div key={i} style={{ fontSize: '0.78rem', padding: '2px 0', display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                                                    <span><code>{inv.invoice_number}</code> · {inv.document_date}</span>
                                                    <span>
                                                        <strong>{fmtMoney(inv.amount_eur)}</strong>
                                                        {inv.status_paid ? <span style={{ marginLeft: 6, color: inv.status_paid === 'paid' ? '#1f7a36' : inv.status_paid === 'overdue' ? '#b14040' : '#8a6817' }}>· {inv.status_paid}</span> : null}
                                                    </span>
                                                </div>
                                            ))}
                                        </div>
                                    ) : null}
                                </DrawerSection>
                            ) : null}

                            {/* Sekcija: Sve line items iste ponude (multi-item ponude vide sve stavke) */}
                            {data.line_items && data.line_items.length > 1 ? (
                                <DrawerSection title={t('Sve stavke ove ponude', 'All offer line items')+` (${data.line_items.length})`}>
                                    {data.line_items.map((li: any, i: number) => (
                                        <div key={i} style={{ display: 'flex', justifyContent: 'space-between', gap: 12, padding: '3px 0', borderBottom: i < data.line_items.length - 1 ? '1px solid #f1ece0' : 0, fontSize: '0.8rem' }}>
                                            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: '1 1 auto', minWidth: 0 }} title={li.naziv_artikla ?? ''}>
                                                <code style={{ marginRight: 6 }}>{li.sifra_artikla}</code>{li.naziv_artikla}
                                            </span>
                                            <span style={{ flex: '0 0 auto', whiteSpace: 'nowrap' }}>
                                                {Math.round(Number(li.kolicina))} kom · {fmtNum(li.fine_ukupno_g, 2)}g · <strong>{fmtMoney(li.iznos_sa_pdv)}</strong>
                                            </span>
                                        </div>
                                    ))}
                                    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0 0', marginTop: 4, borderTop: '2px solid #c99b31', fontWeight: 700 }}>
                                        <span>{t('UKUPNO', 'TOTAL')}</span>
                                        <span>{fmtMoney(data.offer_total)}</span>
                                    </div>
                                </DrawerSection>
                            ) : null}

                            {/* Sekcija: Import metadata */}
                            <DrawerSection title={t('Tehničko', 'Technical')} defaultOpen={false}>
                                <DrawerKV label={t('ID artikla (JSON)', 'JSON item id')} value={<code>{o.id_artikla ?? '—'}</code>} />
                                <DrawerKV label={t('Import source', 'Import source')} value={o.import_source ?? '—'} />
                                <DrawerKV label={t('Importirano', 'Imported at')} value={(o.imported_at ?? '').replace('T', ' ').slice(0, 16)} />
                                <DrawerKV label={t('Kreirano', 'Created')} value={(o.created_at ?? '').replace('T', ' ').slice(0, 16)} />
                            </DrawerSection>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}

function DrawerSection({ title, children, defaultOpen = true }: { title: string; children: React.ReactNode; defaultOpen?: boolean }) {
    const [open, setOpen] = useState(defaultOpen);
    return (
        <div style={{ marginBottom: 12, border: '1px solid rgba(184,156,91,0.3)', borderRadius: 6, overflow: 'hidden' }}>
            <button
                type="button"
                onClick={() => setOpen(!open)}
                style={{
                    width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                    background: 'rgba(184,156,91,0.10)', border: 0, padding: '7px 12px', cursor: 'pointer',
                    fontSize: '0.78rem', color: '#6b4f1c', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em',
                }}
            >
                <span>{title}</span>
                <span style={{ fontSize: '0.72rem' }}>{open ? '▾' : '▸'}</span>
            </button>
            {open ? <div style={{ padding: '8px 12px' }}>{children}</div> : null}
        </div>
    );
}

function DrawerKV({ label, value }: { label: string; value: React.ReactNode }) {
    return (
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 12, padding: '3px 0', fontSize: '0.82rem', borderBottom: '1px solid #f1ece0' }}>
            <span className="muted" style={{ fontSize: '0.76rem', minWidth: 130 }}>{label}</span>
            <span style={{ textAlign: 'right' }}>{value}</span>
        </div>
    );
}

function RucBox({ label, eur, pct, fmtMoney, fmtPct, placeholder, sub }: { label: string; eur: number | null; pct: number | null; fmtMoney: (n: number | null | undefined) => string; fmtPct: (n: number | null | undefined) => string; placeholder?: string; sub?: React.ReactNode }) {
    const color = eur == null ? '#999' : eur >= 0 ? '#1f7a36' : '#b14040';
    return (
        <div style={{ padding: '8px 10px', background: '#fff', border: '1px solid rgba(184,156,91,0.4)', borderRadius: 4, textAlign: 'center' }}>
            <div style={{ fontSize: '0.7rem', color: '#6b4f1c', fontWeight: 700, marginBottom: 2 }}>{label}</div>
            <div style={{ fontSize: '0.95rem', fontWeight: 700, color }}>{eur != null ? fmtMoney(eur) : (placeholder ?? '—')}</div>
            <div style={{ fontSize: '0.76rem', color: '#666' }}>{pct != null ? fmtPct(pct) : ''}</div>
            {sub != null ? <div style={{ fontSize: '0.62rem', color: '#999', marginTop: 2 }}>{sub}</div> : null}
        </div>
    );
}

function BreakdownCard({ title, children }: { title: string; children: React.ReactNode }) {
    return (
        <div style={{ padding: '7px 8px', background: '#fff', border: '1px solid rgba(184,156,91,0.4)', borderRadius: 6, minWidth: 0 }}>
            <div style={{ fontSize: '0.58rem', color: '#6b4f1c', fontWeight: 700, marginBottom: 3, textTransform: 'uppercase', letterSpacing: '0.03em', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{title}</div>
            {children}
        </div>
    );
}

function BreakdownRow({ left, right, onClick, active, first }: { left: React.ReactNode; right: React.ReactNode; onClick?: () => void; active?: boolean; first?: boolean }) {
    return (
        <div
            role={onClick ? 'button' : undefined}
            onClick={onClick}
            style={{
                display: 'flex', justifyContent: 'space-between', gap: 5, fontSize: '0.66rem',
                padding: '2px 5px', margin: '0 -5px',
                borderTop: first ? 0 : '1px solid #eee',
                cursor: onClick ? 'pointer' : 'default',
                background: active ? 'rgba(201,155,49,0.18)' : undefined,
                borderRadius: active ? 4 : 0,
                transition: 'background 0.12s',
            }}
            onMouseEnter={(e) => { if (onClick && !active) (e.currentTarget as HTMLDivElement).style.background = 'rgba(201,155,49,0.08)'; }}
            onMouseLeave={(e) => { if (onClick && !active) (e.currentTarget as HTMLDivElement).style.background = 'transparent'; }}
        >
            <span>{left}</span>
            <span>{right}</span>
        </div>
    );
}

function OtkupEventRow({ ev, locale, fmtMoney, fmtNum }: {
    ev: { id: number; type: 'otkup_investicijsko' | 'otkup_lom'; txn_at: string; kasa_name: string | null; doc_ref: string; amount_eur: number; gold_fine_g: number | null; silver_fine_g: number | null; ruc_eur: number | null };
    locale: 'hr' | 'en';
    fmtMoney: (n: number | null | undefined) => string;
    fmtNum: (n: number | null | undefined, d?: number) => string;
}) {
    const t = (hr: string, en: string): string => (locale === 'en' ? en : hr);
    const isLom = ev.type === 'otkup_lom';
    const color = isLom ? '#1e40af' : '#6f42c1';
    const label = isLom ? t('Otkup lom', 'Scrap buyback') : t('Otkup investicijsko', 'Investment buyback');
    const icon = isLom ? '⚙' : '↺';
    const time = ev.txn_at && ev.txn_at.length >= 16 ? ev.txn_at.slice(11, 16) : '';
    return (
        <div style={{ borderLeft: `4px solid ${color}`, background: '#faf9fc', borderRadius: 4, padding: '7px 12px', display: 'flex', alignItems: 'center', gap: 12, fontSize: '0.82rem', flexWrap: 'wrap' }}>
            <span style={{ color, fontWeight: 700 }}>{icon} {label}</span>
            <span className="muted">{time}{ev.kasa_name ? ' · ' + ev.kasa_name : ''}</span>
            <span className="muted" style={{ fontSize: '0.74rem' }}>#{ev.doc_ref}</span>
            <span style={{ marginLeft: 'auto', display: 'flex', gap: 12, alignItems: 'center' }}>
                {ev.gold_fine_g ? <span>🥇 {fmtNum(ev.gold_fine_g, 1)}g</span> : null}
                {isLom && ev.silver_fine_g ? <span>🥈 {fmtNum(ev.silver_fine_g, 1)}g</span> : null}
                <strong style={{ color }}>−{fmtMoney(ev.amount_eur)}</strong>
                {ev.ruc_eur != null ? <span style={{ color: '#6b4f1c' }} title={isLom ? t('RUC = prihod − nabavna', 'GM = revenue − cost') : t('RUC = (spot − plaćeno) + premija izrade', 'GM = (spot − paid) + manufacturing premium')}>RUC {fmtMoney(ev.ruc_eur)}</span> : null}
            </span>
        </div>
    );
}

function FilterChip({ label, onClear }: { label: string; onClear: () => void }) {
    return (
        <span style={{
            display: 'inline-flex', alignItems: 'center', gap: 6,
            padding: '2px 4px 2px 8px', fontSize: '0.74rem',
            background: 'rgba(31,122,54,0.10)', color: '#1f7a36',
            border: '1px solid rgba(31,122,54,0.3)', borderRadius: 12,
        }}>
            {label}
            <button type="button" onClick={onClear} style={{ border: 0, background: 'transparent', color: '#1f7a36', cursor: 'pointer', padding: '0 4px', fontWeight: 700 }}>×</button>
        </span>
    );
}

function LiveOffersFeed({ token, locale, countries = 'HR' }: { token: string; locale: 'hr' | 'en'; countries?: string }) {
    const t = (hr: string, en: string) => (locale === 'en' ? en : hr);
    const fmtMoney = (n: number | null | undefined) => n == null ? '—' : formatMoney(n, locale);
    const fmtPct = (n: number | null | undefined) => n == null ? '—' : `${n.toFixed(1)}%`;
    const fmtNum = (n: number | null | undefined, dp = 2) => n == null ? '—' : Number(n).toLocaleString(locale === 'en' ? 'en-GB' : 'hr-HR', { minimumFractionDigits: dp, maximumFractionDigits: dp });

    // Filteri — default na DANAS
    const today = new Date().toISOString().slice(0, 10);
    const [dateFrom, setDateFrom] = useState(today);
    const [dateTo, setDateTo] = useState(today);
    const [poslovnica, setPoslovnica] = useState('');
    const [vrstaKovine, setVrstaKovine] = useState('');
    const [accepted, setAccepted] = useState('');
    const [hedgeStatus, setHedgeStatus] = useState('');
    const [minIznos, setMinIznos] = useState('');
    const [search, setSearch] = useState('');
    const [mint, setMint] = useState('');
    const [finePerUnit, setFinePerUnit] = useState('');
    const [region, setRegion] = useState('');
    // Tip transakcije — scaffold. Kad JSON proširi sa 'type' poljem, filter postaje aktivan.
    const [feedType, setFeedType] = useState<'' | 'prodaja_investicijsko' | 'otkup_investicijsko' | 'otkup_lom'>('');
    // PO TIPU kartica: vrste isključene iz kombiniranog zbroja (Σ). Toggle ne dira feed listu.
    const [excludedTypes, setExcludedTypes] = useState<Set<string>>(new Set());
    // V2 unified view — 1 row = 1 ponuda (header), 1 row može imati više line items.
    // Default = 'lines' (V1) jer V1 ima sve KPI/RUC info po artiklu.
    const [viewMode, setViewMode] = useState<'lines' | 'unified'>(() => {
        try { return (localStorage.getItem('ado_m1_view_mode') as 'lines' | 'unified') ?? 'lines'; } catch { return 'lines'; }
    });
    const [dataV2, setDataV2] = useState<any>(null);
    // Server-side sort polja (uključujući RUC computed)
    type SortKey = 'datum_ponude' | 'iznos_sa_pdv' | 'fine_ukupno_g' | 'spot_na_izdavanju' |
        'ruc_planiran_eur' | 'ruc_planiran_pct' | 'ruc_realiziran_eur' | 'ruc_realiziran_pct' |
        'ruc_fixing_eur' | 'ruc_fixing_pct';
    const [sort, setSort] = useState<SortKey>('datum_ponude');
    const [dir, setDir] = useState<'asc' | 'desc'>('desc');
    // Pagination
    const [page, setPage] = useState(1);
    const [perPage, setPerPage] = useState(100);
    // Promjena stranice → skrolaj na vrh liste (inače ostaješ na dnu).
    const feedTopRef = React.useRef<HTMLDivElement | null>(null);
    const goToPage = (p: number) => {
        setPage(p);
        requestAnimationFrame(() => feedTopRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }));
    };
    // Group by poslovnica (client-side, na current page)
    const [groupByBranch, setGroupByBranch] = useState(false);

    // Quick range presets
    function setRange(preset: 'today' | 'yesterday' | 'this_week' | 'last_week' | 'this_month' | 'last_30d') {
        const now = new Date();
        const fmt = (d: Date) => d.toISOString().slice(0, 10);
        const startOfWeek = (d: Date) => {
            const dt = new Date(d);
            const day = dt.getDay() || 7; // pon=1..ned=7
            if (day !== 1) dt.setDate(dt.getDate() - (day - 1));
            return dt;
        };
        switch (preset) {
            case 'today': setDateFrom(fmt(now)); setDateTo(fmt(now)); break;
            case 'yesterday': {
                const y = new Date(now); y.setDate(y.getDate() - 1);
                setDateFrom(fmt(y)); setDateTo(fmt(y)); break;
            }
            case 'this_week': {
                const s = startOfWeek(now);
                setDateFrom(fmt(s)); setDateTo(fmt(now)); break;
            }
            case 'last_week': {
                const s = startOfWeek(now); s.setDate(s.getDate() - 7);
                const e = new Date(s); e.setDate(e.getDate() + 6);
                setDateFrom(fmt(s)); setDateTo(fmt(e)); break;
            }
            case 'this_month': {
                const s = new Date(now.getFullYear(), now.getMonth(), 1);
                setDateFrom(fmt(s)); setDateTo(fmt(now)); break;
            }
            case 'last_30d': {
                const s = new Date(now); s.setDate(s.getDate() - 30);
                setDateFrom(fmt(s)); setDateTo(fmt(now)); break;
            }
        }
    }
    const activePreset = (): string => {
        const td = new Date().toISOString().slice(0, 10);
        if (dateFrom === td && dateTo === td) return 'today';
        return '';
    };
    const [data, setData] = useState<LiveOffersResponse | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [drawerId, setDrawerId] = useState<number | null>(null);
    const [drawerData, setDrawerData] = useState<any>(null);

    // Live feed: tihi poll (~20s) na stranici 1 → novi redovi sami iskoče na vrh uz highlight.
    const seenIdsRef = React.useRef<Set<string>>(new Set());
    const flashTimerRef = React.useRef<number | null>(null);
    const [flashIds, setFlashIds] = useState<Set<string>>(new Set());
    const [liveStampedAt, setLiveStampedAt] = useState<number>(0);
    const [liveNewCount, setLiveNewCount] = useState(0);

    // 📈 Po satima (intraday pace): danas vs prosjek N dana
    const [paceOpen, setPaceOpen] = useState(false);
    const [paceDays, setPaceDays] = useState(30);
    const [paceType, setPaceType] = useState<'accepted' | 'offered'>('accepted');
    const [paceMetric, setPaceMetric] = useState<'count' | 'eur'>('count');
    const [paceData, setPaceData] = useState<any>(null);
    const [paceLoading, setPaceLoading] = useState(false);
    useEffect(() => {
        if (!paceOpen) return;
        let alive = true;
        setPaceLoading(true);
        const qs = new URLSearchParams({ days: String(paceDays), type: paceType, countries });
        if (poslovnica) qs.set('poslovnica', poslovnica);
        api<any>(`/ado/live-offers/hourly-pace?${qs}`, token)
            .then((r) => { if (alive) setPaceData(r); })
            .catch(() => { if (alive) setPaceData(null); })
            .finally(() => { if (alive) setPaceLoading(false); });
        return () => { alive = false; };
    }, [paceOpen, paceDays, paceType, poslovnica, countries, token]);

    const rowIdsOf = useCallback((payload: any): string[] => {
        return ((payload?.rows ?? []) as any[]).map((r) => String(viewMode === 'unified' ? r.offer_document_id : r.id));
    }, [viewMode]);

    const load = useCallback(async (silent = false) => {
        if (!silent) setLoading(true);
        if (!silent) setError('');
        try {
            const qs = new URLSearchParams({
                date_from: dateFrom, date_to: dateTo,
                sort, dir, page: String(page), per_page: String(perPage), countries,
            });
            if (poslovnica) qs.set('poslovnica', poslovnica);
            if (vrstaKovine) qs.set('vrsta_kovine', vrstaKovine);
            if (accepted) qs.set('accepted', accepted);
            if (hedgeStatus) qs.set('hedge_status', hedgeStatus);
            if (minIznos) qs.set('min_iznos', minIznos);
            if (search.trim()) qs.set('search', search.trim());
            if (mint) qs.set('mint', mint);
            if (finePerUnit) qs.set('fine_per_unit', finePerUnit);
            if (region) qs.set('region', region);
            if (feedType) qs.set('feed_type', feedType);
            const url = viewMode === 'unified' ? `/ado/live-offers-v2?${qs.toString()}` : `/ado/live-offers?${qs.toString()}`;
            const r = await api<any>(url, token);
            const newIds = rowIdsOf(r);
            const fresh = silent ? newIds.filter((id) => !seenIdsRef.current.has(id)) : [];
            if (viewMode === 'unified') { setDataV2(r); setData(null); }
            else { setData(r); setDataV2(null); }
            seenIdsRef.current = new Set(newIds);
            if (silent) {
                setLiveStampedAt(Date.now());
                if (fresh.length > 0) {
                    setFlashIds(new Set(fresh));
                    setLiveNewCount(fresh.length);
                    if (flashTimerRef.current) window.clearTimeout(flashTimerRef.current);
                    flashTimerRef.current = window.setTimeout(() => { setFlashIds(new Set()); setLiveNewCount(0); }, 6000);
                }
            }
        } catch (e) {
            if (!silent) setError(e instanceof Error ? e.message : 'API');
        } finally { if (!silent) setLoading(false); }
    }, [dateFrom, dateTo, poslovnica, vrstaKovine, accepted, hedgeStatus, minIznos, search, mint, finePerUnit, region, feedType, countries, sort, dir, page, perPage, token, viewMode, rowIdsOf]);

    // Reset na stranicu 1 kad se filteri/sort mijenjaju
    useEffect(() => { setPage(1); }, [dateFrom, dateTo, poslovnica, vrstaKovine, accepted, hedgeStatus, minIznos, search, mint, finePerUnit, region, feedType, countries, sort, dir, perPage]);

    useEffect(() => { void load(); }, [load]);

    // Tihi live-poll: samo na stranici 1, pauziran kad je tab u pozadini. Novi redovi se sami dodaju + flash.
    useEffect(() => {
        if (page !== 1) return;
        const iv = window.setInterval(() => { if (!document.hidden) void load(true); }, 20000);
        return () => window.clearInterval(iv);
    }, [page, load]);

    async function openDrawer(idOrErp: number | string) {
        // ERP-only ponude imaju id="erp_NNN" (string). JSON ponude imaju number id.
        const isErp = typeof idOrErp === 'string' && idOrErp.startsWith('erp_');
        const numericId = isErp ? Number((idOrErp as string).replace('erp_', '')) : Number(idOrErp);
        setDrawerId(numericId);
        setDrawerData(null);
        try {
            const url = isErp ? `/ado/live-offers/erp/${numericId}` : `/ado/live-offers/${numericId}`;
            const r = await api<any>(url, token);
            setDrawerData(r);
        } catch (e) {
            setError(e instanceof Error ? e.message : 'API');
        }
    }

    // V2 drawer — uzima offer_document_id (header) — vraća header + svi line items.
    async function openDrawerUnified(headerId: number) {
        setDrawerId(headerId);
        setDrawerData(null);
        try {
            const r = await api<any>(`/ado/live-offers-v2/${headerId}`, token);
            // Mapiraj v2 shape u shape koji LiveOfferDrawer očekuje.
            // line_items[0] služi kao "primary offer" za drawer header KPIs.
            const primary = (r.line_items ?? [])[0] ?? {
                broj_ponude: r.header?.offer_number,
                datum_ponude: r.header?.offer_date,
                naziv_artikla: '(ERP — bez JSON line itema)',
                iznos_sa_pdv: r.header?.amount_total,
                source: 'erp_only',
            };
            // Aggregate iznos sa svim line itemima
            const totalIznos = (r.line_items ?? []).reduce((s: number, l: any) => s + Number(l.iznos_sa_pdv ?? 0), 0);
            primary.iznos_sa_pdv = totalIznos || (r.header?.amount_total ?? 0);
            const erpMatches = r.header ? [{
                id: r.header.id,
                offer_number: r.header.offer_number,
                offer_date: r.header.offer_date,
                partner_code: r.header.partner_code,
                partner_name: r.header.partner_name,
                amount_total: r.header.amount_total,
                invoice_count: r.header.invoice_count,
            }] : [];
            setDrawerData({
                offer: primary,
                hedge_links: [],
                line_items: r.line_items ?? [],
                offer_total: totalIznos,
                erp_matches: erpMatches,
                erp_invoices: r.invoices ?? [],
                is_erp_only: r.is_erp_only ?? false,
                annotations_v2: r.annotations ?? [],
            });
        } catch (e) {
            setError(e instanceof Error ? e.message : 'API');
        }
    }

    function toggleSort(k: SortKey) {
        if (sort === k) setDir(dir === 'asc' ? 'desc' : 'asc');
        else { setSort(k); setDir('desc'); }
    }
    const sortArrow = (k: SortKey) => sort === k ? (dir === 'asc' ? ' ▲' : ' ▼') : '';

    const s = data?.summary;
    const acceptedPct = s && s.count_lines > 0 ? (100 * s.accepted_count / s.count_lines) : 0;

    // displayRows = server-paginated + server-sorted (uključujući RUC%)
    const displayRows = data?.rows ?? [];

    // Grouping po poslovnici (client-side) — vraća array bilo
    // [{kind:'row', row}] ili [{kind:'subtotal', label, totals}]
    const groupedRows = React.useMemo(() => {
        if (!groupByBranch) return null;
        const out: Array<{ kind: 'row'; row: LiveOfferRow; idx: number } | { kind: 'subtotal'; label: string; count: number; iznos: number; nabavna: number; fine: number; rucPlan: number; rucReal: number | null; rucPlanNeto: number; rucRealNeto: number | null }> = [];
        const baseFrom = data?.pagination?.from_idx ?? 1;
        // Sort by poslovnica, then preserve original order
        const sorted = [...displayRows].sort((a, b) => (a.poslovnica ?? '').localeCompare(b.poslovnica ?? ''));
        let curKey = '__none__';
        let bucket: typeof sorted = [];
        const flush = () => {
            if (bucket.length === 0) return;
            const label = bucket[0].poslovnica ?? '(bez poslovnice)';
            const tot = bucket.reduce((a, r) => ({
                iznos: a.iznos + r.iznos_sa_pdv,
                nabavna: a.nabavna + (r.vrijednost_na_izdavanju ?? 0),
                fine: a.fine + r.fine_ukupno_g,
                rucPlan: a.rucPlan + r.ruc_planiran_eur,
                rucReal: r.ruc_realiziran_eur != null ? (a.rucReal ?? 0) + r.ruc_realiziran_eur : a.rucReal,
                rucPlanNeto: a.rucPlanNeto + (r.ruc_planiran_neto_eur ?? r.ruc_planiran_eur),
                rucRealNeto: r.ruc_realiziran_eur != null ? (a.rucRealNeto ?? 0) + (r.ruc_realiziran_neto_eur ?? r.ruc_realiziran_eur) : a.rucRealNeto,
            }), { iznos: 0, nabavna: 0, fine: 0, rucPlan: 0, rucReal: null as number | null, rucPlanNeto: 0, rucRealNeto: null as number | null });
            out.push({ kind: 'subtotal', label, count: bucket.length, ...tot });
            bucket = [];
        };
        sorted.forEach((r, i) => {
            const key = r.poslovnica ?? '__none__';
            if (key !== curKey) { flush(); curKey = key; }
            bucket.push(r);
            out.push({ kind: 'row', row: r, idx: baseFrom + i });
        });
        flush();
        return out;
    }, [groupByBranch, displayRows, data]);

    return (
        <div className="ado-m1__operations" style={{ paddingTop: 8 }}>
            <style>{`
                @keyframes feedNewFlash {
                    0%   { background: rgba(31,122,54,0.22); box-shadow: inset 0 0 0 2px rgba(31,122,54,0.55); }
                    70%  { background: rgba(31,122,54,0.12); }
                    100% { background: transparent; box-shadow: none; }
                }
                .feed-new-flash { animation: feedNewFlash 6s ease-out; border-radius: 8px; }
                @keyframes liveDotPulse { 0%,100% { opacity: 1; } 50% { opacity: 0.25; } }
                .live-dot { display:inline-block; width:8px; height:8px; border-radius:50%; background:#1f7a36; animation: liveDotPulse 1.6s ease-in-out infinite; }
            `}</style>
            {/* Live indikator — vidljiv samo na stranici 1 (gdje tihi poll dodaje nove redove). */}
            {page === 1 ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8, fontSize: '0.76rem', color: '#5b6065' }}>
                    <span className="live-dot" />
                    <strong style={{ color: '#1f7a36' }}>{t('Uživo', 'Live')}</strong>
                    <span>· {t('novo se prikazuje automatski', 'new entries appear automatically')}</span>
                    {liveNewCount > 0 ? (
                        <span style={{ background: '#1f7a36', color: '#fff', borderRadius: 10, padding: '1px 8px', fontWeight: 700 }}>
                            +{liveNewCount} {t('novo', 'new')}
                        </span>
                    ) : null}
                    {liveStampedAt > 0 ? (
                        <span style={{ marginLeft: 'auto', color: '#9aa0a6' }}>
                            {t('ažurirano', 'updated')} {new Date(liveStampedAt).toLocaleTimeString(locale === 'en' ? 'en-GB' : 'hr-HR')}
                        </span>
                    ) : null}
                </div>
            ) : null}

            {/* 📈 Po satima — intraday pace: danas vs prosjek N dana za isto doba dana */}
            <div style={{ background: '#fff', border: '1px solid rgba(184,156,91,0.3)', borderRadius: 8, marginBottom: 10, overflow: 'hidden' }}>
                <button type="button" onClick={() => setPaceOpen((v) => !v)} style={{ width: '100%', textAlign: 'left', background: 'transparent', border: 0, padding: '9px 12px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8, fontWeight: 700, color: '#6b4f1c' }}>
                    <span>{paceOpen ? '▾' : '▸'}</span>📈 {t('Po satima — kako stojimo danas', 'By hour — how we pace today')}
                    <span style={{ marginLeft: 'auto', fontWeight: 400, fontSize: '0.76rem', color: '#888' }}>{t('danas vs prosjek razdoblja', 'today vs period average')}</span>
                </button>
                {paceOpen ? (
                    <div style={{ padding: '0 12px 12px' }}>
                        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginBottom: 10, fontSize: '0.8rem' }}>
                            {/* metrika */}
                            <div style={{ display: 'inline-flex', border: '1px solid rgba(184,156,91,0.4)', borderRadius: 6, overflow: 'hidden' }}>
                                {(['count', 'eur'] as const).map((m) => (
                                    <button key={m} type="button" onClick={() => setPaceMetric(m)} style={{ border: 0, padding: '4px 10px', cursor: 'pointer', background: paceMetric === m ? '#6b4f1c' : 'transparent', color: paceMetric === m ? '#fff' : '#6b4f1c', fontWeight: 700 }}>{m === 'count' ? t('Broj', 'Count') : '€'}</button>
                                ))}
                            </div>
                            {/* tip */}
                            <div style={{ display: 'inline-flex', border: '1px solid rgba(184,156,91,0.4)', borderRadius: 6, overflow: 'hidden' }}>
                                {(['accepted', 'offered'] as const).map((tp) => (
                                    <button key={tp} type="button" onClick={() => setPaceType(tp)} style={{ border: 0, padding: '4px 10px', cursor: 'pointer', background: paceType === tp ? '#1f7a36' : 'transparent', color: paceType === tp ? '#fff' : '#1f7a36', fontWeight: 700 }}>{tp === 'accepted' ? t('Potvrđene', 'Accepted') : t('Ponuđene', 'Offered')}</button>
                                ))}
                            </div>
                            {/* razdoblje */}
                            <div style={{ display: 'inline-flex', border: '1px solid rgba(184,156,91,0.4)', borderRadius: 6, overflow: 'hidden' }}>
                                {[30, 60, 90].map((d) => (
                                    <button key={d} type="button" onClick={() => setPaceDays(d)} style={{ border: 0, padding: '4px 10px', cursor: 'pointer', background: paceDays === d ? '#3b82f6' : 'transparent', color: paceDays === d ? '#fff' : '#3b82f6', fontWeight: 700 }}>{d}d</button>
                                ))}
                            </div>
                        </div>
                        {paceLoading ? <p className="muted" style={{ fontSize: '0.82rem' }}>{t('Učitavam…', 'Loading…')}</p>
                            : !paceData || (paceData.hours ?? []).length === 0 ? <p className="muted" style={{ fontSize: '0.82rem' }}>{t('Nema podataka.', 'No data.')}</p>
                                : (() => {
                                    const today = paceMetric === 'count' ? paceData.series.today_count : paceData.series.today_eur;
                                    const avg = paceMetric === 'count' ? paceData.series.avg_count : paceData.series.avg_eur;
                                    const fmt = (n: number) => paceMetric === 'eur'
                                        ? (n >= 1e6 ? (n / 1e6).toFixed(1) + 'M' : n >= 1e3 ? (n / 1e3).toFixed(0) + 'k' : Math.round(n) + '') + ' €'
                                        : String(Math.round(n));
                                    // "Do sad" usporedba na trenutni sat
                                    const idxNow = (paceData.hours as number[]).indexOf(paceData.now_hour);
                                    const lastIdx = idxNow >= 0 ? idxNow : ((today as any[]).map((v, i) => v != null ? i : -1).filter((i) => i >= 0).pop() ?? -1);
                                    const tNow = lastIdx >= 0 ? today[lastIdx] : null;
                                    const aNow = lastIdx >= 0 ? avg[lastIdx] : null;
                                    const diffPct = (tNow != null && aNow) ? Math.round(100 * (tNow - aNow) / aNow) : null;
                                    return (
                                        <>
                                            {tNow != null && aNow != null ? (
                                                <div style={{ fontSize: '0.84rem', marginBottom: 8, padding: '6px 10px', background: '#faf7ef', borderRadius: 6 }}>
                                                    {t('Do', 'By')} <strong>{paceData.hour_labels[lastIdx]}</strong>: {t('danas', 'today')} <strong style={{ color: '#c99b31' }}>{fmt(tNow)}</strong> {t('vs prosjek', 'vs avg')} <strong style={{ color: '#1f7a36' }}>{fmt(aNow)}</strong>
                                                    {diffPct != null ? <span style={{ marginLeft: 8, fontWeight: 700, color: diffPct >= 0 ? '#1f7a36' : '#b14040' }}>{diffPct >= 0 ? '▲ +' : '▼ '}{diffPct}%</span> : null}
                                                    <span style={{ color: '#9aa0a6', marginLeft: 8 }}>({t('prosjek', 'avg')} {paceData.active_days} {t('dana', 'days')})</span>
                                                </div>
                                            ) : null}
                                            <NiceLineChart
                                                height={240}
                                                locale={locale}
                                                xLabels={paceData.hour_labels}
                                                series={[
                                                    { name: `${t('Danas', 'Today')} (${paceData.today_date})`, color: '#c99b31', values: today },
                                                    { name: `${t('Prosjek', 'Avg')} ${paceData.days}d`, color: '#1f7a36', values: avg },
                                                ]}
                                                yFormatter={fmt}
                                            />
                                            <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', justifyContent: 'center', marginTop: 6, fontSize: '0.74rem' }}>
                                                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}><span style={{ width: 14, borderTop: '2px solid #c99b31', display: 'inline-block' }} />{t('Danas', 'Today')}</span>
                                                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}><span style={{ width: 14, borderTop: '2px solid #1f7a36', display: 'inline-block' }} />{t('Prosjek', 'Avg')} {paceData.days}d</span>
                                            </div>
                                        </>
                                    );
                                })()}
                    </div>
                ) : null}
            </div>

            {/* Info kartice — breakdown po metalu / kovnici / poslovnici / veličini poluge / tipu. Klik na redak = filter. */}
            {data?.breakdown ? (
                <div style={{ display: 'grid', gridAutoFlow: 'column', gridAutoColumns: 'minmax(150px, 1fr)', gap: 8, marginBottom: 10, overflowX: 'auto', alignItems: 'start' }}>
                    {/* Po tipu — prodaja investicijsko (ponude) + otkup investicijsko + otkup lom (15-min izvještaji).
                        Klik na redak uključi/isključi tu vrstu iz KOMBINIRANOG zbroja (Σ). Ne dira feed listu. */}
                    {(() => {
                        const byType = data.breakdown.by_type;
                        const typeRows = byType && byType.length > 0 ? byType : [
                            { type: 'prodaja_investicijsko' as const, count_lines: data.summary.count_lines, count_offers: data.summary.count_offers, total_eur: data.summary.total_prodajno, total_fine_g: data.summary.total_fine_g },
                            { type: 'otkup_investicijsko' as const, count_lines: 0, count_offers: 0, total_eur: 0, total_fine_g: 0 },
                            { type: 'otkup_lom' as const, count_lines: 0, count_offers: 0, total_eur: 0, total_fine_g: 0 },
                        ];
                        const meta: Record<string, { icon: string; label: string; color: string }> = {
                            prodaja_investicijsko: { icon: '🥇', label: t('Prodaja investicijsko', 'Investment sale'), color: '#c99b31' },
                            otkup_investicijsko: { icon: '↺', label: t('Otkup investicijsko', 'Investment buyback'), color: '#6f42c1' },
                            otkup_lom: { icon: '⚙', label: t('Otkup lom', 'Scrap buyback'), color: '#1e40af' },
                        };
                        const included = typeRows.filter((tr) => tr.count_offers > 0 && !excludedTypes.has(tr.type));
                        const combo = included.reduce((a, tr) => ({
                            count_offers: a.count_offers + (tr.count_offers || 0),
                            total_eur: a.total_eur + (tr.total_eur || 0),
                            total_fine_g: a.total_fine_g + (tr.total_fine_g || 0),
                            ruc: a.ruc + (((tr as { ruc_neto_eur?: number; ruc_eur?: number }).ruc_neto_eur ?? (tr as { ruc_eur?: number }).ruc_eur) || 0),
                        }), { count_offers: 0, total_eur: 0, total_fine_g: 0, ruc: 0 });
                        return (
                            <BreakdownCard title={t('Po tipu', 'By type')}>
                                <BreakdownRow
                                    first
                                    active
                                    left={<strong style={{ color: '#1f7a36' }}>Σ {t('Ukupno (uključeno)', 'Total (included)')}</strong>}
                                    right={<><strong>{fmtNum(combo.total_fine_g, 1)}g</strong> · {combo.count_offers} · <strong>{fmtMoney(combo.total_eur)}</strong> · <span style={{ color: '#6b4f1c' }}>RUC <strong>{fmtMoney(combo.ruc)}</strong></span></>}
                                />
                                {typeRows.map((tr) => {
                                    const m = meta[tr.type];
                                    const hasData = tr.count_offers > 0;
                                    const isExcluded = excludedTypes.has(tr.type);
                                    return (
                                        <BreakdownRow
                                            key={tr.type}
                                            active={hasData && !isExcluded}
                                            onClick={!hasData ? undefined : () => setExcludedTypes((prev) => {
                                                const n = new Set(prev);
                                                if (n.has(tr.type)) n.delete(tr.type); else n.add(tr.type);
                                                return n;
                                            })}
                                            first={false}
                                            left={<span style={{ color: hasData ? (isExcluded ? '#aaa' : m.color) : '#bbb', fontWeight: 700, textDecoration: isExcluded ? 'line-through' : 'none' }} title={hasData ? t('Klik: uključi/isključi iz zbroja', 'Click: include/exclude from total') : t('Nema podataka za raspon', 'No data for range')}>
                                                {hasData ? (isExcluded ? '☐' : '☑') : '·'} {m.icon} {m.label}
                                            </span>}
                                            right={hasData
                                                ? <span style={{ opacity: isExcluded ? 0.4 : 1 }}><strong>{fmtNum(tr.total_fine_g, 1)}g</strong> · {tr.count_offers} · <strong>{fmtMoney(tr.total_eur)}</strong>{(tr.ruc_neto_eur ?? tr.ruc_eur) != null ? <> · <span style={{ color: '#6b4f1c' }}>RUC <strong>{fmtMoney(tr.ruc_neto_eur ?? tr.ruc_eur)}</strong></span></> : null}</span>
                                                : <span style={{ color: '#bbb', fontStyle: 'italic' }}>{t('—', '—')}</span>}
                                        />
                                    );
                                })}
                            </BreakdownCard>
                        );
                    })()}
                    {/* Otkup lom (buyback) — detalj iz 15-min izvještaja */}
                    {(() => {
                        const lom = data.breakdown.buyback_lom;
                        if (!lom || lom.count === 0) return null;
                        return (
                            <BreakdownCard title={t('Otkup lom (buyback)', 'Scrap buyback')}>
                                <BreakdownRow first left={<span>{t('Transakcija', 'Txns')}</span>} right={<strong>{lom.count}</strong>} />
                                <BreakdownRow left={<span style={{ color: '#c99b31' }}>🥇 {t('Zlato fine', 'Gold fine')}</span>} right={<strong>{fmtNum(lom.gold_fine_g, 1)}g</strong>} />
                                <BreakdownRow left={<span style={{ color: '#7a7a7a' }}>🥈 {t('Srebro fine', 'Silver fine')}</span>} right={<strong>{fmtNum(lom.silver_fine_g, 1)}g</strong>} />
                                <BreakdownRow left={<span>{t('Nabavna', 'Cost')}</span>} right={<strong>{fmtMoney(lom.nabavna_eur)}</strong>} />
                                <BreakdownRow left={<span>{t('Prihod (TCA)', 'Revenue')}</span>} right={<strong>{fmtMoney(lom.prihod_eur)}</strong>} />
                                <BreakdownRow left={<span style={{ color: '#6b4f1c', fontWeight: 700 }}>RUC</span>} right={<strong style={{ color: '#6b4f1c' }}>{fmtMoney(lom.ruc_eur)}</strong>} />
                            </BreakdownCard>
                        );
                    })()}
                    {/* Po metalu + Cash agregat — stacked u jednom stupcu (PO METALU je nizak, pa popunjava visinu) */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8, minWidth: 0 }}>
                    {data.breakdown.by_metal.length > 0 ? (
                        <BreakdownCard title={t('Po metalu', 'By metal')}>
                            {data.breakdown.by_metal.map((m, i) => {
                                const isSilver = m.vrsta_kovine === 'Srebro';
                                const active = vrstaKovine === (m.vrsta_kovine ?? '');
                                return (
                                    <BreakdownRow
                                        key={i}
                                        active={active}
                                        onClick={() => setVrstaKovine(active ? '' : (m.vrsta_kovine ?? ''))}
                                        first={i === 0}
                                        left={<span style={{ color: isSilver ? '#7a7a7a' : '#c99b31', fontWeight: 700 }}>{isSilver ? '🥈 Ag' : '🥇 Au'} {m.vrsta_kovine ?? '—'}</span>}
                                        right={<><strong>{fmtNum(m.total_fine_g, 1)}g</strong> · {m.count_offers} pon · <strong>{fmtMoney(m.total_eur)}</strong></>}
                                    />
                                );
                            })}
                        </BreakdownCard>
                    ) : null}
                    {(() => {
                        const bt = data.breakdown.by_type;
                        if (!bt || bt.length === 0) return null;
                        const inc = bt.filter((tr) => !excludedTypes.has(tr.type) && tr.count_offers > 0);
                        const cashIn = inc.filter((tr) => (tr.cash_dir ?? 1) > 0).reduce((a, tr) => a + (tr.total_eur || 0), 0);
                        const cashOut = inc.filter((tr) => (tr.cash_dir ?? 1) < 0).reduce((a, tr) => a + (tr.total_eur || 0), 0);
                        const net = cashIn - cashOut;
                        const rucSumNeto = inc.reduce((a, tr) => a + ((tr.ruc_neto_eur ?? tr.ruc_eur) || 0), 0);
                        const rucSumBruto = inc.reduce((a, tr) => a + (tr.ruc_eur || 0), 0);
                        return (
                            <BreakdownCard title={t('Cash agregat (uključeno)', 'Cash aggregate (incl.)')}>
                                <BreakdownRow first left={<span style={{ color: '#1f7a36', fontWeight: 700 }}>＋ {t('Prodaja', 'Sales')}</span>} right={<strong>{fmtMoney(cashIn)}</strong>} />
                                <BreakdownRow left={<span style={{ color: '#b14040', fontWeight: 700 }}>− {t('Otkup', 'Buyback')}</span>} right={<strong>{fmtMoney(cashOut)}</strong>} />
                                <BreakdownRow left={<strong>{t('Neto cash', 'Net cash')}</strong>} right={<strong style={{ color: net >= 0 ? '#1f7a36' : '#b14040' }}>{fmtMoney(net)}</strong>} />
                                <BreakdownRow left={<span style={{ color: '#6b4f1c', fontWeight: 700 }}>RUC Σ {t('neto', 'net')}</span>} right={<><strong style={{ color: '#6b4f1c' }}>{fmtMoney(rucSumNeto)}</strong> <span style={{ fontSize: '0.72em', color: '#999' }}>/ {t('bruto', 'gross')} {fmtMoney(rucSumBruto)}</span></>} />
                            </BreakdownCard>
                        );
                    })()}
                    </div>
                    {/* Po kovnici (Argor, PAMP, Kinebar …) */}
                    {data.breakdown.by_mint && data.breakdown.by_mint.length > 0 ? (
                        <BreakdownCard title={t('Po kovnici', 'By mint')}>
                            {data.breakdown.by_mint.slice(0, 8).map((m, i) => {
                                const active = mint === m.mint;
                                return (
                                    <BreakdownRow
                                        key={i}
                                        active={active}
                                        onClick={() => setMint(active ? '' : m.mint)}
                                        first={i === 0}
                                        left={<span style={{ fontWeight: 600 }}>🏛 {m.mint}</span>}
                                        right={<>{m.count_offers} pon · <strong>{fmtMoney(m.total_eur)}</strong></>}
                                    />
                                );
                            })}
                        </BreakdownCard>
                    ) : null}
                    {/* Po veličini poluge TOP */}
                    {data.breakdown.by_size.length > 0 ? (
                        <BreakdownCard title={t('Po veličini poluge (top)', 'By bar size (top)')}>
                            {data.breakdown.by_size.slice(0, 6).map((s, i) => {
                                const active = finePerUnit === String(s.fine_per_unit);
                                return (
                                    <BreakdownRow
                                        key={i}
                                        active={active}
                                        onClick={() => setFinePerUnit(active ? '' : String(s.fine_per_unit))}
                                        first={i === 0}
                                        left={<strong>{s.fine_per_unit > 0 ? `${s.fine_per_unit}g` : '—'}</strong>}
                                        right={<>{Math.round(Number(s.total_kom))} kom · {s.count_offers} pon · <strong>{fmtMoney(s.total_eur)}</strong></>}
                                    />
                                );
                            })}
                        </BreakdownCard>
                    ) : null}
                    {/* Po poslovnici TOP */}
                    {data.breakdown.by_branch.length > 0 ? (
                        <BreakdownCard title={t('Po poslovnici (top)', 'By branch (top)')}>
                            {data.breakdown.by_branch.slice(0, 6).map((b, i) => {
                                const active = poslovnica === String(b.id_poslovnice ?? '');
                                return (
                                    <BreakdownRow
                                        key={i}
                                        active={active}
                                        onClick={() => setPoslovnica(active ? '' : String(b.id_poslovnice ?? ''))}
                                        first={i === 0}
                                        left={<span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 140, display: 'inline-block' }} title={b.poslovnica ?? '—'}>{b.poslovnica ?? '—'}</span>}
                                        right={<>{b.count_offers} pon · <strong>{fmtMoney(b.total_eur)}</strong></>}
                                    />
                                );
                            })}
                        </BreakdownCard>
                    ) : null}
                </div>
            ) : null}

            {/* Active filter chips (klik X = remove) */}
            {(vrstaKovine || mint || finePerUnit || poslovnica || feedType) ? (
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 8, alignItems: 'center' }}>
                    <span style={{ fontSize: '0.74rem', color: '#666' }}>{t('Aktivni filteri:', 'Active filters:')}</span>
                    {feedType ? <FilterChip label={`Tip: ${feedType.replaceAll('_', ' ')}`} onClear={() => setFeedType('')} /> : null}
                    {vrstaKovine ? <FilterChip label={`Metal: ${vrstaKovine}`} onClear={() => setVrstaKovine('')} /> : null}
                    {mint ? <FilterChip label={`Kovnica: ${mint}`} onClear={() => setMint('')} /> : null}
                    {finePerUnit ? <FilterChip label={`Veličina: ${finePerUnit}g`} onClear={() => setFinePerUnit('')} /> : null}
                    {region ? <FilterChip label={`Regija: ${region}`} onClear={() => setRegion('')} /> : null}
                    {poslovnica ? (
                        <FilterChip
                            label={`Poslovnica: ${((data ?? dataV2)?.filters_available?.poslovnice ?? []).find((p: any) => String(p.id_poslovnice) === poslovnica)?.poslovnica ?? poslovnica}`}
                            onClear={() => setPoslovnica('')}
                        />
                    ) : null}
                </div>
            ) : null}

            {/* View toggle: V1 (po stavkama) vs V2 (po narudžbi) */}
            <div style={{ display: 'flex', gap: 6, marginBottom: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                <span style={{ fontSize: '0.78rem', color: '#6b4f1c', fontWeight: 600, marginRight: 4 }}>👁 {t('Prikaz:', 'View:')}</span>
                {([['unified', '📦 ' + t('Po narudžbi (V2)', 'By offer (V2)')], ['lines', '📄 ' + t('Po stavkama (V1)', 'By line item (V1)')]] as const).map(([key, label]) => {
                    const active = viewMode === key;
                    return (
                        <button key={key} type="button" onClick={() => { setViewMode(key); try { localStorage.setItem('ado_m1_view_mode', key); } catch {} }} style={{
                            padding: '3px 12px', borderRadius: 4, fontSize: '0.74rem', fontWeight: 600,
                            cursor: 'pointer',
                            border: active ? 0 : '1px solid rgba(184,156,91,0.4)',
                            background: active ? 'linear-gradient(135deg, #6b4f1c, #c99b31)' : 'transparent',
                            color: active ? '#fffbeb' : '#6b4f1c',
                        }}>{label}</button>
                    );
                })}
                {viewMode === 'unified' && dataV2?.summary ? (
                    <span style={{ marginLeft: 'auto', fontSize: '0.78rem', color: '#34383d' }}>
                        <strong>{dataV2.summary.count_offers}</strong> {t('ponuda', 'offers')} ·
                        <strong style={{ marginLeft: 6 }}>{fmtMoney(dataV2.summary.total_eur)}</strong> ·
                        <strong style={{ marginLeft: 6 }}>{fmtNum(dataV2.summary.total_fine_g, 1)}g</strong> fine
                    </span>
                ) : null}
            </div>

            {/* Quick range presets + Group toggle */}
            <div style={{ display: 'flex', gap: 6, marginBottom: 10, flexWrap: 'wrap', alignItems: 'center' }}>
                <span style={{ fontSize: '0.78rem', color: '#6b4f1c', fontWeight: 600, marginRight: 4 }}>📅 {t('Brzi raspon:', 'Quick range:')}</span>
                {([
                    ['today', t('Danas', 'Today')],
                    ['yesterday', t('Jučer', 'Yesterday')],
                    ['this_week', t('Ovaj tjedan', 'This week')],
                    ['last_week', t('Prošli tjedan', 'Last week')],
                    ['this_month', t('Ovaj mjesec', 'This month')],
                    ['last_30d', t('Zadnjih 30d', 'Last 30d')],
                ] as const).map(([key, label]) => {
                    const active = key === 'today' ? activePreset() === 'today' : false;
                    return (
                        <button key={key} type="button" onClick={() => setRange(key)}
                            style={{
                                padding: '3px 10px', borderRadius: 4, fontSize: '0.74rem', fontWeight: 600,
                                cursor: 'pointer',
                                border: active ? 0 : '1px solid rgba(184,156,91,0.4)',
                                background: active ? 'linear-gradient(135deg, #6b4f1c, #c99b31)' : 'transparent',
                                color: active ? '#fffbeb' : '#6b4f1c',
                            }}>{label}</button>
                    );
                })}
                <span style={{ width: 1, height: 18, background: 'rgba(184,156,91,0.4)', margin: '0 6px' }} />
                <button type="button" onClick={() => setGroupByBranch(!groupByBranch)}
                    title={t('Grupira ponude na trenutnoj stranici po poslovnici sa subtotal-ima', 'Group current page offers by branch with subtotals')}
                    style={{
                        padding: '3px 10px', borderRadius: 4, fontSize: '0.74rem', fontWeight: 600, cursor: 'pointer',
                        border: groupByBranch ? 0 : '1px solid rgba(31,122,54,0.4)',
                        background: groupByBranch ? 'linear-gradient(135deg, #1f7a36, #2d8f44)' : 'transparent',
                        color: groupByBranch ? '#fff' : '#1f7a36',
                    }}>
                    {groupByBranch ? '✓ ' : '📊 '}{t('Grupiraj po poslovnici', 'Group by branch')}
                </button>
            </div>
            {/* Filter panel */}
            <div style={{ display: 'grid', gridAutoFlow: 'column', gridAutoColumns: 'minmax(108px, 1fr)', gap: 8, padding: '10px 14px', background: 'rgba(201,155,49,0.06)', border: '1px solid rgba(184,156,91,0.3)', borderRadius: 8, marginBottom: 12, overflowX: 'auto', alignItems: 'end' }}>
                <label style={{ fontSize: '0.78rem', gridColumn: 'span 2' }}>{t('Raspon datuma', 'Date range')}
                    <DateRangePicker fromIso={dateFrom} toIso={dateTo} locale={locale}
                        onChange={(f, to) => { setDateFrom(f); setDateTo(to); }} />
                </label>
                <label style={{ fontSize: '0.78rem' }}>{t('Poslovnica', 'Branch')}
                    <select value={poslovnica} onChange={(e) => setPoslovnica(e.target.value)} style={{ width: '100%', padding: '4px 6px' }}>
                        <option value="">— sve —</option>
                        {((data ?? dataV2)?.filters_available?.poslovnice ?? []).map((p: any) => (
                            <option key={p.id_poslovnice} value={String(p.id_poslovnice)}>{p.poslovnica}</option>
                        ))}
                    </select>
                </label>
                <label style={{ fontSize: '0.78rem' }}>{t('Regija', 'Region')}
                    <select value={region} onChange={(e) => setRegion(e.target.value)} style={{ width: '100%', padding: '4px 6px' }}>
                        <option value="">— sve —</option>
                        {((data ?? dataV2)?.filters_available?.regije ?? []).map((rg: string) => (
                            <option key={rg} value={rg}>{rg}</option>
                        ))}
                    </select>
                </label>
                <label style={{ fontSize: '0.78rem' }}>{t('Kovnica', 'Mint')}
                    <select value={mint} onChange={(e) => setMint(e.target.value)} style={{ width: '100%', padding: '4px 6px' }}>
                        <option value="">— sve —</option>
                        {((data ?? dataV2)?.filters_available?.mints ?? []).map((m: string) => (
                            <option key={m} value={m}>{m}</option>
                        ))}
                    </select>
                </label>
                <label style={{ fontSize: '0.78rem' }}>{t('Veličina (g)', 'Size (g)')}
                    <select value={finePerUnit} onChange={(e) => setFinePerUnit(e.target.value)} style={{ width: '100%', padding: '4px 6px' }}>
                        <option value="">— sve —</option>
                        {((data ?? dataV2)?.filters_available?.sizes ?? []).map((s: number) => (
                            <option key={String(s)} value={String(s)}>{s} g</option>
                        ))}
                    </select>
                </label>
                <label style={{ fontSize: '0.78rem' }}>{t('Metal', 'Metal')}
                    <select value={vrstaKovine} onChange={(e) => setVrstaKovine(e.target.value)} style={{ width: '100%', padding: '4px 6px' }}>
                        <option value="">— sve —</option>
                        <option value="Zlato">🥇 Zlato</option>
                        <option value="Srebro">🥈 Srebro</option>
                    </select>
                </label>
                <label style={{ fontSize: '0.78rem' }}>{t('Status', 'Status')}
                    <select value={accepted} onChange={(e) => setAccepted(e.target.value)} style={{ width: '100%', padding: '4px 6px' }}>
                        <option value="">— sve —</option>
                        <option value="1">✓ Prihvaćeno</option>
                        <option value="0">⏳ Otvoreno</option>
                    </select>
                </label>
                <label style={{ fontSize: '0.78rem' }}>{t('Hedge', 'Hedge')}
                    <select value={hedgeStatus} onChange={(e) => setHedgeStatus(e.target.value)} style={{ width: '100%', padding: '4px 6px' }}>
                        <option value="">— sve —</option>
                        <option value="none">— bez hedge</option>
                        <option value="partial">⚠ partial</option>
                        <option value="full">✓ full</option>
                    </select>
                </label>
                <label style={{ fontSize: '0.78rem' }}>{t('Min € iznos', 'Min € amount')}
                    <input type="number" min="0" step="100" value={minIznos} onChange={(e) => setMinIznos(e.target.value)} placeholder="0" style={{ width: '100%', padding: '4px 6px' }} />
                </label>
                <label style={{ fontSize: '0.78rem' }}>{t('Pretraži', 'Search')}
                    <input type="search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="broj ponude" style={{ width: '100%', padding: '4px 6px' }} />
                </label>
            </div>

            {/* Sumarni redak */}
            {s ? (
                <div style={{ display: 'grid', gridAutoFlow: 'column', gridAutoColumns: 'minmax(92px, 1fr)', gap: 8, padding: '9px 12px', background: 'linear-gradient(135deg, rgba(31,122,54,0.08), rgba(201,155,49,0.05))', border: '1.5px solid #c99b31', borderRadius: 8, marginBottom: 12, overflowX: 'auto' }}>
                    <div><div style={{ fontSize: '0.6rem', color: '#666' }}>{t('Ponuda', 'Offers')}</div><strong style={{ fontSize: '0.95rem' }}>{s.count_offers}</strong><div className="muted" style={{ fontSize: '0.58rem' }}>{s.count_lines} {t('stavki', 'lines')}</div></div>
                    <div><div style={{ fontSize: '0.6rem', color: '#666' }}>{t('Prodajno', 'Sales')}</div><strong style={{ fontSize: '0.95rem', color: '#1f7a36' }}>{fmtMoney(s.total_prodajno)}</strong></div>
                    <div><div style={{ fontSize: '0.6rem', color: '#666' }}>{t('Nabavna (izdavanje)', 'Cost (issue)')}</div><strong style={{ fontSize: '0.85rem' }}>{fmtMoney(s.total_nabavna_izdavanje)}</strong></div>
                    <div title={t('Dobavljačka premija + zavisni troškovi nabave iz cjenika (m.40.1). Neto RUC = bruto − ovo.', 'Supplier premium + ancillary purchase costs from the price list (m.40.1). Net GM = gross − this.')}><div style={{ fontSize: '0.6rem', color: '#666' }}>{t('Premija nab.', 'Purch. prem.')}</div><strong style={{ fontSize: '0.85rem', color: '#8a6817' }}>−{fmtMoney(s.total_purchase_premium_eur ?? 0)}</strong></div>
                    {(() => {
                        // RUC plan. — NETO (bruto − nabavni premium) kao glavna brojka, bruto ispod.
                        const prem = s.total_purchase_premium_eur ?? 0;
                        const rucBruto = s.total_prodajno - s.total_nabavna_izdavanje;
                        const rucNeto = rucBruto - prem;
                        const netoPct = s.total_prodajno > 0 ? (100 * rucNeto / s.total_prodajno) : 0;
                        return (
                            <div title={t('Neto RUC = prodajno − nabavna metala − nabavni premium.', 'Net GM = sales − metal cost − supplier premium.')}><div style={{ fontSize: '0.6rem', color: '#666' }}>{t('RUC plan.', 'RUC plan.')}</div><strong style={{ fontSize: '0.9rem', color: '#6b4f1c' }}>{fmtMoney(rucNeto)}</strong><div className="muted" style={{ fontSize: '0.58rem' }}>{netoPct.toFixed(1)}% · {t('bruto', 'gross')} {fmtMoney(rucBruto)}</div></div>
                        );
                    })()}
                    {(() => {
                        // RUC pot € + % — agregirano SAMO za prihvaćene redove (neto). Bez prihvaćenih → "—".
                        const prodPrihv = s.total_prodajno_prihvaceno ?? 0;
                        const premAcc = s.total_purchase_premium_accepted_eur ?? 0;
                        const rucPotBruto = prodPrihv - s.total_nabavna_prihvacanje;
                        const rucPotNeto = rucPotBruto - premAcc;
                        const hasData = prodPrihv > 0 && s.total_nabavna_prihvacanje > 0;
                        const rucPotPct = hasData ? (100 * rucPotNeto / prodPrihv) : null;
                        return (
                            <div title={t('Neto RUC potvrđenih ponuda.', 'Net GM of confirmed offers.')}><div style={{ fontSize: '0.6rem', color: '#666' }}>{t('RUC pot.', 'RUC cnf.')}</div><strong style={{ fontSize: '0.9rem', color: hasData ? (rucPotNeto >= 0 ? '#1f7a36' : '#b14040') : '#999' }}>{hasData ? fmtMoney(rucPotNeto) : '—'}</strong><div className="muted" style={{ fontSize: '0.58rem' }}>{rucPotPct != null ? `${rucPotPct.toFixed(1)}% · ${t('bruto', 'gross')} ${fmtMoney(rucPotBruto)}` : '—'}</div></div>
                        );
                    })()}
                    {/* RUC fix € + % — placeholder dok backend ne izloži vrijednost_na_fixingu polje */}
                    <div title={t('Bit će popunjeno kad bude integrirano s fixingom dobavljača.', 'Will be populated once supplier fixing integration is live.')}>
                        <div style={{ fontSize: '0.6rem', color: '#666' }}>{t('RUC fix', 'RUC fix')}</div>
                        <strong style={{ fontSize: '0.9rem', color: '#999' }}>—</strong>
                        <div className="muted" style={{ fontSize: '0.58rem' }}>—</div>
                    </div>
                    {(() => {
                        const incT = (data?.breakdown?.by_type ?? []).filter((tr) => !excludedTypes.has(tr.type) && tr.count_offers > 0);
                        const sumNeto = incT.reduce((a, tr) => a + ((tr.ruc_neto_eur ?? tr.ruc_eur) || 0), 0);
                        const sumBruto = incT.reduce((a, tr) => a + (tr.ruc_eur || 0), 0);
                        return (
                            <div title={t('Zbroj NETO RUC-a svih uključenih vrsta iz PO TIPU (prodaja + lom). Bruto ispod.', 'Sum of NET GM across included PO TIPU types (sales + scrap). Gross below.')}><div style={{ fontSize: '0.6rem', color: '#666' }}>RUC Σ {t('neto', 'net')}</div><strong style={{ fontSize: '0.9rem', color: '#1f7a36' }}>{fmtMoney(sumNeto)}</strong><div className="muted" style={{ fontSize: '0.58rem' }}>{t('bruto', 'gross')} {fmtMoney(sumBruto)}</div></div>
                        );
                    })()}
                    <div><div style={{ fontSize: '0.6rem', color: '#666' }}>🥇 {t('Fine g', 'Fine g')}</div><strong style={{ fontSize: '0.9rem', color: '#c99b31' }}>{fmtNum(s.total_fine_g, 2)}</strong></div>
                    <div><div style={{ fontSize: '0.6rem', color: '#666' }}>{t('Prihvaćeno', 'Accepted')}</div><strong style={{ fontSize: '0.95rem' }}>{s.accepted_count}/{s.count_lines}</strong><div className="muted" style={{ fontSize: '0.58rem' }}>{acceptedPct.toFixed(0)}%</div></div>
                    <div><div style={{ fontSize: '0.6rem', color: '#666' }}>{t('Hedgano', 'Hedged')}</div><strong style={{ fontSize: '0.85rem' }}>{s.hedged_full_count}✓ / {s.hedged_partial_count}⚠</strong></div>
                </div>
            ) : null}

            {error ? <p className="error">{error}</p> : null}
            {loading ? <p className="muted">{t('Učitavam…', 'Loading…')}</p> : null}

            {/* Sort toolbar */}
            {data ? (
                <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', alignItems: 'center', marginBottom: 10, padding: '8px 10px', background: 'rgba(184,156,91,0.10)', borderRadius: 6, fontSize: '0.74rem' }}>
                    <span style={{ color: '#6b4f1c', fontWeight: 700, marginRight: 4 }}>↕ {t('Sort:', 'Sort:')}</span>
                    {([
                        ['datum_ponude', t('Datum', 'Date')],
                        ['iznos_sa_pdv', t('Iznos', 'Amount')],
                        ['spot_na_izdavanju', t('Spot izd.', 'Spot iss.')],
                        ['valjanost_min', t('Valjanost', 'Validity')],
                        ['ruc_planiran_eur', t('RUC pon. €', 'RUC off. €')],
                        ['ruc_planiran_pct', t('RUC pon. %', 'RUC off. %')],
                        ['ruc_realiziran_eur', t('RUC pot. €', 'RUC cnf. €')],
                        ['ruc_realiziran_pct', t('RUC pot. %', 'RUC cnf. %')],
                    ] as const).map(([key, label]) => {
                        const active = sort === key;
                        return (
                            <button key={key} type="button" onClick={() => toggleSort(key as any)}
                                style={{
                                    padding: '3px 9px', borderRadius: 4, cursor: 'pointer', fontSize: '0.72rem', fontWeight: 600,
                                    border: active ? 0 : '1px solid rgba(184,156,91,0.4)',
                                    background: active ? 'linear-gradient(135deg, #6b4f1c, #c99b31)' : '#fff',
                                    color: active ? '#fffbeb' : '#6b4f1c',
                                }}>
                                {label}{active ? (dir === 'asc' ? ' ▲' : ' ▼') : ''}
                            </button>
                        );
                    })}
                </div>
            ) : null}

            {/* Sidro za skrol na vrh liste pri promjeni stranice */}
            <div ref={feedTopRef} style={{ scrollMarginTop: 12 }} />

            {/* V2 (unified) — 1 row = 1 ponuda */}
            {viewMode === 'unified' && dataV2 ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    {(dataV2.rows ?? []).length === 0 ? <p className="muted" style={{ textAlign: 'center', padding: 20 }}>{t('Nema ponuda za izabrane filtere.', 'No offers for selected filters.')}</p> : null}
                    {(dataV2.rows ?? []).map((r: any, idx: number) => (
                        <div key={r.offer_document_id} className={flashIds.has(String(r.offer_document_id)) ? 'feed-new-flash' : undefined}>
                            <UnifiedOfferCard
                                r={r}
                                idx={(dataV2.pagination?.from_idx ?? 1) + idx}
                                locale={locale}
                                fmtMoney={fmtMoney}
                                fmtNum={fmtNum}
                                token={token}
                                onAnnotationsChanged={() => void load()}
                                onOpen={() => openDrawerUnified(r.offer_document_id)}
                            />
                        </div>
                    ))}
                </div>
            ) : null}

            {/* V1 (line items) */}
            {viewMode === 'lines' && data ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    {(() => {
                        // Feed prati PO TIPU toggle: prodaja isključena → sakrij ponude; otkup isključen → sakrij te evente.
                        const showOffers = !excludedTypes.has('prodaja_investicijsko');
                        // Otkup eventi nisu paginirani s ponudama pa ih prikazujemo samo na 1. stranici.
                        const otkupEvents = (page === 1 ? (data.buyback_rows ?? []) : []).filter((b) => !excludedTypes.has(b.type));
                        if ((showOffers ? displayRows.length : 0) === 0 && otkupEvents.length === 0) {
                            return <p className="muted" style={{ textAlign: 'center', padding: 20 }}>{t('Nema stavki za izabrane filtere.', 'No items for selected filters.')}</p>;
                        }
                        // Grupiranje po poslovnici radi samo za ponude (otkup eventi nemaju to grupiranje).
                        if (groupedRows && showOffers) {
                            return groupedRows.map((g, gi) => {
                                if (g.kind === 'subtotal') {
                                    const rucPlanPct = g.iznos > 0 ? (100 * g.rucPlanNeto / g.iznos) : 0;
                                    const rucRealPct = g.iznos > 0 && g.rucRealNeto != null ? (100 * g.rucRealNeto / g.iznos) : null;
                                    return (
                                        <div key={'sub_'+gi} style={{ padding: '8px 12px', background: 'linear-gradient(90deg, rgba(31,122,54,0.18), rgba(31,122,54,0.06))', borderLeft: '4px solid #1f7a36', borderRadius: 4, fontSize: '0.78rem', display: 'flex', flexWrap: 'wrap', gap: 12, fontWeight: 700, color: '#1f7a36' }}>
                                            <span>📊 {g.label}</span>
                                            <span>· {g.count} {t('stavki', 'lines')}</span>
                                            <span>· {fmtNum(g.fine, 1)}g fine</span>
                                            <span>· {t('Iznos:', 'Amount:')} {fmtMoney(g.iznos)}</span>
                                            <span style={{ color: g.rucPlanNeto >= 0 ? '#1f7a36' : '#b14040' }}>· RUC pon: {fmtMoney(g.rucPlanNeto)} ({rucPlanPct.toFixed(1)}%) <span style={{ color: '#999', fontWeight: 400 }}>· {t('bruto', 'gross')} {fmtMoney(g.rucPlan)}</span></span>
                                            <span style={{ color: (g.rucRealNeto ?? 0) >= 0 ? '#1f7a36' : '#b14040' }}>· RUC pot: {g.rucRealNeto != null ? fmtMoney(g.rucRealNeto) : '—'} ({rucRealPct != null ? rucRealPct.toFixed(1)+'%' : '—'})</span>
                                        </div>
                                    );
                                }
                                const r = g.row;
                                return <LiveOfferCard key={r.id} r={r} idx={g.idx} onOpen={() => openDrawer(r.id)} locale={locale} fmtMoney={fmtMoney} fmtNum={fmtNum} fmtPct={fmtPct} token={token} onAnnotationsChanged={() => void load()} />;
                            });
                        }
                        // Ponude su SERVER-sortirane (po izboru u Sort traci) — NE diramo im redoslijed.
                        // Otkup evente interleavamo po vremenu SAMO kad je sort po datumu; za ostale sortove
                        // (iznos/RUC) idu na kraj, da sortiranje ponuda radi ispravno.
                        type FeedItem = { kind: 'offer'; dt: string; row: any; idx: number } | { kind: 'otkup'; dt: string; ev: any };
                        const offerItems: FeedItem[] = showOffers
                            ? displayRows.map((r, idx) => ({ kind: 'offer' as const, dt: String((r as any).datum_ponude ?? ''), row: r, idx: (data.pagination.from_idx ?? 1) + idx }))
                            : [];
                        const otkupItems: FeedItem[] = otkupEvents.map((ev) => ({ kind: 'otkup' as const, dt: String(ev.txn_at ?? ''), ev }));
                        let items: FeedItem[];
                        if (sort === 'datum_ponude') {
                            items = [...offerItems, ...otkupItems].sort((a, b) => (dir === 'asc' ? a.dt.localeCompare(b.dt) : b.dt.localeCompare(a.dt)));
                        } else {
                            otkupItems.sort((a, b) => b.dt.localeCompare(a.dt));
                            items = [...offerItems, ...otkupItems];
                        }
                        return items.map((it) => it.kind === 'offer'
                            ? (
                                <div key={'o' + it.row.id} className={flashIds.has(String(it.row.id)) ? 'feed-new-flash' : undefined}>
                                    <LiveOfferCard r={it.row} idx={it.idx} onOpen={() => openDrawer(it.row.id)} locale={locale} fmtMoney={fmtMoney} fmtNum={fmtNum} fmtPct={fmtPct} token={token} onAnnotationsChanged={() => void load()} />
                                </div>
                            )
                            : <OtkupEventRow key={'b' + it.ev.id} ev={it.ev} locale={locale} fmtMoney={fmtMoney} fmtNum={fmtNum} />
                        );
                    })()}
                </div>
            ) : null}


            {/* Paginator footer */}
            {data?.pagination && data.pagination.total > 0 ? (
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 4px', fontSize: '0.82rem', flexWrap: 'wrap', gap: 8 }}>
                    <div className="muted">
                        {t('Prikazano', 'Showing')} <strong>{data.pagination.from_idx}–{data.pagination.to_idx}</strong> {t('od', 'of')} <strong>{data.pagination.total}</strong>
                    </div>
                    <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                        <label style={{ fontSize: '0.78rem' }}>{t('Po stranici:', 'Per page:')}
                            <select value={perPage} onChange={(e) => setPerPage(Number(e.target.value))} style={{ marginLeft: 4, padding: '2px 4px' }}>
                                <option value={100}>100</option>
                                <option value={300}>300</option>
                                <option value={500}>500</option>
                            </select>
                        </label>
                        <button type="button" onClick={() => goToPage(1)} disabled={page <= 1} className="secondary compact">«</button>
                        <button type="button" onClick={() => goToPage(Math.max(1, page - 1))} disabled={page <= 1} className="secondary compact">‹</button>
                        <span style={{ fontWeight: 700 }}>{page} / {data.pagination.total_pages}</span>
                        <button type="button" onClick={() => goToPage(Math.min(data.pagination.total_pages, page + 1))} disabled={page >= data.pagination.total_pages} className="secondary compact">›</button>
                        <button type="button" onClick={() => goToPage(data.pagination.total_pages)} disabled={page >= data.pagination.total_pages} className="secondary compact">»</button>
                    </div>
                </div>
            ) : null}

            {/* Drawer */}
            {drawerId ? (
                <LiveOfferDrawer
                    data={drawerData}
                    locale={locale}
                    onClose={() => setDrawerId(null)}
                    fmtMoney={fmtMoney}
                    fmtNum={fmtNum}
                    fmtPct={fmtPct}
                />
            ) : null}
        </div>
    );
}

function OperationsPanel({ rows, locale, onPartnerClick, token }: { rows: OpenOfferRow[]; locale: 'hr' | 'en'; onPartnerClick?: (code: string) => void; token: string }) {
    const t = (hr: string, en: string): string => (locale === 'en' ? en : hr);
    const [subTab, setSubTab] = useState<'live' | 'open'>('live');
    const [query, setQuery] = useState('');
    const filtered = useMemo(() => {
        if (query.trim() === '') return rows;
        const q = query.toLowerCase();

        return rows.filter((r) => `${r.offer_number} ${r.partner_name} ${r.partner_code}`.toLowerCase().includes(q));
    }, [rows, query]);

    return (
        <div className="ado-m1__operations">
            {/* Sub-tab toggle: Live Feed (kase) vs Otvorene narudžbe (Faros) */}
            <div style={{ display: 'flex', gap: 6, marginBottom: 14, borderBottom: '1px solid rgba(184,156,91,0.3)', paddingBottom: 8 }}>
                <button type="button" onClick={() => setSubTab('live')}
                    style={{ padding: '6px 14px', borderRadius: 6, border: subTab === 'live' ? 0 : '1px solid rgba(184,156,91,0.4)',
                        background: subTab === 'live' ? 'linear-gradient(135deg, #6b4f1c, #c99b31)' : 'transparent',
                        color: subTab === 'live' ? '#fffbeb' : '#6b4f1c', fontWeight: 600, fontSize: '0.84rem', cursor: 'pointer' }}>
                    🔴 {t('Live Feed (kase)', 'Live Feed (POS)')}
                </button>
                <button type="button" onClick={() => setSubTab('open')}
                    style={{ padding: '6px 14px', borderRadius: 6, border: subTab === 'open' ? 0 : '1px solid rgba(184,156,91,0.4)',
                        background: subTab === 'open' ? 'linear-gradient(135deg, #6b4f1c, #c99b31)' : 'transparent',
                        color: subTab === 'open' ? '#fffbeb' : '#6b4f1c', fontWeight: 600, fontSize: '0.84rem', cursor: 'pointer' }}>
                    📋 {t('Otvorene ponude (Faros)', 'Open offers (Faros)')} ({rows.length})
                </button>
            </div>

            {subTab === 'live' ? <LiveOffersFeed token={token} locale={locale} /> : (
                <>
                    <header className="ado-m1__ops-header">
                        <h3>{t(`Otvorene ponude (${rows.length})`, `Open offers (${rows.length})`)}</h3>
                        <input
                            type="search"
                            placeholder={t('Pretraži po broju, partneru…', 'Search by number, partner…')}
                            value={query}
                            onChange={(e) => setQuery(e.target.value)}
                        />
                    </header>
                    <table>
                        <thead>
                            <tr>
                                <th>{t('Broj ponude', 'Offer #')}</th>
                                <th>{t('Partner', 'Partner')}</th>
                                <th>{t('Datum', 'Date')}</th>
                                <th>{t('Starost', 'Age')}</th>
                                <th>{t('Iznos', 'Amount')}</th>
                                <th>{t('Kasa', 'Kasa')}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filtered.map((r) => {
                                const ageBadge = r.age_days != null && r.age_days > 14 ? 'is-stuck' : (r.age_days != null && r.age_days > 7 ? 'is-warn' : '');
                                return (
                                    <tr key={r.offer_id} className={ageBadge}>
                                        <td>{r.offer_number}</td>
                                        <td>
                                            <button type="button" onClick={() => onPartnerClick?.(r.partner_code)} className="ado-m1__partner-link">
                                                <PersonName value={r.partner_name} isPerson={r.partner_name_is_person} />
                                                <span className="ado-m1__muted"> ({r.partner_code})</span>
                                            </button>
                                        </td>
                                        <td>{r.offer_date}</td>
                                        <td>{r.age_days != null ? `${r.age_days}d` : '—'}</td>
                                        <td>{formatMoney(r.amount_offered, locale)}</td>
                                        <td>{r.kasa_id_str}</td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </>
            )}
        </div>
    );
}

function CustomersPanel({ rows, locale, onPartnerClick, token }: { rows: CustomerRow[]; locale: 'hr' | 'en'; onPartnerClick?: (code: string) => void; token: string }) {
    const t = (hr: string, en: string): string => (locale === 'en' ? en : hr);
    const [tierFilter, setTierFilter] = useState<string>('all');

    // Tier counts za chip-ove
    const tierCounts: Record<string, number> = { vip: 0, high: 0, regular: 0, at_risk: 0, lost: 0, inactive: 0, never_active: 0 };
    rows.forEach((r) => { tierCounts[r.rfm_tier] = (tierCounts[r.rfm_tier] ?? 0) + 1; });

    const atRisk = rows.filter((r) => r.is_at_risk).sort((a, b) => b.monetary - a.monetary);
    const bots = rows.filter((r) => r.is_bot_signal);
    const filteredRows = tierFilter === 'all' ? rows : rows.filter((r) => r.rfm_tier === tierFilter);
    // Sortiranje (klik na zaglavlje kolone).
    type CKey = 'partner_name' | 'offers_count' | 'realized_total' | 'offered_total' | 'monetary' | 'conversion_by_count_pct' | 'conversion_by_value_pct' | 'recency_days';
    const [cSortKey, setCSortKey] = useState<CKey>('realized_total');
    const [cSortDir, setCSortDir] = useState<'asc' | 'desc'>('desc');
    const sortedFiltered = React.useMemo(() => {
        const arr = [...filteredRows];
        arr.sort((a, b) => {
            const av: any = (a as any)[cSortKey] ?? (cSortKey === 'partner_name' ? '' : 0);
            const bv: any = (b as any)[cSortKey] ?? (cSortKey === 'partner_name' ? '' : 0);
            const c = cSortKey === 'partner_name' ? String(av).localeCompare(String(bv)) : (Number(av) - Number(bv));
            return cSortDir === 'asc' ? c : -c;
        });
        return arr;
    }, [filteredRows, cSortKey, cSortDir]);
    const cToggle = (k: CKey) => { if (cSortKey === k) setCSortDir((d) => d === 'asc' ? 'desc' : 'asc'); else { setCSortKey(k); setCSortDir(k === 'partner_name' ? 'asc' : 'desc'); } };
    const cArrow = (k: CKey) => cSortKey === k ? (cSortDir === 'asc' ? ' ▲' : ' ▼') : '';
    const cThSort: React.CSSProperties = { cursor: 'pointer', userSelect: 'none' };
    const top = sortedFiltered.slice(0, 100);

    const tierConfig: Record<string, { label: string; emoji: string; bg: string; border: string }> = {
        vip:          { label: t('VIP', 'VIP'),                emoji: '👑', bg: 'rgba(201,155,49,0.18)', border: '#c99b31' },
        high:         { label: t('Visoki', 'High'),            emoji: '⭐', bg: 'rgba(31,122,54,0.10)',  border: '#1f7a36' },
        regular:      { label: t('Stalni', 'Regular'),         emoji: '✓',  bg: 'rgba(0,0,0,0.04)',      border: 'rgba(0,0,0,0.2)' },
        at_risk:      { label: t('At-risk', 'At-risk'),        emoji: '⚠', bg: 'rgba(177,64,64,0.10)',  border: '#b14040' },
        lost:         { label: t('Izgubljen', 'Lost'),         emoji: '✗',  bg: 'rgba(0,0,0,0.06)',      border: '#888' },
        inactive:     { label: t('Neaktivan', 'Inactive'),     emoji: '—',  bg: 'rgba(0,0,0,0.04)',      border: '#aaa' },
        never_active: { label: t('Bez akcije', 'Never active'), emoji: '·',  bg: 'rgba(0,0,0,0.03)',      border: '#ccc' },
    };

    return (
        <div className="ado-m1__customers">
            {/* RFM tier chips */}
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 12, alignItems: 'center' }}>
                <span style={{ fontSize: '0.78rem', color: '#6b4f1c', fontWeight: 700, marginRight: 4 }}>{t('RFM segmenti:', 'RFM tiers:')}</span>
                <button type="button" onClick={() => setTierFilter('all')}
                    style={{ padding: '4px 12px', borderRadius: 4, fontSize: '0.78rem', fontWeight: 600, cursor: 'pointer',
                        border: tierFilter === 'all' ? 0 : '1px solid rgba(184,156,91,0.4)',
                        background: tierFilter === 'all' ? 'linear-gradient(135deg, #6b4f1c, #c99b31)' : '#fff',
                        color: tierFilter === 'all' ? '#fff' : '#6b4f1c' }}>
                    {t('Svi', 'All')} ({rows.length})
                </button>
                {(['vip', 'high', 'regular', 'at_risk', 'lost', 'inactive'] as const).map((k) => {
                    const c = tierConfig[k];
                    const n = tierCounts[k] ?? 0;
                    if (n === 0 && k !== 'at_risk') return null;
                    const active = tierFilter === k;
                    return (
                        <button key={k} type="button" onClick={() => setTierFilter(k)}
                            style={{ padding: '4px 12px', borderRadius: 4, fontSize: '0.78rem', fontWeight: 600, cursor: 'pointer',
                                border: '1px solid '+(active ? c.border : 'rgba(184,156,91,0.3)'),
                                background: active ? c.bg : '#fff',
                                color: active ? c.border : '#444' }}>
                            {c.emoji} {c.label} ({n})
                        </button>
                    );
                })}
            </div>

            {/* At-risk alert lista */}
            {atRisk.length > 0 && (
                <section style={{ marginBottom: 16, padding: '12px 14px', background: 'linear-gradient(90deg, rgba(177,64,64,0.08), transparent)', borderLeft: '4px solid #b14040', borderRadius: 4 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                        <h3 style={{ margin: 0, fontSize: '0.95rem', color: '#b14040' }}>
                            ⚠ {t(`At-risk klijenti (${atRisk.length})`, `At-risk customers (${atRisk.length})`)}
                        </h3>
                        <ExportXlsxBtn token={token} type="customers_atrisk" label={t('Export xlsx', 'Export xlsx')} locale={locale} />
                    </div>
                    <p className="muted" style={{ margin: '4px 0 10px', fontSize: '0.78rem' }}>
                        {t('Visoka vrijednost (≥10k €), nedavno tihi. Hitno kontaktirati.', 'High value (≥10k €), recently silent. Reach out ASAP.')}
                    </p>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 8 }}>
                        {atRisk.slice(0, 12).map((r) => (
                            <div key={r.partner_code} style={{ padding: '8px 10px', background: '#fff', border: '1px solid rgba(177,64,64,0.3)', borderRadius: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
                                <button type="button" onClick={() => onPartnerClick?.(r.partner_code)} style={{ background: 'transparent', border: 0, padding: 0, textAlign: 'left', cursor: 'pointer', flex: '1 1 auto' }}>
                                    <div style={{ fontWeight: 700, fontSize: '0.85rem' }}>
                                        <PersonName value={r.partner_name} isPerson={r.partner_name_is_person} />
                                    </div>
                                    <div className="muted" style={{ fontSize: '0.74rem' }}>
                                        {formatMoney(r.monetary, locale)} naplaćeno · {r.recency_days}d {t('bez ponude', 'silent')} · {r.offers_count} {t('ponuda', 'offers')}
                                    </div>
                                </button>
                                <button
                                    type="button"
                                    onClick={(e) => { e.stopPropagation(); openMailDraft({ partnerName: r.partner_name, partnerCode: r.partner_code, locale }); }}
                                    title={t('Pripremi email tekst (clipboard)', 'Prepare email text (clipboard)')}
                                    style={{ padding: '4px 8px', border: '1px solid rgba(184,156,91,0.4)', background: '#fff', borderRadius: 3, cursor: 'pointer', fontSize: '0.9rem', flexShrink: 0 }}>📧</button>
                            </div>
                        ))}
                    </div>
                </section>
            )}

            {bots.length > 0 && (
                <section className="ado-m1__bot-alert" style={{ marginBottom: 16 }}>
                    <h3>{t(`⚠ Potencijalni botovi: ${bots.length}`, `⚠ Potential bots: ${bots.length}`)}</h3>
                    <p className="ado-m1__muted">{t(
                        'Conversion = 0% + 3+ ponuda + recency < 30 dana. Provjeri ručno prije bilo kakve akcije.',
                        'Conversion = 0% + 3+ offers + recency < 30 days. Verify manually before any action.'
                    )}</p>
                </section>
            )}

            <section>
                <h3>{tierFilter === 'all' ? t(`Top klijenti (${top.length})`, `Top customers (${top.length})`) : `${tierConfig[tierFilter]?.emoji} ${tierConfig[tierFilter]?.label} (${top.length})`}</h3>
                <table>
                    <thead>
                        <tr>
                            <th style={cThSort} onClick={() => cToggle('partner_name')}>{t('Partner', 'Partner')}{cArrow('partner_name')}</th>
                            <th>{t('RFM', 'RFM')}</th>
                            <th style={cThSort} onClick={() => cToggle('offers_count')}>{t('Ponude', 'Offers')}{cArrow('offers_count')}</th>
                            <th style={cThSort} onClick={() => cToggle('realized_total')}>{t('Realizirano', 'Realized')}{cArrow('realized_total')}</th>
                            <th style={cThSort} onClick={() => cToggle('offered_total')}>{t('Ponuđeno', 'Offered')}{cArrow('offered_total')}</th>
                            <th style={cThSort} onClick={() => cToggle('monetary')}>{t('Naplaćeno', 'Earned')}{cArrow('monetary')}</th>
                            <th style={cThSort} onClick={() => cToggle('conversion_by_count_pct')}>{t('% kol.', '% cnt')}{cArrow('conversion_by_count_pct')}</th>
                            <th style={cThSort} onClick={() => cToggle('conversion_by_value_pct')}>{t('% vrij.', '% val')}{cArrow('conversion_by_value_pct')}</th>
                            <th style={cThSort} onClick={() => cToggle('recency_days')}>{t('Recency', 'Recency')}{cArrow('recency_days')}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {top.map((r) => {
                            const c = tierConfig[r.rfm_tier];
                            return (
                                <tr
                                    key={r.partner_code}
                                    className={r.is_bot_signal ? 'is-bot ado-m1__row-clickable' : 'ado-m1__row-clickable'}
                                    onClick={() => onPartnerClick?.(r.partner_code)}
                                    style={{ cursor: 'pointer' }}
                                >
                                    <td>
                                        <PersonName value={r.partner_name} isPerson={r.partner_name_is_person} />
                                        <br /><span className="ado-m1__muted">{r.partner_code}</span>
                                    </td>
                                    <td>
                                        <span style={{ padding: '2px 8px', background: c?.bg, border: '1px solid '+c?.border, borderRadius: 12, fontSize: '0.7rem', fontWeight: 700, color: c?.border, whiteSpace: 'nowrap' }}>
                                            {c?.emoji} {c?.label}
                                        </span>
                                        {r.is_bot_signal ? <span style={{ marginLeft: 4, color: '#b14040' }} title="bot signal">⚠</span> : null}
                                    </td>
                                    <td>{formatNumber(r.offers_count, locale)}</td>
                                    <td>{formatNumber(r.realized_count, locale)}</td>
                                    <td>{formatMoney(r.offered_total, locale)}</td>
                                    <td>{formatMoney(r.realized_total, locale)}</td>
                                    <td>{r.conversion_by_count_pct.toFixed(1)}%</td>
                                    <td>{r.conversion_by_value_pct.toFixed(1)}%</td>
                                    <td>{r.recency_days != null ? `${r.recency_days}d` : '—'}</td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </section>
        </div>
    );
}

/**
 * m.1 tab Pregled — interaktivni pregled prodaje zlata s KPI karticama,
 * pivot-grupiranjem, sparklineovima i Y2Y usporedbom.
 * Backend: GET /ado-m1/gold-sales-pregled (App\Support\AdoModule1\GoldSalesPregled).
 */
type GoldSalesRow = {
    godina: number | null;
    mjesec: number | null;
    tjedan?: number | null;
    kolicina: number;
    promet: number;
    fine_g: number;
    ruc: number;
    tx: number;
    avg_ruc_per_tx: number | null;
    avg_promet_per_tx: number | null;
    avg_fine_per_tx: number | null;
    avg_qty_per_tx: number | null;
    avg_price_per_fine_g: number | null;
};

type GoldSalesSeriesPoint = {
    align: number;
    label: string;
    rolling_promet: number | null;
    rolling_promet_ly: number | null;
    rolling_ruc: number | null;
    rolling_ruc_ly: number | null;
    rolling_fine_g: number | null;
    rolling_fine_g_ly: number | null;
    rolling_tx: number | null;
    rolling_tx_ly: number | null;
};

type GoldPricePoint = {
    id: string;
    godina: number | null;
    mjesec: number | null;
    tjedan?: number | null;
    label: string;
    spot_eur_g: number | null;
    promet: number;
    ruc: number;
    avg_price_per_fine_g: number | null;
    dxy: number | null;
    real_yield: number | null;
    silver_eur_g: number | null;
};

type GoldMarketingPoint = {
    id: string;
    label: string;
    spend: number | null;
    new_clients: number | null;
    cac: number | null;
    promet: number;
    ruc: number;
    mktg_pct_promet: number | null;
};

type GoldStimPoint = {
    id: string;
    label: string;
    stim: number | null;
    turnover: number | null;
    ruc_hr: number | null;
    ruc_gold: number;
    stim_pct_ruc: number | null;
};

type GoldSalesPregledPayload = {
    granularity: 'mjesecno' | 'tjedno';
    date_from: string;
    date_to: string;
    rows: GoldSalesRow[];
    totals: GoldSalesRow;
    comparisons: {
        window_label: string;
        series: GoldSalesSeriesPoint[];
    };
    gold_price: { available: boolean; unit: string; series: GoldPricePoint[]; corr_promet: number | null; corr_ruc: number | null; note: string };
    marketing: { available: boolean; djelatnost: string; series: GoldMarketingPoint[]; cac_avg: number | null; note: string };
    stimulacije: { available: boolean; series: GoldStimPoint[]; note: string };
    puls?: PregledPulsPayload;
};

type PregledPulsDay = { datum: string; tx: number; promet: number; fine_g: number };
type PregledPulsPayload = {
    date_from: string;
    date_to: string;
    danas: PregledPulsDay;
    jucer: PregledPulsDay;
    prije_7_dana: PregledPulsDay;
    zadnja_prodaja_at: string | null;
    do_prihvacanja: { n: number; median_min: number | null; p90_min: number | null; isti_dan_pct: number | null; od: string | null };
    otkup: { company: string; n: number; eur: number; fine_g: number; od: string | null; do: string | null };
};

/* Traka na tabu Pregled: samo velicine sa svjezim izvorom (vidi App\Support\AdoModule1\PregledPuls). */
function PregledPulsStrip({ data, locale }: { data: PregledPulsPayload; locale: 'hr' | 'en' }) {
    const t = (hr: string, en: string) => (locale === 'en' ? en : hr);
    const fmtM = (n: number) => formatMoney(n, locale);
    const fmtN = (n: number) => formatNumber(n, locale);
    const hrDate = (iso: string | null) => (iso ? `${iso.slice(8, 10)}.${iso.slice(5, 7)}.${iso.slice(0, 4)}.` : '-');
    const fmtMin = (m: number | null) => (m == null ? '-' : m < 90 ? `${Math.round(m)} min` : `${(m / 60).toFixed(1)} h`);
    const dayCard = (label: string, d: PregledPulsDay) => (
        <PulsBadge label={label} value={fmtM(d.promet)} sub={`${fmtN(d.tx)} TX, ${fmtN(Math.round(d.fine_g))} g`} />
    );
    const last = data.zadnja_prodaja_at;
    const lastToday = last !== null && last.slice(0, 10) === data.danas.datum;
    const lead = data.do_prihvacanja;
    const leadFrom = lead.od && lead.od > data.date_from ? `, ${t('feed od', 'feed since')} ${hrDate(lead.od)}` : '';
    const bb = data.otkup;
    const bbFrom = bb.od && bb.od.slice(0, 10) > data.date_from ? `, ${t('od', 'since')} ${hrDate(bb.od.slice(0, 10))}` : '';

    return (
        <div className="ado-puls-strip">
            {dayCard(t('Zlato danas', 'Gold today'), data.danas)}
            {dayCard(t('Jucer', 'Yesterday'), data.jucer)}
            {dayCard(`${t('Isti dan pr. tj.', 'Same day last wk')} ${hrDate(data.prije_7_dana.datum).slice(0, 6)}`, data.prije_7_dana)}
            <PulsBadge
                label={t('Zadnja prodaja', 'Last sale')}
                value={last === null ? '-' : lastToday ? last.slice(11, 16) : hrDate(last.slice(0, 10))}
                sub={t('zivi feed blagajne', 'live POS feed')}
                accent={lastToday ? 'pos' : 'warn'}
            />
            <PulsBadge
                label={t('Ponuda do prihvata', 'Offer to accept')}
                value={fmtMin(lead.median_min)}
                sub={lead.n > 0 ? `${t('medijan', 'median')}; 90% ${t('do', 'within')} ${fmtMin(lead.p90_min)}${leadFrom}` : t('nema podataka u razdoblju', 'no data in period')}
            />
            <PulsBadge
                label={t('Otkup ADMS', 'Buyback ADMS')}
                value={fmtM(bb.eur)}
                sub={bb.n > 0 ? `${fmtN(bb.n)} TX, ${fmtN(Math.round(bb.fine_g))} g${bbFrom}` : t('nema podataka u razdoblju', 'no data in period')}
            />
        </div>
    );
}

/* -- helper: mini SVG sparkline (self-contained, no kycCharts dependency) -- */
function GoldSparkline({ values, color, height = 28 }: { values: number[]; color: string; height?: number }) {
    if (values.length < 2) return <div style={{ height }} />;
    const mn = Math.min(...values), mx = Math.max(...values), rng = mx - mn || 1;
    const pts = values.map((v, i) => `${(i / (values.length - 1)) * 100},${28 - ((v - mn) / rng) * 24}`).join(' ');
    return (
        <svg viewBox="0 0 100 32" preserveAspectRatio="none" style={{ width: '100%', height, display: 'block', overflow: 'visible' }} aria-hidden="true">
            <polygon points={`0,32 ${pts} 100,32`} fill={color} opacity={0.11} />
            <polyline points={pts} fill="none" stroke={color} strokeWidth={2} vectorEffect="non-scaling-stroke" strokeLinejoin="round" strokeLinecap="round" />
        </svg>
    );
}

/* -- helper: mini donut (self-contained) -- */
function GoldDonut({ slices, size = 130, centerValue, centerLabel }: { slices: Array<{ key: string; label: string; value: number; color: string }>; size?: number; centerValue?: string; centerLabel?: string }) {
    const total = slices.reduce((s, x) => s + (x.value || 0), 0);
    const r = 38, thick = 14, circ = 2 * Math.PI * r;
    let off = 0;
    return (
        <div style={{ position: 'relative', width: size, height: size, flex: 'none' }}>
            <svg viewBox="0 0 100 100" width={size} height={size} aria-hidden="true">
                <circle cx="50" cy="50" r={r} fill="none" stroke="#eef2f6" strokeWidth={thick} />
                {total > 0 && slices.filter(s => s.value > 0).map(s => {
                    const len = (s.value / total) * circ, dash = `${Math.max(len - 1, 0.6)} ${circ - Math.max(len - 1, 0.6)}`;
                    const el = <circle key={s.key} cx="50" cy="50" r={r} fill="none" stroke={s.color} strokeWidth={thick} strokeDasharray={dash} strokeDashoffset={-off} strokeLinecap="butt" transform="rotate(-90 50 50)"><title>{s.label}: {s.value.toLocaleString()}</title></circle>;
                    off += len; return el;
                })}
            </svg>
            <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', pointerEvents: 'none' }}>
                {centerValue && <div style={{ fontSize: size * 0.17, fontWeight: 800, lineHeight: 1, letterSpacing: '-.03em', fontVariantNumeric: 'tabular-nums', color: '#1d2b3d' }}>{centerValue}</div>}
                {centerLabel && <div style={{ fontSize: 10, color: '#8b98a9', marginTop: 3, textTransform: 'uppercase', letterSpacing: '.08em', fontWeight: 700 }}>{centerLabel}</div>}
            </div>
        </div>
    );
}

/* -- helper: mini column chart (self-contained) -- */
function GoldColumns({ data, color, height = 110 }: { data: Array<{ label: string; value: number }>; color: string; height?: number }) {
    const mx = Math.max(1, ...data.map(d => d.value));
    return (
        <div>
            <div style={{ display: 'flex', alignItems: 'flex-end', gap: 3, height }}>
                {data.map((d, i) => {
                    const h = Math.max((d.value / mx) * 100, d.value > 0 ? 3 : 0.9);
                    return (
                        <div key={`${d.label}-${i}`} title={`${d.label}: ${d.value.toLocaleString()}`} style={{ flex: '1 1 0', display: 'flex', flexDirection: 'column', justifyContent: 'flex-end', height: '100%', minWidth: 0 }}>
                            <div style={{ height: `${h}%`, background: d.value > 0 ? color : '#e6ebf1', borderRadius: '4px 4px 1px 1px', minHeight: 2, transition: 'height .4s ease' }} />
                        </div>
                    );
                })}
            </div>
            <div style={{ display: 'flex', gap: 3, marginTop: 5 }}>
                {data.map((d, i) => <div key={`l-${i}`} style={{ flex: '1 1 0', fontSize: 8.5, color: '#8b98a9', textAlign: 'center', fontWeight: 650, whiteSpace: 'nowrap', overflow: 'hidden' }}>{d.label}</div>)}
            </div>
        </div>
    );
}

/* -- helper: dual-axis correlation chart (two series, each own scale) -- */
function GoldDualAxis({ labels, left, right, height = 260, bars }: {
    labels: string[];
    left: { name: string; color: string; values: Array<number | null>; fmt: (n: number) => string };
    right: { name: string; color: string; values: Array<number | null>; fmt: (n: number) => string };
    height?: number;
    bars?: boolean;
}) {
    const w = 820, padL = 62, padR = 62, padT = 14, padB = 30;
    const innerW = w - padL - padR, innerH = height - padT - padB;
    const n = labels.length;
    if (n === 0) return <div style={{ padding: 20, color: '#8b98a9', fontSize: 12 }}>Nema podataka.</div>;
    const xAt = (i: number) => padL + (n <= 1 ? innerW / 2 : (i / (n - 1)) * innerW);
    const scale = (vals: Array<number | null>) => {
        const nums = vals.filter((v): v is number => v != null);
        const mn = nums.length ? Math.min(...nums) : 0, mx = nums.length ? Math.max(...nums) : 1;
        const lo = mn - (mx - mn) * 0.08, hi = mx + (mx - mn) * 0.08;
        const rng = hi - lo || 1;
        return (v: number) => padT + innerH * (1 - (v - lo) / rng);
    };
    const yL = scale(left.values), yR = scale(right.values);
    const path = (vals: Array<number | null>, y: (v: number) => number) => {
        const segs: string[] = []; let cur: string[] = [];
        vals.forEach((v, i) => {
            if (v == null) { if (cur.length) { segs.push(cur.join(' ')); cur = []; } return; }
            cur.push(`${cur.length ? 'L' : 'M'}${xAt(i).toFixed(1)},${y(v).toFixed(1)}`);
        });
        if (cur.length) segs.push(cur.join(' '));
        return segs.join(' ');
    };
    const ticks = [0, 0.25, 0.5, 0.75, 1];
    const lNums = left.values.filter((v): v is number => v != null);
    const rNums = right.values.filter((v): v is number => v != null);
    const lLo = lNums.length ? Math.min(...lNums) : 0, lHi = lNums.length ? Math.max(...lNums) : 1;
    const rLo = rNums.length ? Math.min(...rNums) : 0, rHi = rNums.length ? Math.max(...rNums) : 1;
    const step = Math.max(1, Math.ceil(n / 12));
    return (
        <svg viewBox={`0 0 ${w} ${height}`} style={{ width: '100%', height, fontFamily: 'inherit', overflow: 'visible' }} preserveAspectRatio="xMidYMid meet">
            {ticks.map(t => {
                const y = padT + innerH * (1 - t);
                return (
                    <g key={t}>
                        <line x1={padL} x2={padL + innerW} y1={y} y2={y} stroke="rgba(0,0,0,0.07)" strokeWidth={1} />
                        <text x={padL - 6} y={y + 3} textAnchor="end" fontSize="9.5" fill={left.color}>{left.fmt(lLo - (lHi - lLo) * 0.08 + (lHi - lLo + (lHi - lLo) * 0.16) * t)}</text>
                        <text x={padL + innerW + 6} y={y + 3} textAnchor="start" fontSize="9.5" fill={right.color}>{right.fmt(rLo - (rHi - rLo) * 0.08 + (rHi - rLo + (rHi - rLo) * 0.16) * t)}</text>
                    </g>
                );
            })}
            {labels.map((lbl, i) => (i % step !== 0 && i !== n - 1) ? null : <text key={i} x={xAt(i)} y={height - 8} textAnchor="middle" fontSize="9" fill="#8b98a9">{lbl}</text>)}
            {bars && left.values.map((v, i) => {
                if (v == null) return null;
                const bw = Math.max(3, (innerW / Math.max(n, 1)) * 0.5);
                const y = yL(v);
                return <rect key={i} x={xAt(i) - bw / 2} y={y} width={bw} height={Math.max(0, padT + innerH - y)} fill={left.color} opacity={0.28} rx={2} />;
            })}
            {!bars && <path d={path(left.values, yL)} fill="none" stroke={left.color} strokeWidth={2.4} strokeLinejoin="round" strokeLinecap="round" />}
            <path d={path(right.values, yR)} fill="none" stroke={right.color} strokeWidth={2.4} strokeLinejoin="round" strokeLinecap="round" />
            {right.values.map((v, i) => v == null ? null : <circle key={i} cx={xAt(i)} cy={yR(v)} r={2.6} fill={right.color} />)}
        </svg>
    );
}

type GoldSortKey = 'promet' | 'ruc' | 'fine_g' | 'tx' | 'kolicina' | 'avg_ruc_per_tx' | 'avg_promet_per_tx' | 'avg_fine_per_tx' | 'avg_qty_per_tx' | 'avg_price_per_fine_g';
type GoldGroupBy = 'none' | 'godina';
type GoldMetric = 'promet' | 'ruc' | 'fine_g' | 'tx';

const GOLD = '#c99b31';
const GOLD_DARK = '#6b4f1c';
const GOLD_INK = '#23405f';
const GOLD_CARD: React.CSSProperties = { background: '#fff', border: '1px solid #dbe3ec', borderRadius: 14, padding: '15px 16px 14px', minWidth: 148, flex: '1 1 148px', position: 'relative', overflow: 'hidden', transition: 'transform .16s ease, box-shadow .16s ease, border-color .16s ease' };

function GoldKpi({ value, label, hint, color, spark, delta }: { value: string; label: string; hint?: string; color: string; spark?: number[]; delta?: number | null }) {
    return (
        <div style={{ ...GOLD_CARD }} onMouseEnter={e => { (e.currentTarget as HTMLElement).style.transform = 'translateY(-2px)'; (e.currentTarget as HTMLElement).style.boxShadow = '0 12px 28px rgba(23,32,51,.12)'; }} onMouseLeave={e => { (e.currentTarget as HTMLElement).style.transform = ''; (e.currentTarget as HTMLElement).style.boxShadow = ''; }}>
            <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 4, background: color }} />
            <div style={{ fontSize: 28, fontWeight: 800, lineHeight: 1, color, letterSpacing: '-.03em', fontVariantNumeric: 'tabular-nums', marginTop: 3 }}>{value}</div>
            {delta != null && delta !== 0 && (
                <span style={{ fontSize: 11, fontWeight: 700, color: delta > 0 ? '#1f7a36' : '#bb1111', marginLeft: 6 }}>
                    {delta > 0 ? '+' : ''}{delta.toFixed(1)}%
                </span>
            )}
            <div style={{ fontSize: 12.5, color: GOLD_INK, marginTop: 7, fontWeight: 700, letterSpacing: '-.005em' }}>{label}</div>
            {hint && <div style={{ fontSize: 10.5, color: '#8794a5', marginTop: 2, lineHeight: 1.3 }}>{hint}</div>}
            {spark && spark.length > 1 && <div style={{ marginTop: 8 }}><GoldSparkline values={spark} color={color} height={24} /></div>}
        </div>
    );
}

function GoldSalesPregledPanel({ token, locale }: { token: string; locale: 'hr' | 'en' }) {
    const t = (hr: string, en: string) => (locale === 'en' ? en : hr);
    const [granularity, setGranularity] = useState<'mjesecno' | 'tjedno'>('mjesecno');
    const [data, setData] = useState<GoldSalesPregledPayload | null>(null);
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [sortKey, setSortKey] = useState<GoldSortKey>('promet');
    const [sortAsc, setSortAsc] = useState(false);
    const [groupBy, setGroupBy] = useState<GoldGroupBy>('none');
    const [collapsed, setCollapsed] = useState<Set<number>>(new Set());
    const [metric, setMetric] = useState<GoldMetric>('promet');
    const [hoverRow, setHoverRow] = useState<number | null>(null);
    const [dateFrom, setDateFrom] = useState('');
    const [dateTo, setDateTo] = useState('');
    const [corrLeft, setCorrLeft] = useState<'promet' | 'ruc' | 'avg_price_per_fine_g'>('promet');
    const [corrRight, setCorrRight] = useState<'spot_eur_g' | 'dxy' | 'real_yield' | 'silver_eur_g'>('spot_eur_g');

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        setError('');
        const qs = new URLSearchParams({ granularity });
        if (dateFrom) qs.set('date_from', dateFrom);
        if (dateTo) qs.set('date_to', dateTo);
        api<GoldSalesPregledPayload>(`/ado-m1/gold-sales-pregled?${qs.toString()}`, token)
            .then((r) => { if (!cancelled) setData(r); })
            .catch((e) => { if (!cancelled) setError(e instanceof Error ? e.message : 'API'); })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [token, granularity, dateFrom, dateTo]);

    const num = (v: number | null | undefined, digits = 2) => v == null ? '-' : v.toLocaleString(locale === 'en' ? 'en-GB' : 'hr-HR', { minimumFractionDigits: digits, maximumFractionDigits: digits });
    const fmtM = (v: number) => formatMoney(v, locale);
    const short = (v: number) => {
        if (Math.abs(v) >= 1e6) return `${(v / 1e6).toFixed(1)}M`;
        if (Math.abs(v) >= 1e3) return `${(v / 1e3).toFixed(0)}k`;
        return v.toFixed(0);
    };

    const sorted = useMemo(() => {
        if (!data) return [];
        const rows = [...data.rows];
        rows.sort((a, b) => {
            const av = (a as any)[sortKey] ?? 0, bv = (b as any)[sortKey] ?? 0;
            return sortAsc ? av - bv : bv - av;
        });
        return rows;
    }, [data, sortKey, sortAsc]);

    const grouped = useMemo(() => {
        if (groupBy === 'none') return null;
        const map = new Map<number, { rows: GoldSalesRow[]; totals: GoldSalesRow }>();
        for (const r of sorted) {
            const k = r.godina ?? 0;
            if (!map.has(k)) map.set(k, { rows: [], totals: { godina: k, mjesec: null, kolicina: 0, promet: 0, fine_g: 0, ruc: 0, tx: 0, avg_ruc_per_tx: null, avg_promet_per_tx: null, avg_fine_per_tx: null, avg_qty_per_tx: null, avg_price_per_fine_g: null } });
            const g = map.get(k)!;
            g.rows.push(r);
            g.totals.kolicina += r.kolicina;
            g.totals.promet += r.promet;
            g.totals.fine_g += r.fine_g;
            g.totals.ruc += r.ruc;
            g.totals.tx += r.tx;
        }
        for (const [, g] of map) {
            const tx = g.totals.tx || 1;
            g.totals.avg_ruc_per_tx = g.totals.ruc / tx;
            g.totals.avg_promet_per_tx = g.totals.promet / tx;
            g.totals.avg_fine_per_tx = g.totals.fine_g / tx;
            g.totals.avg_qty_per_tx = g.totals.kolicina / tx;
            g.totals.avg_price_per_fine_g = g.totals.fine_g > 0 ? g.totals.promet / g.totals.fine_g : null;
        }
        return [...map.entries()].sort((a, b) => b[0] - a[0]);
    }, [sorted, groupBy]);

    const toggleGroup = (y: number) => setCollapsed(prev => { const n = new Set(prev); n.has(y) ? n.delete(y) : n.add(y); return n; });
    const handleSort = (key: GoldSortKey) => { if (sortKey === key) setSortAsc(!sortAsc); else { setSortKey(key); setSortAsc(false); } };
    const sortIcon = (key: GoldSortKey) => sortKey === key ? (sortAsc ? ' \u25B2' : ' \u25BC') : '';

    // -- derived KPI values --
    const totals = data?.totals;
    const rows = data?.rows ?? [];
    const sparkPromet = rows.map(r => r.promet);
    const sparkRuc = rows.map(r => r.ruc);
    const sparkFine = rows.map(r => r.fine_g);
    const sparkTx = rows.map(r => r.tx);

    // Y2Y delta: compare last period vs same period previous year (rough)
    const deltaPromet = useMemo(() => {
        if (rows.length < 2) return null;
        const cur = rows.filter(r => r.godina === Math.max(...rows.map(x => x.godina ?? 0)));
        const prev = rows.filter(r => r.godina === Math.max(...rows.map(x => x.godina ?? 0)) - 1);
        if (!cur.length || !prev.length) return null;
        const cs = cur.reduce((s, r) => s + r.promet, 0), ps = prev.reduce((s, r) => s + r.promet, 0);
        return ps > 0 ? ((cs - ps) / ps) * 100 : null;
    }, [rows]);

    // -- column distribution for donut (by year) --
    const yearSlices = useMemo(() => {
        const map = new Map<number, number>();
        for (const r of rows) { const y = r.godina ?? 0; map.set(y, (map.get(y) ?? 0) + r[metric]); }
        const colors = ['#c99b31', '#23405f', '#1f7a36', '#bb1111', '#8a6817', '#445066'];
        return [...map.entries()].sort((a, b) => b[0] - a[0]).map(([y, v], i) => ({ key: String(y), label: String(y), value: Math.round(v), color: colors[i % colors.length] }));
    }, [rows, metric]);

    // -- month-level columns for bar chart --
    const monthBars = useMemo(() => {
        if (granularity !== 'mjesecno' || !rows.length) return [];
        const last12 = rows.slice(-12);
        return last12.map(r => ({ label: `${r.mjesec}/${String(r.godina ?? '').slice(2)}`, value: Math.round(r[metric]) }));
    }, [rows, granularity, metric]);

    const METRICS: Array<{ key: GoldMetric; hr: string; en: string }> = [
        { key: 'promet', hr: 'Promet', en: 'Revenue' },
        { key: 'ruc', hr: 'RUC', en: 'Margin' },
        { key: 'fine_g', hr: 'Fine (g)', en: 'Fine (g)' },
        { key: 'tx', hr: 'TX', en: 'TX' },
    ];

    // -- Y2Y rolling: prati odabranu metriku (promet/RUC/fine/TX) umjesto fiksnog prometa --
    const rollingByMetric: Record<GoldMetric, { cur: keyof GoldSalesSeriesPoint; ly: keyof GoldSalesSeriesPoint; money: boolean; hr: string; en: string }> = {
        promet: { cur: 'rolling_promet', ly: 'rolling_promet_ly', money: true, hr: 'Y2Y rolling promet', en: 'Y2Y rolling revenue' },
        ruc: { cur: 'rolling_ruc', ly: 'rolling_ruc_ly', money: true, hr: 'Y2Y rolling RUC', en: 'Y2Y rolling margin' },
        fine_g: { cur: 'rolling_fine_g', ly: 'rolling_fine_g_ly', money: false, hr: 'Y2Y rolling fine (g)', en: 'Y2Y rolling fine (g)' },
        tx: { cur: 'rolling_tx', ly: 'rolling_tx_ly', money: false, hr: 'Y2Y rolling TX', en: 'Y2Y rolling TX' },
    };
    const rollCfg = rollingByMetric[metric];

    // -- prosjecna marza (RUC / promet) i marza po finom gramu za prikazani raspon --
    const marzaPct = totals && totals.promet > 0 ? (totals.ruc / totals.promet) * 100 : null;
    const marzaPerFine = totals && totals.fine_g > 0 ? totals.ruc / totals.fine_g : null;

    // -- korelacijski sloj: cijena zlata vs prodaja --
    const gp = data?.gold_price;
    const CORR_LEFT: Array<{ key: 'promet' | 'ruc' | 'avg_price_per_fine_g'; hr: string; en: string; money: boolean }> = [
        { key: 'promet', hr: 'Promet', en: 'Revenue', money: true },
        { key: 'ruc', hr: 'RUC (marza)', en: 'Margin', money: true },
        { key: 'avg_price_per_fine_g', hr: 'Ostvarena cijena EUR/g', en: 'Realized EUR/g', money: false },
    ];
    const CORR_RIGHT: Array<{ key: 'spot_eur_g' | 'dxy' | 'real_yield' | 'silver_eur_g'; hr: string; en: string }> = [
        { key: 'spot_eur_g', hr: 'Spot zlato EUR/g', en: 'Gold spot EUR/g' },
        { key: 'silver_eur_g', hr: 'Srebro EUR/g', en: 'Silver EUR/g' },
        { key: 'dxy', hr: 'USD indeks (DXY)', en: 'USD index (DXY)' },
        { key: 'real_yield', hr: 'Realni prinos %', en: 'Real yield %' },
    ];
    const corrLabels = (gp?.series ?? []).map(s => s.label);
    const corrLeftVals = (gp?.series ?? []).map(s => (s[corrLeft] as number | null));
    const corrRightVals = (gp?.series ?? []).map(s => (s[corrRight] as number | null));
    const corrCoef = useMemo(() => {
        const pairs = corrLeftVals.map((l, i) => [l, corrRightVals[i]] as const).filter(([a, b]) => a != null && b != null) as Array<[number, number]>;
        if (pairs.length < 3) return null;
        const n = pairs.length;
        const mx = pairs.reduce((s, p) => s + p[0], 0) / n, my = pairs.reduce((s, p) => s + p[1], 0) / n;
        let sxy = 0, sxx = 0, syy = 0;
        for (const [a, b] of pairs) { sxy += (a - mx) * (b - my); sxx += (a - mx) ** 2; syy += (b - my) ** 2; }
        if (sxx < 1e-9 || syy < 1e-9) return null;
        return sxy / Math.sqrt(sxx * syy);
    }, [corrLeftVals, corrRightVals]);
    const leftIsMoney = CORR_LEFT.find(c => c.key === corrLeft)?.money ?? true;

    const mk = data?.marketing;
    const stim = data?.stimulacije;
    const corrStrength = (c: number | null) => {
        if (c == null) return t('nedovoljno podataka', 'insufficient data');
        const a = Math.abs(c);
        const s = a >= 0.7 ? t('jaka', 'strong') : a >= 0.4 ? t('umjerena', 'moderate') : a >= 0.2 ? t('slaba', 'weak') : t('zanemariva', 'negligible');
        return `${c > 0 ? '+' : ''}${c.toFixed(2)} (${s} ${c >= 0 ? t('pozitivna', 'positive') : t('negativna', 'negative')})`;
    };

    const thStyle = (key: GoldSortKey): React.CSSProperties => ({
        textAlign: 'right', cursor: 'pointer', userSelect: 'none', padding: '10px 10px', fontSize: 11, fontWeight: 750,
        color: sortKey === key ? GOLD : GOLD_INK, letterSpacing: '-.01em', whiteSpace: 'nowrap', borderBottom: '2px solid #e8edf3',
        background: sortKey === key ? 'rgba(201,155,49,0.06)' : 'transparent', transition: 'background .12s ease',
    });
    const tdStyle = (isHighlight: boolean): React.CSSProperties => ({
        textAlign: 'right', padding: '9px 10px', fontSize: 12.5, fontVariantNumeric: 'tabular-nums', fontWeight: isHighlight ? 700 : 400,
        color: isHighlight ? '#1d2b3d' : '#455266', borderBottom: '1px solid #eef2f6', transition: 'background .1s ease',
    });

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
            <style>{`
                .gp-row { transition: background .1s ease; }
                .gp-row:hover { background: rgba(201,155,49,0.04) !important; }
                .gp-group-row { cursor: pointer; transition: background .12s ease; }
                .gp-group-row:hover { background: rgba(35,64,95,0.06) !important; }
                @media (max-width: 760px) { .gp-kpi-grid { grid-template-columns: repeat(2, 1fr) !important; } .gp-viz-grid { grid-template-columns: 1fr !important; } }
            `}</style>

            {/* -- HEADER -- */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 12 }}>
                <div>
                    <div style={{ fontSize: 10, letterSpacing: '.12em', textTransform: 'uppercase', color: GOLD, fontWeight: 750 }}>ADO - Modul 1 - SPNFT</div>
                    <div style={{ fontSize: 22, fontWeight: 780, letterSpacing: '-.02em', lineHeight: 1.15, marginTop: 2, color: GOLD_INK }}>{t('Prodaja zlata - Pregled', 'Gold Sales - Overview')}</div>
                    <div style={{ fontSize: 11.5, color: '#8794a5', marginTop: 3 }}>
                        {t('Analiticki pregled po transakciji i fine zlatu.', 'Analytical view by transaction and fine gold.')}
                    </div>
                </div>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                    {(['mjesecno', 'tjedno'] as const).map(g => (
                        <button key={g} type="button" onClick={() => setGranularity(g)}
                            style={{ padding: '8px 16px', fontSize: 12.5, fontWeight: 700, border: '1px solid', borderRadius: 9, cursor: 'pointer', transition: 'all .14s ease',
                                background: granularity === g ? GOLD_INK : '#fff', color: granularity === g ? '#fff' : GOLD_INK, borderColor: granularity === g ? GOLD_INK : '#d5dbe3',
                                boxShadow: granularity === g ? '0 4px 12px rgba(35,64,95,.18)' : 'none' }}>
                            {g === 'mjesecno' ? t('Mjesecno', 'Monthly') : t('Tjedno', 'Weekly')}
                        </button>
                    ))}
                    <span style={{ width: 1, height: 22, background: '#dbe3ec' }} />
                    <label style={{ fontSize: 11, color: '#8794a5', display: 'flex', flexDirection: 'column', gap: 2 }}>
                        {t('Od', 'From')}
                        <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} style={{ fontSize: 12, padding: '5px 8px', border: '1px solid #d5dbe3', borderRadius: 7, color: GOLD_INK }} />
                    </label>
                    <label style={{ fontSize: 11, color: '#8794a5', display: 'flex', flexDirection: 'column', gap: 2 }}>
                        {t('Do', 'To')}
                        <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} style={{ fontSize: 12, padding: '5px 8px', border: '1px solid #d5dbe3', borderRadius: 7, color: GOLD_INK }} />
                    </label>
                    {(dateFrom || dateTo) && (
                        <button type="button" onClick={() => { setDateFrom(''); setDateTo(''); }} style={{ padding: '6px 10px', fontSize: 11, fontWeight: 700, border: '1px solid #d5dbe3', borderRadius: 7, cursor: 'pointer', background: '#fff', color: '#8794a5', alignSelf: 'flex-end' }}>{t('Reset', 'Reset')}</button>
                    )}
                </div>
            </div>

            {data?.puls && <PregledPulsStrip data={data.puls} locale={locale} />}

            {/* -- eksplicitno razdoblje za koje vrijede zbrojevi -- */}
            {data && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 14px', background: 'linear-gradient(135deg,#f4f7fa,#eef2f7)', border: '1px solid #dde5ee', borderRadius: 10 }}>
                    <span style={{ fontSize: 10.5, letterSpacing: '.1em', textTransform: 'uppercase', color: GOLD_DARK, fontWeight: 800 }}>{t('Razdoblje', 'Period')}</span>
                    <span style={{ fontSize: 14, fontWeight: 800, color: GOLD_INK, fontVariantNumeric: 'tabular-nums' }}>{data.date_from} &rarr; {data.date_to}</span>
                    <span style={{ fontSize: 11.5, color: '#8794a5' }}>{t('Svi zbrojevi (promet, RUC, marza) i KPI kartice odnose se na ovo razdoblje.', 'All totals (revenue, RUC, margin) and KPI cards refer to this period.')}</span>
                </div>
            )}

            {error && <div style={{ padding: '12px 16px', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 10, color: '#991b1b', fontSize: 13 }}>{error}</div>}
            {loading && <div style={{ padding: 40, textAlign: 'center', color: '#8794a5', fontSize: 13 }}>{t('Ucitavam...', 'Loading...')}</div>}

            {!loading && !error && data && (
                <>
                    {/* -- KPI CARDS -- */}
                    <div className="gp-kpi-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 12 }}>
                        <GoldKpi value={fmtM(totals!.promet)} label={t('Ukupan promet', 'Total revenue')} color={GOLD} spark={sparkPromet} delta={deltaPromet} hint={`${totals!.tx} ${t('transakcija', 'transactions')}`} />
                        <GoldKpi value={fmtM(totals!.ruc)} label={t('RUC (marza)', 'Margin (RUC)')} color="#23405f" spark={sparkRuc} hint={`${num(totals!.avg_ruc_per_tx)} EUR/TX`} />
                        <GoldKpi value={marzaPct == null ? '-' : `${marzaPct.toFixed(2)}%`} label={t('Prosjecna marza', 'Average margin')} color="#7a1f5f" hint={marzaPerFine == null ? undefined : `${num(marzaPerFine)} EUR/g ${t('fine', 'fine')}`} />
                        <GoldKpi value={`${num(totals!.fine_g, 1)} g`} label={t('Fine zlato', 'Fine gold')} color="#8a6817" spark={sparkFine} hint={`${t('Prosj. cijena', 'Avg. price')} ${num(totals!.avg_price_per_fine_g)} EUR/g`} />
                        <GoldKpi value={String(totals!.tx)} label={t('Transakcije', 'Transactions')} color="#1f7a36" spark={sparkTx} hint={`${t('Prosj. promet', 'Avg. revenue')} ${fmtM(totals!.avg_promet_per_tx ?? 0)}/TX`} />
                    </div>

                    {/* -- VIZ ROW: donut + columns + Y2Y chart -- */}
                    <div className="gp-viz-grid" style={{ display: 'grid', gridTemplateColumns: '280px 1fr 1fr', gap: 14 }}>
                        {/* DONUT */}
                        <div style={{ background: '#fff', border: '1px solid #dbe3ec', borderRadius: 14, padding: 16 }}>
                            <div style={{ fontSize: 13, fontWeight: 750, color: GOLD_INK, marginBottom: 10 }}>{t('Udio po godini', 'Share by year')}</div>
                            <div style={{ display: 'flex', gap: 6, marginBottom: 12, flexWrap: 'wrap' }}>
                                {METRICS.map(m => <button key={m.key} type="button" onClick={() => setMetric(m.key)} style={{ fontSize: 10.5, fontWeight: 700, padding: '3px 9px', border: '1px solid', borderRadius: 6, cursor: 'pointer', background: metric === m.key ? GOLD_INK : '#fff', color: metric === m.key ? '#fff' : GOLD_INK, borderColor: metric === m.key ? GOLD_INK : '#d5dbe3' }}>{locale === 'en' ? m.en : m.hr}</button>)}
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'center' }}>
                                <GoldDonut slices={yearSlices} size={130} centerValue={short(yearSlices.reduce((s, x) => s + x.value, 0))} centerLabel={locale === 'en' ? METRICS.find(m => m.key === metric)?.en : METRICS.find(m => m.key === metric)?.hr} />
                            </div>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 5, marginTop: 12 }}>
                                {yearSlices.map(s => (
                                    <div key={s.key} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                        <span style={{ width: 10, height: 10, borderRadius: 3, background: s.color, flex: 'none' }} />
                                        <span style={{ fontSize: 12, color: '#455266', fontWeight: 600, flex: 1 }}>{s.label}</span>
                                        <span style={{ fontSize: 12.5, fontWeight: 800, color: '#1d2b3d', fontVariantNumeric: 'tabular-nums' }}>{s.value.toLocaleString()}</span>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* COLUMN CHART */}
                        <div style={{ background: '#fff', border: '1px solid #dbe3ec', borderRadius: 14, padding: 16 }}>
                            <div style={{ fontSize: 13, fontWeight: 750, color: GOLD_INK, marginBottom: 4 }}>{t('Zadnjih 12 mjeseci', 'Last 12 months')}</div>
                            <div style={{ fontSize: 10.5, color: '#8794a5', marginBottom: 10 }}>{locale === 'en' ? METRICS.find(m => m.key === metric)?.en : METRICS.find(m => m.key === metric)?.hr}</div>
                            {monthBars.length > 0 ? <GoldColumns data={monthBars} color={GOLD} height={120} /> : <div style={{ color: '#8b98a9', fontSize: 12, padding: 20 }}>{t('Prebaci na mjesecno', 'Switch to monthly')}</div>}
                        </div>

                        {/* Y2Y ROLLING - prati odabranu metriku */}
                        <div style={{ background: '#fff', border: '1px solid #dbe3ec', borderRadius: 14, padding: 16 }}>
                            <div style={{ fontSize: 13, fontWeight: 750, color: GOLD_INK, marginBottom: 4 }}>{locale === 'en' ? rollCfg.en : rollCfg.hr}</div>
                            <div style={{ fontSize: 10.5, color: '#8794a5', marginBottom: 8 }}>{data.comparisons.window_label}</div>
                            <NiceLineChart
                                locale={locale}
                                xLabels={data.comparisons.series.map(s => s.label)}
                                series={[
                                    { name: t('Tekuca', 'Current'), color: GOLD, values: data.comparisons.series.map(s => (s[rollCfg.cur] as number | null)) },
                                    { name: t('Prosla g.', 'Prior Y'), color: '#999', style: 'dashed', values: data.comparisons.series.map(s => (s[rollCfg.ly] as number | null)) },
                                ]}
                                yFormatter={n => rollCfg.money ? fmtM(n) : n.toLocaleString(locale === 'en' ? 'en-US' : 'hr-HR', { maximumFractionDigits: 0 })}
                            />
                        </div>
                    </div>

                    {/* -- PIVOT TOOLBAR -- */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', padding: '10px 14px', background: '#f7f9fb', borderRadius: 10, border: '1px solid #eef2f6' }}>
                        <span style={{ fontSize: 12, fontWeight: 750, color: GOLD_INK }}>{t('Grupiraj:', 'Group by:')}</span>
                        {([['none', t('Bez', 'None')], ['godina', t('Godina', 'Year')]] as const).map(([k, l]) => (
                            <button key={k} type="button" onClick={() => setGroupBy(k as GoldGroupBy)}
                                style={{ fontSize: 11.5, fontWeight: 700, padding: '5px 12px', border: '1px solid', borderRadius: 7, cursor: 'pointer', background: groupBy === k ? GOLD_INK : '#fff', color: groupBy === k ? '#fff' : GOLD_INK, borderColor: groupBy === k ? GOLD_INK : '#d5dbe3' }}>
                                {l}
                            </button>
                        ))}
                        <span style={{ flex: 1 }} />
                        <span style={{ fontSize: 11, color: '#8794a5' }}>{rows.length} {t('redaka', 'rows')}</span>
                    </div>

                    {/* -- TABLE -- */}
                    <div style={{ overflowX: 'auto', background: '#fff', border: '1px solid #dbe3ec', borderRadius: 14 }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12.5 }}>
                            <thead>
                                <tr>
                                    <th style={{ ...thStyle('promet'), textAlign: 'left', cursor: 'default', background: 'transparent', color: GOLD_INK }}>{t('Godina', 'Year')}</th>
                                    <th style={{ ...thStyle('promet'), textAlign: 'left', cursor: 'default', background: 'transparent', color: GOLD_INK }}>{t('Mj.', 'Mo.')}</th>
                                    {granularity === 'tjedno' && <th style={{ ...thStyle('promet'), textAlign: 'left', cursor: 'default', background: 'transparent', color: GOLD_INK }}>{t('Tj.', 'Wk.')}</th>}
                                    <th style={thStyle('kolicina')} onClick={() => handleSort('kolicina')}>{t('Kol.', 'Qty')}{sortIcon('kolicina')}</th>
                                    <th style={thStyle('promet')} onClick={() => handleSort('promet')}>{t('Promet', 'Revenue')}{sortIcon('promet')}</th>
                                    <th style={thStyle('fine_g')} onClick={() => handleSort('fine_g')}>{t('Fine', 'Fine')}{sortIcon('fine_g')}</th>
                                    <th style={thStyle('ruc')} onClick={() => handleSort('ruc')}>RUC{sortIcon('ruc')}</th>
                                    <th style={thStyle('tx')} onClick={() => handleSort('tx')}>TX{sortIcon('tx')}</th>
                                    <th style={thStyle('avg_ruc_per_tx')} onClick={() => handleSort('avg_ruc_per_tx')}>{t('RUC/TX', 'RUC/TX')}{sortIcon('avg_ruc_per_tx')}</th>
                                    <th style={thStyle('avg_promet_per_tx')} onClick={() => handleSort('avg_promet_per_tx')}>{t('Prom/TX', 'Rev/TX')}{sortIcon('avg_promet_per_tx')}</th>
                                    <th style={thStyle('avg_fine_per_tx')} onClick={() => handleSort('avg_fine_per_tx')}>{t('Fine/TX', 'Fine/TX')}{sortIcon('avg_fine_per_tx')}</th>
                                    <th style={thStyle('avg_qty_per_tx')} onClick={() => handleSort('avg_qty_per_tx')}>{t('Kol/TX', 'Qty/TX')}{sortIcon('avg_qty_per_tx')}</th>
                                    <th style={thStyle('avg_price_per_fine_g')} onClick={() => handleSort('avg_price_per_fine_g')}>{t('EUR/g', 'EUR/g')}{sortIcon('avg_price_per_fine_g')}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {groupBy === 'none' ? sorted.map((r, i) => (
                                    <tr key={i} className="gp-row" onMouseEnter={() => setHoverRow(i)} onMouseLeave={() => setHoverRow(null)} style={{ background: hoverRow === i ? 'rgba(201,155,49,0.04)' : i % 2 === 0 ? '#fff' : '#fafbfc' }}>
                                        <td style={{ ...tdStyle(false), textAlign: 'left' }}>{r.godina}</td>
                                        <td style={{ ...tdStyle(false), textAlign: 'left' }}>{r.mjesec}</td>
                                        {granularity === 'tjedno' && <td style={{ ...tdStyle(false), textAlign: 'left' }}>{r.tjedan}</td>}
                                        <td style={tdStyle(false)}>{num(r.kolicina, 0)}</td>
                                        <td style={tdStyle(true)}>{fmtM(r.promet)}</td>
                                        <td style={tdStyle(false)}>{num(r.fine_g, 2)}</td>
                                        <td style={tdStyle(true)}>{fmtM(r.ruc)}</td>
                                        <td style={tdStyle(false)}>{r.tx}</td>
                                        <td style={tdStyle(false)}>{num(r.avg_ruc_per_tx)}</td>
                                        <td style={tdStyle(false)}>{num(r.avg_promet_per_tx)}</td>
                                        <td style={tdStyle(false)}>{num(r.avg_fine_per_tx)}</td>
                                        <td style={tdStyle(false)}>{num(r.avg_qty_per_tx)}</td>
                                        <td style={tdStyle(false)}>{num(r.avg_price_per_fine_g)}</td>
                                    </tr>
                                )) : grouped?.map(([year, g]) => (
                                    <React.Fragment key={year}>
                                        <tr className="gp-group-row" onClick={() => toggleGroup(year)} style={{ background: '#f7f9fb' }}>
                                            <td colSpan={granularity === 'tjedno' ? 3 : 2} style={{ padding: '10px 10px', fontWeight: 780, fontSize: 13, color: GOLD_INK, borderBottom: '2px solid #dbe3ec' }}>
                                                <span style={{ display: 'inline-block', width: 18, fontSize: 11, color: '#8794a5' }}>{collapsed.has(year) ? '\u25B6' : '\u25BC'}</span>
                                                {year} <span style={{ fontWeight: 600, color: '#8794a5', marginLeft: 6, fontSize: 11 }}>({g.rows.length} {t('redaka', 'rows')})</span>
                                            </td>
                                            <td style={{ ...tdStyle(true), fontWeight: 780, background: '#f7f9fb', borderBottom: '2px solid #dbe3ec' }}>{num(g.totals.kolicina, 0)}</td>
                                            <td style={{ ...tdStyle(true), fontWeight: 780, background: '#f7f9fb', borderBottom: '2px solid #dbe3ec', color: GOLD }}>{fmtM(g.totals.promet)}</td>
                                            <td style={{ ...tdStyle(true), fontWeight: 780, background: '#f7f9fb', borderBottom: '2px solid #dbe3ec' }}>{num(g.totals.fine_g, 1)}</td>
                                            <td style={{ ...tdStyle(true), fontWeight: 780, background: '#f7f9fb', borderBottom: '2px solid #dbe3ec' }}>{fmtM(g.totals.ruc)}</td>
                                            <td style={{ ...tdStyle(true), fontWeight: 780, background: '#f7f9fb', borderBottom: '2px solid #dbe3ec' }}>{g.totals.tx}</td>
                                            <td style={{ ...tdStyle(false), background: '#f7f9fb', borderBottom: '2px solid #dbe3ec' }}>{num(g.totals.avg_ruc_per_tx)}</td>
                                            <td style={{ ...tdStyle(false), background: '#f7f9fb', borderBottom: '2px solid #dbe3ec' }}>{num(g.totals.avg_promet_per_tx)}</td>
                                            <td style={{ ...tdStyle(false), background: '#f7f9fb', borderBottom: '2px solid #dbe3ec' }}>{num(g.totals.avg_fine_per_tx)}</td>
                                            <td style={{ ...tdStyle(false), background: '#f7f9fb', borderBottom: '2px solid #dbe3ec' }}>{num(g.totals.avg_qty_per_tx)}</td>
                                            <td style={{ ...tdStyle(false), background: '#f7f9fb', borderBottom: '2px solid #dbe3ec' }}>{num(g.totals.avg_price_per_fine_g)}</td>
                                        </tr>
                                        {!collapsed.has(year) && g.rows.map((r, i) => (
                                            <tr key={`${year}-${i}`} className="gp-row" style={{ background: i % 2 === 0 ? '#fff' : '#fafbfc' }}>
                                                <td style={{ ...tdStyle(false), textAlign: 'left', paddingLeft: 28 }}>{r.godina}</td>
                                                <td style={{ ...tdStyle(false), textAlign: 'left' }}>{r.mjesec}</td>
                                                {granularity === 'tjedno' && <td style={{ ...tdStyle(false), textAlign: 'left' }}>{r.tjedan}</td>}
                                                <td style={tdStyle(false)}>{num(r.kolicina, 0)}</td>
                                                <td style={tdStyle(true)}>{fmtM(r.promet)}</td>
                                                <td style={tdStyle(false)}>{num(r.fine_g, 2)}</td>
                                                <td style={tdStyle(true)}>{fmtM(r.ruc)}</td>
                                                <td style={tdStyle(false)}>{r.tx}</td>
                                                <td style={tdStyle(false)}>{num(r.avg_ruc_per_tx)}</td>
                                                <td style={tdStyle(false)}>{num(r.avg_promet_per_tx)}</td>
                                                <td style={tdStyle(false)}>{num(r.avg_fine_per_tx)}</td>
                                                <td style={tdStyle(false)}>{num(r.avg_qty_per_tx)}</td>
                                                <td style={tdStyle(false)}>{num(r.avg_price_per_fine_g)}</td>
                                            </tr>
                                        ))}
                                    </React.Fragment>
                                ))}
                            </tbody>
                            <tfoot>
                                <tr style={{ background: 'linear-gradient(135deg, #23405f 0%, #2e4a68 55%, #1f4a3b 100%)' }}>
                                    <td colSpan={granularity === 'tjedno' ? 3 : 2} style={{ padding: '12px 10px', fontWeight: 800, fontSize: 13, color: '#fff', borderTop: '2px solid #c99b31' }}>{t('UKUPNO', 'TOTAL')}</td>
                                    <td style={{ textAlign: 'right', padding: '12px 10px', fontWeight: 800, fontSize: 13, color: '#fff', fontVariantNumeric: 'tabular-nums', borderTop: '2px solid #c99b31' }}>{num(totals!.kolicina, 0)}</td>
                                    <td style={{ textAlign: 'right', padding: '12px 10px', fontWeight: 800, fontSize: 13, color: '#ffd666', fontVariantNumeric: 'tabular-nums', borderTop: '2px solid #c99b31' }}>{fmtM(totals!.promet)}</td>
                                    <td style={{ textAlign: 'right', padding: '12px 10px', fontWeight: 800, fontSize: 13, color: '#fff', fontVariantNumeric: 'tabular-nums', borderTop: '2px solid #c99b31' }}>{num(totals!.fine_g, 1)}</td>
                                    <td style={{ textAlign: 'right', padding: '12px 10px', fontWeight: 800, fontSize: 13, color: '#fff', fontVariantNumeric: 'tabular-nums', borderTop: '2px solid #c99b31' }}>{fmtM(totals!.ruc)}</td>
                                    <td style={{ textAlign: 'right', padding: '12px 10px', fontWeight: 800, fontSize: 13, color: '#fff', fontVariantNumeric: 'tabular-nums', borderTop: '2px solid #c99b31' }}>{totals!.tx}</td>
                                    <td style={{ textAlign: 'right', padding: '12px 10px', fontWeight: 700, fontSize: 12, color: 'rgba(255,255,255,0.8)', fontVariantNumeric: 'tabular-nums', borderTop: '2px solid #c99b31' }}>{num(totals!.avg_ruc_per_tx)}</td>
                                    <td style={{ textAlign: 'right', padding: '12px 10px', fontWeight: 700, fontSize: 12, color: 'rgba(255,255,255,0.8)', fontVariantNumeric: 'tabular-nums', borderTop: '2px solid #c99b31' }}>{num(totals!.avg_promet_per_tx)}</td>
                                    <td style={{ textAlign: 'right', padding: '12px 10px', fontWeight: 700, fontSize: 12, color: 'rgba(255,255,255,0.8)', fontVariantNumeric: 'tabular-nums', borderTop: '2px solid #c99b31' }}>{num(totals!.avg_fine_per_tx)}</td>
                                    <td style={{ textAlign: 'right', padding: '12px 10px', fontWeight: 700, fontSize: 12, color: 'rgba(255,255,255,0.8)', fontVariantNumeric: 'tabular-nums', borderTop: '2px solid #c99b31' }}>{num(totals!.avg_qty_per_tx)}</td>
                                    <td style={{ textAlign: 'right', padding: '12px 10px', fontWeight: 700, fontSize: 12, color: 'rgba(255,255,255,0.8)', fontVariantNumeric: 'tabular-nums', borderTop: '2px solid #c99b31' }}>{num(totals!.avg_price_per_fine_g)}</td>
                                </tr>
                            </tfoot>
                        </table>
                    </div>

                    {/* -- KORELACIJSKI SLOJ: prodaja vs cijena zlata -- */}
                    <div style={{ background: '#fff', border: '1px solid #dbe3ec', borderRadius: 14, padding: 18 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 12, marginBottom: 12 }}>
                            <div>
                                <div style={{ fontSize: 10, letterSpacing: '.1em', textTransform: 'uppercase', color: GOLD, fontWeight: 800 }}>{t('Korelacija', 'Correlation')}</div>
                                <div style={{ fontSize: 16, fontWeight: 780, color: GOLD_INK, letterSpacing: '-.01em' }}>{t('Prodaja naspram trzista zlata', 'Sales vs gold market')}</div>
                                <div style={{ fontSize: 11.5, color: '#8794a5', marginTop: 2 }}>{t('Lijeva os: prodaja. Desna os: trzisni pokazatelj. Svaka serija na svojoj skali.', 'Left axis: sales. Right axis: market driver. Each series on its own scale.')}</div>
                            </div>
                            {gp?.available && (
                                <div style={{ textAlign: 'right' }}>
                                    <div style={{ fontSize: 10, color: '#8794a5', textTransform: 'uppercase', letterSpacing: '.08em', fontWeight: 700 }}>{t('Pearsonov koeficijent', 'Pearson coefficient')}</div>
                                    <div style={{ fontSize: 20, fontWeight: 800, color: corrCoef == null ? '#8794a5' : Math.abs(corrCoef) >= 0.4 ? '#1f7a36' : '#455266', fontVariantNumeric: 'tabular-nums' }}>{corrStrength(corrCoef)}</div>
                                </div>
                            )}
                        </div>
                        {gp?.available ? (
                            <>
                                <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 12 }}>
                                    <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
                                        <span style={{ fontSize: 11, fontWeight: 700, color: GOLD }}>{t('Prodaja:', 'Sales:')}</span>
                                        {CORR_LEFT.map(c => <button key={c.key} type="button" onClick={() => setCorrLeft(c.key)} style={{ fontSize: 11, fontWeight: 700, padding: '4px 10px', border: '1px solid', borderRadius: 7, cursor: 'pointer', background: corrLeft === c.key ? GOLD : '#fff', color: corrLeft === c.key ? '#fff' : GOLD_DARK, borderColor: corrLeft === c.key ? GOLD : '#e2c98d' }}>{locale === 'en' ? c.en : c.hr}</button>)}
                                    </div>
                                    <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
                                        <span style={{ fontSize: 11, fontWeight: 700, color: GOLD_INK }}>{t('Trziste:', 'Market:')}</span>
                                        {CORR_RIGHT.map(c => <button key={c.key} type="button" onClick={() => setCorrRight(c.key)} style={{ fontSize: 11, fontWeight: 700, padding: '4px 10px', border: '1px solid', borderRadius: 7, cursor: 'pointer', background: corrRight === c.key ? GOLD_INK : '#fff', color: corrRight === c.key ? '#fff' : GOLD_INK, borderColor: corrRight === c.key ? GOLD_INK : '#d5dbe3' }}>{locale === 'en' ? c.en : c.hr}</button>)}
                                    </div>
                                </div>
                                <GoldDualAxis
                                    labels={corrLabels}
                                    left={{ name: CORR_LEFT.find(c => c.key === corrLeft)!.hr, color: GOLD, values: corrLeftVals, fmt: (n) => leftIsMoney ? short(n) : num(n, 0) }}
                                    right={{ name: CORR_RIGHT.find(c => c.key === corrRight)!.hr, color: GOLD_INK, values: corrRightVals, fmt: (n) => num(n, corrRight === 'dxy' || corrRight === 'real_yield' ? 1 : 2) }}
                                    height={280}
                                    bars
                                />
                                <div style={{ display: 'flex', gap: 18, marginTop: 8, fontSize: 11.5, flexWrap: 'wrap' }}>
                                    <span style={{ display: 'flex', alignItems: 'center', gap: 6, color: GOLD_DARK, fontWeight: 700 }}><span style={{ width: 14, height: 10, background: GOLD, opacity: .55, borderRadius: 2 }} />{locale === 'en' ? CORR_LEFT.find(c => c.key === corrLeft)!.en : CORR_LEFT.find(c => c.key === corrLeft)!.hr}</span>
                                    <span style={{ display: 'flex', alignItems: 'center', gap: 6, color: GOLD_INK, fontWeight: 700 }}><span style={{ width: 14, height: 3, background: GOLD_INK, borderRadius: 2 }} />{locale === 'en' ? CORR_RIGHT.find(c => c.key === corrRight)!.en : CORR_RIGHT.find(c => c.key === corrRight)!.hr}</span>
                                    <span style={{ color: '#8794a5' }}>{t('Spot cijena je prosjek dnevnih vrijednosti u razdoblju.', 'Spot price is the average of daily values in the period.')}</span>
                                </div>
                            </>
                        ) : (
                            <div style={{ padding: 20, color: '#8b98a9', fontSize: 12.5 }}>{t('Cijena zlata nije dostupna za ovo razdoblje/granularnost.', 'Gold price unavailable for this period/granularity.')}</div>
                        )}
                    </div>

                    {/* -- MARKETING / CAC + STIMULACIJE (side by side) -- */}
                    <div className="gp-viz-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
                        {/* MARKETING */}
                        <div style={{ background: '#fff', border: '1px solid #dbe3ec', borderRadius: 14, padding: 18 }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 4 }}>
                                <div style={{ fontSize: 15, fontWeight: 780, color: GOLD_INK }}>{t('Marketing i trosak akvizicije', 'Marketing & acquisition cost')}</div>
                                {mk?.available && mk.cac_avg != null && <div style={{ fontSize: 12, color: '#8794a5' }}>{t('Prosj. CAC', 'Avg. CAC')} <b style={{ color: GOLD_DARK }}>{fmtM(mk.cac_avg)}</b></div>}
                            </div>
                            <div style={{ fontSize: 11, color: '#8794a5', marginBottom: 10 }}>{t('CAC = marketing potrosnja (investicijsko) / novi klijenti u mjesecu.', 'CAC = marketing spend (investment) / new clients in month.')}</div>
                            {mk?.available ? (
                                <div style={{ overflowX: 'auto' }}>
                                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                                        <thead><tr>
                                            {[t('Mj.', 'Mo.'), t('Potrosnja', 'Spend'), t('Novi kl.', 'New cl.'), 'CAC', t('% prometa', '% rev.')].map((h, i) => <th key={i} style={{ textAlign: i === 0 ? 'left' : 'right', padding: '7px 8px', fontSize: 10.5, fontWeight: 750, color: GOLD_INK, borderBottom: '2px solid #e8edf3', whiteSpace: 'nowrap' }}>{h}</th>)}
                                        </tr></thead>
                                        <tbody>
                                            {mk.series.filter(s => s.spend != null || s.new_clients != null).slice(-14).map((s, i) => (
                                                <tr key={s.id} style={{ background: i % 2 ? '#fafbfc' : '#fff' }}>
                                                    <td style={{ padding: '6px 8px', color: '#455266' }}>{s.label}</td>
                                                    <td style={{ padding: '6px 8px', textAlign: 'right', fontVariantNumeric: 'tabular-nums', color: '#455266' }}>{s.spend == null ? '-' : fmtM(s.spend)}</td>
                                                    <td style={{ padding: '6px 8px', textAlign: 'right', fontVariantNumeric: 'tabular-nums', color: '#455266' }}>{s.new_clients ?? '-'}</td>
                                                    <td style={{ padding: '6px 8px', textAlign: 'right', fontVariantNumeric: 'tabular-nums', fontWeight: 700, color: GOLD_DARK }}>{s.cac == null ? '-' : fmtM(s.cac)}</td>
                                                    <td style={{ padding: '6px 8px', textAlign: 'right', fontVariantNumeric: 'tabular-nums', color: '#455266' }}>{s.mktg_pct_promet == null ? '-' : `${s.mktg_pct_promet.toFixed(2)}%`}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            ) : (
                                <div style={{ padding: 16, color: '#8b98a9', fontSize: 12 }}>{t('Marketing podaci nisu dostupni za ovo razdoblje (samo mjesecno).', 'Marketing data unavailable for this period (monthly only).')}</div>
                            )}
                        </div>

                        {/* STIMULACIJE */}
                        <div style={{ background: '#fff', border: '1px solid #dbe3ec', borderRadius: 14, padding: 18 }}>
                            <div style={{ fontSize: 15, fontWeight: 780, color: GOLD_INK, marginBottom: 4 }}>{t('Place i stimulacije', 'Payroll & incentives')}</div>
                            <div style={{ fontSize: 11, color: '#8794a5', marginBottom: 10 }}>{t('Isplacene stimulacije i udio u ostvarenom RUC-u (svi izvori).', 'Paid incentives and share of realized RUC (all sources).')}</div>
                            {stim?.available ? (
                                <div style={{ overflowX: 'auto' }}>
                                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                                        <thead><tr>
                                            {[t('Mj.', 'Mo.'), t('Stimulacija', 'Incentive'), t('Promet', 'Turnover'), 'RUC', t('% RUC', '% RUC')].map((h, i) => <th key={i} style={{ textAlign: i === 0 ? 'left' : 'right', padding: '7px 8px', fontSize: 10.5, fontWeight: 750, color: GOLD_INK, borderBottom: '2px solid #e8edf3', whiteSpace: 'nowrap' }}>{h}</th>)}
                                        </tr></thead>
                                        <tbody>
                                            {stim.series.filter(s => s.stim != null).slice(-14).map((s, i) => (
                                                <tr key={s.id} style={{ background: i % 2 ? '#fafbfc' : '#fff' }}>
                                                    <td style={{ padding: '6px 8px', color: '#455266' }}>{s.label}</td>
                                                    <td style={{ padding: '6px 8px', textAlign: 'right', fontVariantNumeric: 'tabular-nums', fontWeight: 700, color: GOLD_DARK }}>{s.stim == null ? '-' : fmtM(s.stim)}</td>
                                                    <td style={{ padding: '6px 8px', textAlign: 'right', fontVariantNumeric: 'tabular-nums', color: '#455266' }}>{s.turnover == null ? '-' : fmtM(s.turnover)}</td>
                                                    <td style={{ padding: '6px 8px', textAlign: 'right', fontVariantNumeric: 'tabular-nums', color: '#455266' }}>{s.ruc_hr == null ? '-' : fmtM(s.ruc_hr)}</td>
                                                    <td style={{ padding: '6px 8px', textAlign: 'right', fontVariantNumeric: 'tabular-nums', color: '#455266' }}>{s.stim_pct_ruc == null ? '-' : `${s.stim_pct_ruc.toFixed(2)}%`}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            ) : (
                                <div style={{ padding: 16, color: '#8b98a9', fontSize: 12 }}>{t('Podaci o stimulacijama nisu dostupni za ovo razdoblje (samo mjesecno).', 'Incentive data unavailable for this period (monthly only).')}</div>
                            )}
                        </div>
                    </div>
                </>
            )}
        </div>
    );
}
